import {
  Link,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { useUser, useAuth, RedirectToSignIn } from "@clerk/clerk-react";
import { generateCaseDisplayName } from "../utils/caseDisplayName";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../lib/supabase";
import {
  INSURANCE_COMPANIES,
  INSURANCE_CATEGORIES,
  PLAN_TYPES,
  getAvailablePlanTypes,
  InsuranceCompany,
} from "../data/insuranceCompanies";
import {
  generateDynamicQuestions,
  DynamicQuestion,
  SelectedCondition,
  SelectedTreatment,
} from "../utils/dynamicQuestions";
import {
  CASE_TEMPLATES,
  TEMPLATE_CATEGORIES,
  CaseTemplate,
} from "../data/caseTemplates";
import { SPECIALTY_TEMPLATE_PRIORITIES } from "../data/specialtyPriorities";
import {
  RegionalPlanSelector,
  shouldShowRegionalSelector,
} from "../components/RegionalPlanSelector";
import { CaseScheduling, SchedulingData } from "../components/CaseScheduling";
import { SiteOfServiceIntake } from "../components/SiteOfService/SiteOfServiceIntake";
import type { SiteOfServiceInputs } from "../types/siteOfService";
import {
  serializeSoSInputs,
  parseSoSFromContext,
  createEmptySoSInputs,
} from "../utils/siteOfServiceGenerator";
import { COMMON_REQUESTS, CommonRequest } from "../data/commonRequests";
import { usePriorAuthGeneration } from "../hooks/usePriorAuthGeneration";
import {
  ScrubAuditPanel,
  buildAuditFields,
} from "../components/dev/ScrubAuditPanel";
import { scrubText, containsObviousPHI } from "../services/scrubber";
import {
  searchMedications,
  inferMedicationClass,
  analyzeMedications,
  generateMedicationSummaryDisplay,
  MEDICATION_CLASSES as MED_CLASSES_DATA,
  MedicationMatch,
} from "../data/medications";
import { useSubscription } from "../hooks/useSubscription";
import { useLetterCount } from "../hooks/useLetterCount";
import { assembleLetter, type LetterSlots } from "../utils/letterTemplates";
import { normalizeInsurer } from "../utils/submissionValidation";
import {
  generateAppealQuestions,
  getAppealFieldKeys,
} from "../utils/appealQuestions";

// ============================================
// MEDICATION TYPES
// ============================================

interface MedicationEntry {
  name: string;
  displayName: string;
  classId: string | null;
  className: string | null;
  isCustom: boolean;
}

// ============================================
// ICONS
// ============================================

function IconUser({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.5-1.632z"
      />
    </svg>
  );
}

function IconClipboard({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
      />
    </svg>
  );
}

function IconShield({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
      />
    </svg>
  );
}

function IconExclamation({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}

function IconBeaker({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"
      />
    </svg>
  );
}

function IconSparkles({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
      />
    </svg>
  );
}

function IconLightBulb({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"
      />
    </svg>
  );
}

function IconSearch({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
      />
    </svg>
  );
}

function IconX({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function IconChevronDown({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

function IconCheck({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 12.75l6 6 9-13.5"
      />
    </svg>
  );
}

function IconLoader({ className }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function IconPlus({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4.5v15m7.5-7.5h-15"
      />
    </svg>
  );
}

function IconMedication({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 00-3.7-3.7 48.678 48.678 0 00-7.324 0 4.006 4.006 0 00-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3l-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 003.7 3.7 48.656 48.656 0 007.324 0 4.006 4.006 0 003.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3l-3 3"
      />
    </svg>
  );
}

function IconCamera({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z"
      />
    </svg>
  );
}

function IconScissors({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7.848 8.25l1.536.887M7.848 8.25a3 3 0 11-5.196-3 3 3 0 015.196 3zm1.536.887a2.165 2.165 0 011.083 1.839c.005.351.054.695.14 1.024M9.384 9.137l2.077 1.199M7.848 15.75l1.536-.887m-1.536.887a3 3 0 11-5.196 3 3 3 0 015.196-3zm1.536-.887a2.165 2.165 0 001.083-1.838c.005-.352.054-.695.14-1.025m-1.223 2.863l2.077-1.199m0-3.328a4.323 4.323 0 012.068-1.379l5.325-1.628a4.5 4.5 0 012.48-.044l.803.215-7.794 4.5m-2.882-1.664A4.331 4.331 0 0010.607 12m3.736 0l7.794 4.5-.802.215a4.5 4.5 0 01-2.48-.043l-5.326-1.629a4.324 4.324 0 01-2.068-1.379M14.343 12l-2.882 1.664"
      />
    </svg>
  );
}

function IconCube({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
      />
    </svg>
  );
}

function IconChatBubble({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 21.192a5.98 5.98 0 01-.666-3.29c-.102-.22-.19-.448-.265-.68A8.16 8.16 0 013 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
      />
    </svg>
  );
}

function IconAdmission({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"
      />
    </svg>
  );
}

function IconReferral({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
      />
    </svg>
  );
}

// Helper function to get category icon
function getCategoryIcon(category: string, className: string = "w-5 h-5") {
  switch (category) {
    case "Medication":
    case "medication":
      return <IconMedication className={className} />;
    case "Imaging":
    case "imaging":
      return <IconCamera className={className} />;
    case "Procedure":
    case "procedure":
      return <IconScissors className={className} />;
    case "Admission":
    case "admission":
      return <IconAdmission className={className} />;
    case "Referral":
    case "referral":
      return <IconReferral className={className} />;
    case "DME":
    case "dme":
      return <IconCube className={className} />;
    default:
      return <IconClipboard className={className} />;
  }
}

// ============================================
// CONSTANTS
// ============================================

const REQUEST_TYPES = [
  "Medication",
  "Imaging",
  "Procedure",
  "Referral",
  "Admission",
  "DME (Durable Medical Equipment)",
  "Other",
];

const DENIAL_REASONS = [
  "Not medically necessary",
  "Does not meet clinical criteria",
  "Step therapy required",
  "Site of care / Facility denial",
  "Level of Care",
  "Non-formulary medication",
  "Too soon since last service",
  "Experimental/investigational",
  "Missing documentation",
  "Wrong code/authorization pathway",
  "Other",
];

// Display labels for denial reasons (allows renaming display without breaking stored values)
const DENIAL_REASON_DISPLAY_LABELS: Record<string, string> = {
  "Wrong code/authorization pathway": "Coding / auth pathway issue",
};

// Helper text for denial reason tiles
const DENIAL_REASON_HELPER_TEXT: Record<string, string> = {
  "Not medically necessary": "Plan says service not necessary",
  "Does not meet clinical criteria":
    "Guideline mismatch or missing required trial",
  "Step therapy required": "Must try preferred options first",
  "Missing documentation": "Notes/labs/imaging not received",
  "Wrong code/authorization pathway":
    "Coding/auth route issue (CPT/PA pathway)",
  "Non-formulary medication": "Drug not covered or needs exception",
  "Too soon since last service": "Frequency / refill too early",
  "Experimental/investigational": "Plan deems not evidence-based",
  Other: "Doesn't fit above categories",
  // Level of Care and Site of care intentionally have no helper text
};

// Keyword signals for denial text classification
const DENIAL_CLASSIFICATION_KEYWORDS: Record<string, string[]> = {
  "Step therapy required": [
    "step therapy",
    "try and fail",
    "preferred drug",
    "prior trial",
    "failure of",
    "first-line",
    "first line",
    "step-therapy",
    "tried and failed",
  ],
  "Does not meet clinical criteria": [
    "does not meet criteria",
    "criteria not met",
    "clinical criteria",
    "medical policy",
    "guideline",
    "not indicated",
    "fails to meet",
    "does not satisfy",
    "clinical policy",
  ],
  "Not medically necessary": [
    "not medically necessary",
    "not necessary",
    "not a covered service",
    "no evidence of necessity",
    "medical necessity not established",
    "lack of medical necessity",
    "medically unnecessary",
  ],
  "Missing documentation": [
    "insufficient documentation",
    "records not received",
    "notes not provided",
    "no documentation",
    "missing",
    "documentation not submitted",
    "clinical notes required",
    "please submit",
    "additional information needed",
  ],
  "Wrong code/authorization pathway": [
    "incorrect code",
    "coding",
    "cpt",
    "authorization not obtained",
    "invalid auth",
    "incorrect submission",
    "wrong pathway",
    "procedure code",
    "diagnosis code",
    "icd-10",
    "hcpcs",
    "prior authorization required",
  ],
  "Non-formulary medication": [
    "non-formulary",
    "not on formulary",
    "not covered",
    "exception",
    "tier",
    "pa required for coverage",
    "formulary exception",
    "preferred alternative",
    "generic available",
  ],
  "Too soon since last service": [
    "too soon",
    "refill too early",
    "frequency",
    "timeframe",
    "limit exceeded",
    "quantity limit",
    "days supply",
    "early refill",
    "wait period",
    "not yet eligible",
  ],
  "Level of Care": [
    "level of care",
    "inpatient",
    "observation",
    "medical necessity for admission",
    "continued stay",
    "acute care",
    "skilled nursing",
    "step-down",
    "icu",
    "admission criteria",
    "discharge criteria",
  ],
  "Site of care / Facility denial": [
    "site of care",
    "outpatient facility",
    "hospital-based",
    "freestanding",
    "place of service",
    "ambulatory surgical center",
    "office setting",
    "home infusion",
    "site-of-service",
  ],
  "Experimental/investigational": [
    "experimental",
    "investigational",
    "not proven",
    "not fda approved",
    "insufficient evidence",
    "clinical trial",
    "unproven",
    "off-label",
    "not established",
    "lacks clinical evidence",
  ],
};

// Classification result type
interface DenialClassificationResult {
  reasonKey: string;
  confidence: "high" | "medium" | "low";
  matchedSignals: string[];
  alternativeReasons?: { reasonKey: string; matchedSignals: string[] }[];
}

// Classify pasted denial text
function classifyDenialText(text: string): DenialClassificationResult {
  const normalizedText = text.toLowerCase();
  const results: {
    reasonKey: string;
    matchCount: number;
    matchedSignals: string[];
  }[] = [];

  for (const [reason, keywords] of Object.entries(
    DENIAL_CLASSIFICATION_KEYWORDS
  )) {
    const matchedSignals: string[] = [];
    for (const keyword of keywords) {
      if (normalizedText.includes(keyword.toLowerCase())) {
        matchedSignals.push(keyword);
      }
    }
    if (matchedSignals.length > 0) {
      results.push({
        reasonKey: reason,
        matchCount: matchedSignals.length,
        matchedSignals,
      });
    }
  }

  // Sort by match count descending
  results.sort((a, b) => b.matchCount - a.matchCount);

  if (results.length === 0) {
    return {
      reasonKey: "Other",
      confidence: "low",
      matchedSignals: [],
      alternativeReasons: [],
    };
  }

  const top = results[0];
  const second = results[1];

  // Determine confidence
  let confidence: "high" | "medium" | "low";
  if (top.matchCount >= 3) {
    confidence = "high";
  } else if (
    top.matchCount >= 2 ||
    (top.matchCount === 1 && (!second || second.matchCount === 0))
  ) {
    confidence = "medium";
  } else {
    confidence = "low";
  }

  // Include alternatives for low confidence
  const alternativeReasons = results.slice(1, 3).map((r) => ({
    reasonKey: r.reasonKey,
    matchedSignals: r.matchedSignals,
  }));

  return {
    reasonKey: top.reasonKey,
    confidence,
    matchedSignals: top.matchedSignals,
    alternativeReasons: confidence === "low" ? alternativeReasons : undefined,
  };
}

// Grouped denial reasons for better UX organization
const DENIAL_REASON_GROUPS = [
  {
    label: "Common",
    reasons: [
      "Not medically necessary",
      "Does not meet clinical criteria",
      "Step therapy required",
      "Missing documentation",
    ],
  },
  {
    label: "Coverage / Authorization",
    reasons: [
      "Wrong code/authorization pathway",
      "Non-formulary medication",
      "Too soon since last service",
    ],
  },
  {
    label: "Setting / Level",
    reasons: ["Level of Care", "Site of care / Facility denial"],
  },
  {
    label: "Other",
    reasons: ["Experimental/investigational", "Other"],
  },
];

// Top insurers for quick selection (8-12 tiles)
const TOP_INSURERS = [
  "united",
  "anthem",
  "aetna",
  "cigna",
  "humana",
  "kaiser",
  "medicare_original",
  "medicaid",
  "bcbs",
];

// Short labels for mobile display in the top insurers grid
const TOP_INSURER_SHORT_LABELS: Record<string, string> = {
  united: "United",
  anthem: "Anthem",
  aetna: "Aetna",
  cigna: "Cigna",
  humana: "Humana",
  kaiser: "Kaiser",
  medicare_original: "Medicare",
  medicaid: "Medicaid",
  bcbs: "BCBS",
};

// Plan type chips for optional selection
const PLAN_TYPE_CHIPS = [
  { value: "PPO", label: "PPO" },
  { value: "HMO", label: "HMO" },
  { value: "EPO", label: "EPO" },
  { value: "POS", label: "POS" },
  { value: "MEDICARE_ADVANTAGE", label: "Medicare Advantage" },
  { value: "MEDICARE_ORIGINAL", label: "Medicare (Original)" },
  { value: "MEDICAID", label: "Medicaid" },
  { value: "OTHER", label: "Other" },
  { value: "UNKNOWN", label: "Unknown" },
];

// Smart defaults: insurer → suggested plan type
const INSURER_PLAN_TYPE_DEFAULTS: Record<string, string> = {
  medicare_original: "MEDICARE_ORIGINAL",
  medicaid: "MEDICAID",
  kaiser: "HMO",
};

// Get suggested plan type based on insurer
function getSuggestedPlanType(insurerValue: string): string {
  return INSURER_PLAN_TYPE_DEFAULTS[insurerValue] || "UNKNOWN";
}

const MEDICATION_CLASSES = [
  "Antibiotic",
  "Antidepressant (SSRI/SNRI)",
  "Antidiabetic",
  "Antihypertensive",
  "Anticoagulant",
  "Biologic / Immunomodulator",
  "Cardiovascular",
  "Dermatologic",
  "GI / Acid suppression",
  "GLP-1 Agonist",
  "Lipid-lowering",
  "Neurologic / Antiepileptic",
  "Opioid / Pain",
  "Oncology",
  "Psychiatric",
  "Pulmonary / Respiratory",
  "Rheumatologic",
  "SGLT2 Inhibitor",
  "Other",
];

const FORMULARY_STATUS = [
  { value: "non_formulary", label: "Non-formulary" },
  { value: "tier_3", label: "Tier 3 (Preferred brand)" },
  { value: "tier_4", label: "Tier 4 (Non-preferred brand)" },
  { value: "tier_5", label: "Tier 5 (Specialty)" },
  { value: "pa_required", label: "PA Required (formulary)" },
  { value: "step_therapy", label: "Step therapy required" },
  { value: "quantity_limit", label: "Quantity limit exceeded" },
  { value: "unknown", label: "Unknown" },
];

const IMAGING_TYPES = [
  "MRI",
  "CT",
  "PET",
  "PET-CT",
  "Ultrasound",
  "X-ray",
  "Nuclear medicine",
  "DEXA scan",
  "Mammogram",
  "Fluoroscopy",
  "Angiography",
  "Other",
];

const BODY_REGIONS = [
  "Head / Brain",
  "Neck / Cervical spine",
  "Chest / Thorax",
  "Thoracic spine",
  "Abdomen",
  "Pelvis",
  "Lumbar spine",
  "Shoulder",
  "Elbow / Forearm",
  "Wrist / Hand",
  "Hip",
  "Knee",
  "Ankle / Foot",
  "Extremity (other)",
  "Whole body",
  "Other",
];

const PROCEDURE_URGENCY = [
  { value: "elective", label: "Elective" },
  { value: "urgent", label: "Urgent (within days-weeks)" },
  { value: "emergent", label: "Emergent (immediate)" },
];

const SPECIALTY_TYPES = [
  "Allergy / Immunology",
  "Cardiology",
  "Dermatology",
  "Endocrinology",
  "Gastroenterology",
  "Hematology / Oncology",
  "Infectious Disease",
  "Nephrology",
  "Neurology",
  "Neurosurgery",
  "Ob/Gyn",
  "Ophthalmology",
  "Orthopedic Surgery",
  "Pain Management",
  "Physical Medicine / Rehab",
  "Psychiatry",
  "Pulmonology",
  "Rheumatology",
  "Surgery (General)",
  "Urology",
  "Vascular Surgery",
  "Other",
];

const DME_CATEGORIES = [
  "CPAP / BiPAP",
  "Oxygen equipment",
  "Hospital bed",
  "Wheelchair (manual)",
  "Wheelchair (power)",
  "Walker / Rollator",
  "Orthotic / Brace",
  "Prosthetic",
  "Glucose monitor / CGM",
  "Insulin pump",
  "Nebulizer",
  "Wound care supplies",
  "Compression garments",
  "Other",
];

// Facility types for site-of-care selection
const FACILITY_TYPES = [
  { value: "hospital", label: "Hospital" },
  { value: "asc", label: "Ambulatory Surgery Center" },
  { value: "imaging_center", label: "Imaging Center" },
  { value: "office", label: "Office/Clinic" },
  { value: "not_sure", label: "Not sure" },
];

// Clinical factors supporting hospital-based care (for site-of-care justification)
const HOSPITAL_JUSTIFICATION_FACTORS = [
  {
    id: "anesthesia_risk",
    label: "Anesthesia or airway risk",
    description: "Difficult airway, OSA, or anesthesia complications history",
  },
  {
    id: "high_bmi",
    label: "Elevated BMI",
    description: "BMI >40 or obesity-related comorbidities",
  },
  {
    id: "complex_procedure",
    label: "Complex procedure",
    description:
      "Elevated bleeding risk, long duration, or technical complexity",
  },
  {
    id: "icu_monitoring",
    label: "May need ICU monitoring",
    description: "Reasonable likelihood of requiring post-op ICU admission",
  },
  {
    id: "subspecialty_backup",
    label: "Need for specialty backup",
    description:
      "May require subspecialist intervention (IR, cardiology, etc.)",
  },
  {
    id: "specialized_equipment",
    label: "Specialized equipment needed",
    description: "Equipment not available at lower-acuity settings",
  },
  {
    id: "prior_complications",
    label: "Prior complications",
    description: "History of surgical or procedural complications",
  },
  {
    id: "cardiac_risk",
    label: "Cardiac risk factors",
    description: "History of MI, arrhythmia, or significant cardiac disease",
  },
  {
    id: "anticoagulation",
    label: "Anticoagulation management",
    description: "On anticoagulants requiring bridging or reversal",
  },
  {
    id: "pregnancy_postpartum",
    label: "Pregnancy or postpartum",
    description: "Requires coordination with obstetric services",
  },
  {
    id: "comorbidities",
    label: "Significant comorbidities",
    description: "Multiple or complex medical conditions affecting safety",
  },
  {
    id: "other",
    label: "Other clinical factors",
    description: "Additional factors supporting hospital setting",
  },
];

// Request types that should show facility selection
const FACILITY_RELEVANT_REQUEST_TYPES = [
  "Imaging",
  "Procedure",
  "Surgery",
  "Admission",
  "Infusion",
];

// ============================================
// LEVEL OF CARE (LOC) CONSTANTS
// ============================================

const LOC_LEVELS = [
  { value: "icu", label: "ICU / Critical Care" },
  { value: "stepdown", label: "Step-down / IMC / PCU" },
  { value: "inpatient", label: "Inpatient (Med/Surg)" },
  { value: "observation", label: "Observation" },
  { value: "psych_inpatient", label: "Psychiatric Inpatient" },
  { value: "snf", label: "Skilled Nursing Facility (SNF)" },
  { value: "ltac", label: "Long-Term Acute Care (LTAC)" },
  { value: "rehab_inpatient", label: "Inpatient Rehabilitation" },
  { value: "home_health", label: "Home Health" },
  { value: "outpatient", label: "Outpatient" },
];

const LOC_REVIEW_TYPES = [
  { value: "initial_admission", label: "Initial Admission" },
  { value: "continued_stay", label: "Continued Stay Review" },
  { value: "downgrade", label: "Downgrade / Level Change" },
  { value: "discharge_appeal", label: "Discharge Appeal" },
  { value: "retro_review", label: "Retrospective Review" },
];

// ============================================
// TYPES
// ============================================

interface TriedTreatmentOption {
  value: string;
  label: string;
  category: string;
}

interface ComorbidityOption {
  value: string;
  label: string;
  category: string;
}

interface ICD10Result {
  code: string;
  description: string;
}

// ============================================
// DATA CONSTANTS
// ============================================

const TRIED_TREATMENTS: TriedTreatmentOption[] = [
  // First-line Medications
  {
    value: "metformin",
    label: "Metformin",
    category: "First-line Medications",
  },
  {
    value: "lisinopril",
    label: "Lisinopril / ACE inhibitor",
    category: "First-line Medications",
  },
  {
    value: "amlodipine",
    label: "Amlodipine / CCB",
    category: "First-line Medications",
  },
  {
    value: "atorvastatin",
    label: "Atorvastatin / Statin",
    category: "First-line Medications",
  },
  {
    value: "omeprazole",
    label: "Omeprazole / PPI",
    category: "First-line Medications",
  },
  {
    value: "gabapentin",
    label: "Gabapentin",
    category: "First-line Medications",
  },
  {
    value: "nsaids",
    label: "NSAIDs (ibuprofen, naproxen)",
    category: "First-line Medications",
  },
  {
    value: "acetaminophen",
    label: "Acetaminophen",
    category: "First-line Medications",
  },
  {
    value: "ssri",
    label: "SSRI (sertraline, escitalopram, etc.)",
    category: "First-line Medications",
  },
  {
    value: "snri",
    label: "SNRI (duloxetine, venlafaxine)",
    category: "First-line Medications",
  },
  {
    value: "sulfonylurea",
    label: "Sulfonylurea (glipizide, glimepiride)",
    category: "First-line Medications",
  },
  {
    value: "thiazide",
    label: "Thiazide diuretic (HCTZ)",
    category: "First-line Medications",
  },
  {
    value: "beta_blocker",
    label: "Beta blocker (metoprolol, carvedilol)",
    category: "First-line Medications",
  },
  {
    value: "albuterol",
    label: "Albuterol / SABA",
    category: "First-line Medications",
  },
  {
    value: "ics",
    label: "Inhaled corticosteroid",
    category: "First-line Medications",
  },

  // Physical Therapy & Rehab
  {
    value: "pt_general",
    label: "Physical therapy (general)",
    category: "Physical Therapy & Rehab",
  },
  {
    value: "pt_6weeks",
    label: "PT - completed 6+ weeks",
    category: "Physical Therapy & Rehab",
  },
  {
    value: "pt_12weeks",
    label: "PT - completed 12+ weeks",
    category: "Physical Therapy & Rehab",
  },
  {
    value: "ot",
    label: "Occupational therapy",
    category: "Physical Therapy & Rehab",
  },
  {
    value: "cardiac_rehab",
    label: "Cardiac rehabilitation",
    category: "Physical Therapy & Rehab",
  },
  {
    value: "pulm_rehab",
    label: "Pulmonary rehabilitation",
    category: "Physical Therapy & Rehab",
  },
  {
    value: "speech_therapy",
    label: "Speech therapy",
    category: "Physical Therapy & Rehab",
  },
  {
    value: "home_exercises",
    label: "Home exercise program",
    category: "Physical Therapy & Rehab",
  },
  {
    value: "aquatic_therapy",
    label: "Aquatic therapy",
    category: "Physical Therapy & Rehab",
  },

  // Conservative / Non-Pharmacologic
  {
    value: "rest_ice",
    label: "Rest / Ice / Elevation",
    category: "Conservative Treatments",
  },
  {
    value: "bracing",
    label: "Bracing / Splinting",
    category: "Conservative Treatments",
  },
  {
    value: "compression",
    label: "Compression therapy",
    category: "Conservative Treatments",
  },
  { value: "tens", label: "TENS unit", category: "Conservative Treatments" },
  {
    value: "chiropractic",
    label: "Chiropractic care",
    category: "Conservative Treatments",
  },
  {
    value: "acupuncture",
    label: "Acupuncture",
    category: "Conservative Treatments",
  },
  {
    value: "massage",
    label: "Massage therapy",
    category: "Conservative Treatments",
  },
  {
    value: "weight_loss",
    label: "Weight loss program",
    category: "Conservative Treatments",
  },
  {
    value: "diet_modification",
    label: "Diet modification",
    category: "Conservative Treatments",
  },
  {
    value: "smoking_cessation",
    label: "Smoking cessation",
    category: "Conservative Treatments",
  },
  {
    value: "sleep_hygiene",
    label: "Sleep hygiene education",
    category: "Conservative Treatments",
  },
  { value: "cpap", label: "CPAP trial", category: "Conservative Treatments" },

  // Injections & Procedures
  {
    value: "steroid_injection",
    label: "Steroid injection (joint/epidural)",
    category: "Injections & Procedures",
  },
  {
    value: "trigger_point",
    label: "Trigger point injection",
    category: "Injections & Procedures",
  },
  {
    value: "nerve_block",
    label: "Nerve block",
    category: "Injections & Procedures",
  },
  {
    value: "joint_aspiration",
    label: "Joint aspiration",
    category: "Injections & Procedures",
  },
  {
    value: "hyaluronic_acid",
    label: "Hyaluronic acid injection",
    category: "Injections & Procedures",
  },
  { value: "prp", label: "PRP injection", category: "Injections & Procedures" },
  {
    value: "botox",
    label: "Botox injection",
    category: "Injections & Procedures",
  },

  // Prior Imaging
  {
    value: "xray",
    label: "X-ray completed",
    category: "Prior Imaging/Testing",
  },
  {
    value: "ct_prior",
    label: "CT scan completed",
    category: "Prior Imaging/Testing",
  },
  {
    value: "mri_prior",
    label: "MRI completed",
    category: "Prior Imaging/Testing",
  },
  {
    value: "ultrasound_prior",
    label: "Ultrasound completed",
    category: "Prior Imaging/Testing",
  },
  {
    value: "echo",
    label: "Echocardiogram completed",
    category: "Prior Imaging/Testing",
  },
  {
    value: "stress_test",
    label: "Stress test completed",
    category: "Prior Imaging/Testing",
  },
  {
    value: "sleep_study",
    label: "Sleep study completed",
    category: "Prior Imaging/Testing",
  },
  { value: "eeg", label: "EEG completed", category: "Prior Imaging/Testing" },
  {
    value: "emg",
    label: "EMG/NCS completed",
    category: "Prior Imaging/Testing",
  },
  {
    value: "colonoscopy",
    label: "Colonoscopy completed",
    category: "Prior Imaging/Testing",
  },
  {
    value: "endoscopy",
    label: "Endoscopy completed",
    category: "Prior Imaging/Testing",
  },
  {
    value: "pulm_function",
    label: "Pulmonary function tests",
    category: "Prior Imaging/Testing",
  },

  // Prior Specialist Consultations
  {
    value: "consult_cardio",
    label: "Cardiology consult",
    category: "Prior Consultations",
  },
  {
    value: "consult_neuro",
    label: "Neurology consult",
    category: "Prior Consultations",
  },
  {
    value: "consult_ortho",
    label: "Orthopedics consult",
    category: "Prior Consultations",
  },
  {
    value: "consult_pain",
    label: "Pain management consult",
    category: "Prior Consultations",
  },
  {
    value: "consult_rheum",
    label: "Rheumatology consult",
    category: "Prior Consultations",
  },
  {
    value: "consult_endo",
    label: "Endocrinology consult",
    category: "Prior Consultations",
  },
  { value: "consult_gi", label: "GI consult", category: "Prior Consultations" },
  {
    value: "consult_pulm",
    label: "Pulmonology consult",
    category: "Prior Consultations",
  },
  {
    value: "consult_psych",
    label: "Psychiatry consult",
    category: "Prior Consultations",
  },
  {
    value: "consult_onc",
    label: "Oncology consult",
    category: "Prior Consultations",
  },

  // Failed/Intolerant Outcomes
  {
    value: "failed_efficacy",
    label: "Failed due to lack of efficacy",
    category: "Treatment Outcomes",
  },
  {
    value: "failed_gi_intolerance",
    label: "Failed due to GI intolerance",
    category: "Treatment Outcomes",
  },
  {
    value: "failed_allergy",
    label: "Failed due to allergic reaction",
    category: "Treatment Outcomes",
  },
  {
    value: "failed_side_effects",
    label: "Failed due to side effects",
    category: "Treatment Outcomes",
  },
  {
    value: "contraindicated",
    label: "Contraindicated",
    category: "Treatment Outcomes",
  },
  {
    value: "max_dose_reached",
    label: "Maximum dose reached",
    category: "Treatment Outcomes",
  },
];

const TRIED_CATEGORIES = [
  "First-line Medications",
  "Physical Therapy & Rehab",
  "Conservative Treatments",
  "Injections & Procedures",
  "Prior Imaging/Testing",
  "Prior Consultations",
  "Treatment Outcomes",
];

const COMORBIDITIES: ComorbidityOption[] = [
  // Cardiovascular
  { value: "htn", label: "Hypertension (HTN)", category: "Cardiovascular" },
  {
    value: "cad",
    label: "Coronary Artery Disease (CAD)",
    category: "Cardiovascular",
  },
  {
    value: "chf",
    label: "Congestive Heart Failure (CHF)",
    category: "Cardiovascular",
  },
  {
    value: "hfref",
    label: "Heart Failure with Reduced EF (HFrEF)",
    category: "Cardiovascular",
  },
  {
    value: "hfpef",
    label: "Heart Failure with Preserved EF (HFpEF)",
    category: "Cardiovascular",
  },
  {
    value: "afib",
    label: "Atrial Fibrillation (AFib)",
    category: "Cardiovascular",
  },
  {
    value: "pvd",
    label: "Peripheral Vascular Disease (PVD)",
    category: "Cardiovascular",
  },
  {
    value: "dvt",
    label: "Deep Vein Thrombosis (DVT)",
    category: "Cardiovascular",
  },
  { value: "pe", label: "Pulmonary Embolism (PE)", category: "Cardiovascular" },

  // Endocrine
  { value: "dm1", label: "Type 1 Diabetes Mellitus", category: "Endocrine" },
  { value: "dm2", label: "Type 2 Diabetes Mellitus", category: "Endocrine" },
  { value: "hypothyroid", label: "Hypothyroidism", category: "Endocrine" },
  { value: "hyperthyroid", label: "Hyperthyroidism", category: "Endocrine" },
  { value: "obesity", label: "Obesity", category: "Endocrine" },
  {
    value: "metabolic_syndrome",
    label: "Metabolic Syndrome",
    category: "Endocrine",
  },
  {
    value: "pcos",
    label: "Polycystic Ovary Syndrome (PCOS)",
    category: "Endocrine",
  },

  // Renal
  { value: "ckd1", label: "CKD Stage 1", category: "Renal" },
  { value: "ckd2", label: "CKD Stage 2", category: "Renal" },
  { value: "ckd3", label: "CKD Stage 3", category: "Renal" },
  { value: "ckd4", label: "CKD Stage 4", category: "Renal" },
  { value: "ckd5", label: "CKD Stage 5 / ESRD", category: "Renal" },
  { value: "dialysis", label: "On Dialysis", category: "Renal" },
  { value: "kidney_transplant", label: "Kidney Transplant", category: "Renal" },

  // Pulmonary
  { value: "copd", label: "COPD", category: "Pulmonary" },
  { value: "asthma", label: "Asthma", category: "Pulmonary" },
  {
    value: "osa",
    label: "Obstructive Sleep Apnea (OSA)",
    category: "Pulmonary",
  },
  { value: "pulm_htn", label: "Pulmonary Hypertension", category: "Pulmonary" },
  { value: "ild", label: "Interstitial Lung Disease", category: "Pulmonary" },
  { value: "bronchiectasis", label: "Bronchiectasis", category: "Pulmonary" },

  // Gastrointestinal
  { value: "gerd", label: "GERD", category: "Gastrointestinal" },
  { value: "cirrhosis", label: "Cirrhosis", category: "Gastrointestinal" },
  { value: "hepatitis_b", label: "Hepatitis B", category: "Gastrointestinal" },
  { value: "hepatitis_c", label: "Hepatitis C", category: "Gastrointestinal" },
  { value: "nafld", label: "NAFLD / NASH", category: "Gastrointestinal" },
  {
    value: "ibd",
    label: "Inflammatory Bowel Disease",
    category: "Gastrointestinal",
  },
  { value: "crohns", label: "Crohn's Disease", category: "Gastrointestinal" },
  { value: "uc", label: "Ulcerative Colitis", category: "Gastrointestinal" },
  {
    value: "pancreatitis",
    label: "Chronic Pancreatitis",
    category: "Gastrointestinal",
  },

  // Neurological
  {
    value: "stroke",
    label: "History of Stroke / CVA",
    category: "Neurological",
  },
  { value: "tia", label: "History of TIA", category: "Neurological" },
  {
    value: "seizure",
    label: "Seizure Disorder / Epilepsy",
    category: "Neurological",
  },
  {
    value: "parkinsons",
    label: "Parkinson's Disease",
    category: "Neurological",
  },
  { value: "ms", label: "Multiple Sclerosis", category: "Neurological" },
  { value: "dementia", label: "Dementia", category: "Neurological" },
  {
    value: "neuropathy",
    label: "Peripheral Neuropathy",
    category: "Neurological",
  },
  { value: "migraine", label: "Chronic Migraine", category: "Neurological" },

  // Musculoskeletal
  { value: "oa", label: "Osteoarthritis", category: "Musculoskeletal" },
  { value: "ra", label: "Rheumatoid Arthritis", category: "Musculoskeletal" },
  { value: "osteoporosis", label: "Osteoporosis", category: "Musculoskeletal" },
  { value: "gout", label: "Gout", category: "Musculoskeletal" },
  {
    value: "lupus",
    label: "Systemic Lupus Erythematosus (SLE)",
    category: "Musculoskeletal",
  },
  { value: "fibromyalgia", label: "Fibromyalgia", category: "Musculoskeletal" },
  {
    value: "back_pain",
    label: "Chronic Back Pain",
    category: "Musculoskeletal",
  },

  // Psychiatric
  {
    value: "depression",
    label: "Major Depressive Disorder",
    category: "Psychiatric",
  },
  {
    value: "anxiety",
    label: "Generalized Anxiety Disorder",
    category: "Psychiatric",
  },
  { value: "bipolar", label: "Bipolar Disorder", category: "Psychiatric" },
  { value: "schizophrenia", label: "Schizophrenia", category: "Psychiatric" },
  { value: "ptsd", label: "PTSD", category: "Psychiatric" },
  { value: "sud", label: "Substance Use Disorder", category: "Psychiatric" },

  // Hematologic / Oncologic
  { value: "anemia", label: "Anemia", category: "Hematologic/Oncologic" },
  {
    value: "cancer_active",
    label: "Active Malignancy",
    category: "Hematologic/Oncologic",
  },
  {
    value: "cancer_history",
    label: "History of Cancer (in remission)",
    category: "Hematologic/Oncologic",
  },
  {
    value: "anticoagulation",
    label: "On Anticoagulation",
    category: "Hematologic/Oncologic",
  },

  // Infectious
  { value: "hiv", label: "HIV / AIDS", category: "Infectious" },
  {
    value: "immunocompromised",
    label: "Immunocompromised",
    category: "Infectious",
  },

  // Other
  { value: "pregnancy", label: "Pregnancy", category: "Other" },
  { value: "transplant", label: "Organ Transplant (other)", category: "Other" },
  {
    value: "allergies_multiple",
    label: "Multiple Drug Allergies",
    category: "Other",
  },
];

const COMORBIDITY_CATEGORIES = [
  "Cardiovascular",
  "Endocrine",
  "Renal",
  "Pulmonary",
  "Gastrointestinal",
  "Neurological",
  "Musculoskeletal",
  "Psychiatric",
  "Hematologic/Oncologic",
  "Infectious",
  "Other",
];

// ============================================
// SUBCOMPONENTS
// ============================================

// Template Selector Component
function TemplateSelector({
  onSelectTemplate,
  onStartFresh,
  orderedTemplates,
}: {
  onSelectTemplate: (template: CaseTemplate) => void;
  onStartFresh: () => void;
  orderedTemplates?: CaseTemplate[];
}) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Use ordered templates if provided, otherwise fall back to default
  const templates = orderedTemplates || CASE_TEMPLATES;

  const filteredTemplates = selectedCategory
    ? templates.filter((t) => t.category === selectedCategory)
    : templates;

  const groupedTemplates = TEMPLATE_CATEGORIES.map((cat) => ({
    ...cat,
    templates: templates.filter((t) => t.category === cat.id),
  }));

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="w-full mb-6 p-4 bg-white border border-slate-200 rounded-xl text-left hover:border-slate-300 hover:bg-slate-50/50 transition-all group"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
              <IconSparkles className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="font-medium text-slate-600">
                Quick Start Templates
              </p>
              <p className="text-sm text-slate-400">
                Choose a common scenario to pre-fill the form
              </p>
            </div>
          </div>
          <IconChevronDown className="w-5 h-5 text-slate-400 group-hover:text-slate-500 transition-colors" />
        </div>
      </button>
    );
  }

  return (
    <div className="mb-6 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-6 py-3 bg-slate-50 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center">
              <IconSparkles className="w-4 h-4 text-slate-300" />
            </div>
            <div>
              <h2 className="font-medium text-slate-400 text-sm">
                Quick Start
              </h2>
              <p className="text-xs text-slate-400">
                Choose a template or start from scratch
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(false)}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <IconX className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="px-6 py-3 border-b border-slate-100 bg-slate-50/50">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
              selectedCategory === null
                ? "bg-teal-100 text-teal-700"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            All
          </button>
          {TEMPLATE_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                selectedCategory === cat.id
                  ? "bg-teal-100 text-teal-700"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {getCategoryIcon(cat.icon, "w-4 h-4")}
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Templates Grid */}
      <div className="p-4">
        {selectedCategory === null ? (
          <div className="space-y-4">
            {groupedTemplates.map((group) => (
              <div key={group.id}>
                <div className="flex items-center gap-2 mb-2 px-1">
                  {getCategoryIcon(group.icon, "w-4 h-4 text-slate-400")}
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {group.label}
                  </h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {group.templates.slice(0, 3).map((template) => (
                    <button
                      key={template.id}
                      onClick={() => onSelectTemplate(template)}
                      className="p-3 text-left border border-slate-200 rounded-xl hover:border-teal-300 hover:bg-teal-50/50 transition-all group"
                    >
                      <p className="font-medium text-sm text-slate-800 group-hover:text-teal-700 transition-colors">
                        {template.name}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                        {template.description}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {filteredTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => onSelectTemplate(template)}
                className="p-3 text-left border border-slate-200 rounded-xl hover:border-teal-300 hover:bg-teal-50/50 transition-all group"
              >
                <p className="font-medium text-sm text-slate-800 group-hover:text-teal-700 transition-colors">
                  {template.name}
                </p>
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                  {template.description}
                </p>
              </button>
            ))}
          </div>
        )}

        {/* Start Fresh Button */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <button
            onClick={onStartFresh}
            className="w-full p-3 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-xl transition-all flex items-center justify-center gap-2"
          >
            <IconPlus className="w-4 h-4" />
            Start with blank form
          </button>
        </div>
      </div>
    </div>
  );
}

// Tried Treatments Multi-Select Component
function TriedTreatmentsSelect({
  selected,
  onChange,
}: {
  selected: SelectedTreatment[];
  onChange: (selected: SelectedTreatment[]) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = TRIED_TREATMENTS.filter(
    (option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedOptions = TRIED_CATEGORIES.map((category) => ({
    category,
    options: filteredOptions.filter((o) => o.category === category),
  })).filter((group) => group.options.length > 0);

  const toggleOption = (option: TriedTreatmentOption) => {
    const isSelected = selected.some((s) => s.id === option.value);
    if (isSelected) {
      onChange(selected.filter((s) => s.id !== option.value));
    } else {
      onChange([...selected, { id: option.value, label: option.label }]);
    }
  };

  const removeItem = (id: string) => {
    onChange(selected.filter((s) => s.id !== id));
  };

  return (
    <div className="space-y-3">
      <div ref={containerRef} className="relative">
        <div
          onClick={() => {
            setIsOpen(true);
            inputRef.current?.focus();
          }}
          className={`min-h-[42px] w-full border rounded-lg px-3 py-2 cursor-text transition-colors ${
            isOpen
              ? "border-teal-500 ring-1 ring-teal-500/20"
              : "border-slate-300 hover:border-slate-400"
          }`}
        >
          <div className="flex flex-wrap gap-2 pr-6">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setIsOpen(true)}
              placeholder="Search treatments, tests, consultations..."
              className="flex-1 min-w-[200px] outline-none text-sm bg-transparent"
            />
          </div>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <IconChevronDown
              className={`w-4 h-4 text-slate-400 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </div>
        </div>

        {isOpen && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200/80 rounded-xl shadow-xl shadow-slate-200/50 max-h-72 overflow-y-auto">
            {groupedOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-500">
                No treatments found
              </div>
            ) : (
              groupedOptions.map((group) => (
                <div key={group.category}>
                  <div className="px-3 py-2 bg-gradient-to-r from-slate-100 to-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wider sticky top-0 border-b border-slate-100">
                    {group.category}
                  </div>
                  {group.options.map((option) => {
                    const isSelected = selected.some(
                      (s) => s.id === option.value
                    );
                    return (
                      <button
                        type="button"
                        key={option.value}
                        onClick={() => toggleOption(option)}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-slate-50 ${
                          isSelected
                            ? "text-teal-700 bg-teal-50/50"
                            : "text-slate-700"
                        }`}
                      >
                        <span>{option.label}</span>
                        {isSelected && (
                          <IconCheck className="w-4 h-4 text-teal-500" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-3">
          {selected.map((treatment) => (
            <span
              key={treatment.id}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium bg-gradient-to-r from-amber-50 to-amber-100/80 text-amber-700 border border-amber-200/50 shadow-sm transition-all hover:shadow"
            >
              <span>{treatment.label}</span>
              <button
                type="button"
                onClick={() => removeItem(treatment.id)}
                className="rounded-md p-0.5 hover:bg-amber-200/60 transition-colors"
              >
                <IconX className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Common Conditions Multi-Select Component
function CommonConditionsSelect({
  selected,
  onChange,
}: {
  selected: SelectedCondition[];
  onChange: (selected: SelectedCondition[]) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = COMORBIDITIES.filter(
    (option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedOptions = COMORBIDITY_CATEGORIES.map((category) => ({
    category,
    options: filteredOptions.filter((o) => o.category === category),
  })).filter((group) => group.options.length > 0);

  const toggleOption = (option: ComorbidityOption) => {
    const isSelected = selected.some((s) => s.id === option.value);
    if (isSelected) {
      onChange(selected.filter((s) => s.id !== option.value));
    } else {
      onChange([
        ...selected,
        { id: option.value, label: option.label, type: "common" },
      ]);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
        className={`min-h-[44px] w-full border rounded-xl px-3 py-2.5 cursor-text transition-all ${
          isOpen
            ? "border-teal-500 ring-2 ring-teal-500/20 bg-white"
            : "border-slate-200 bg-slate-50/50 hover:border-slate-300 hover:bg-white"
        }`}
      >
        <div className="flex flex-wrap gap-2 pr-6">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsOpen(true)}
            placeholder="Search common conditions..."
            className="flex-1 min-w-[150px] outline-none text-sm bg-transparent"
          />
        </div>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <IconChevronDown
            className={`w-4 h-4 text-slate-400 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200/80 rounded-xl shadow-xl shadow-slate-200/50 max-h-72 overflow-y-auto">
          {groupedOptions.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-500">
              No conditions found
            </div>
          ) : (
            groupedOptions.map((group) => (
              <div key={group.category}>
                <div className="px-3 py-2 bg-gradient-to-r from-slate-100 to-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wider sticky top-0 border-b border-slate-100">
                  {group.category}
                </div>
                {group.options.map((option) => {
                  const isSelected = selected.some(
                    (s) => s.id === option.value
                  );
                  return (
                    <button
                      type="button"
                      key={option.value}
                      onClick={() => toggleOption(option)}
                      className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-slate-50 ${
                        isSelected
                          ? "text-teal-700 bg-teal-50/50"
                          : "text-slate-700"
                      }`}
                    >
                      <span>{option.label}</span>
                      {isSelected && (
                        <IconCheck className="w-4 h-4 text-teal-500" />
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ICD-10 Search Component
function ICD10Search({
  selected,
  onChange,
}: {
  selected: SelectedCondition[];
  onChange: (selected: SelectedCondition[]) => void;
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<ICD10Result[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchTerm.length < 2) {
      setResults([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search?sf=code,name&terms=${encodeURIComponent(
            searchTerm
          )}&maxList=25`
        );
        const data = await response.json();
        const formattedResults: ICD10Result[] = (data[3] || []).map(
          (item: string[]) => ({
            code: item[0],
            description: item[1],
          })
        );
        setResults(formattedResults);
      } catch (error) {
        console.error("ICD-10 search error:", error);
        setResults([]);
      }
      setIsLoading(false);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm]);

  const addResult = (result: ICD10Result) => {
    const id = `icd10_${result.code}`;
    if (!selected.some((s) => s.id === id)) {
      onChange([
        ...selected,
        {
          id,
          label: `${result.code} - ${result.description}`,
          type: "icd10",
          code: result.code,
        },
      ]);
    }
    setSearchTerm("");
    setResults([]);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`flex items-center border rounded-lg transition-colors ${
          isOpen
            ? "border-teal-500 ring-1 ring-teal-500/20"
            : "border-slate-300 hover:border-slate-400"
        }`}
      >
        <div className="pl-3">
          <IconSearch className="w-4 h-4 text-slate-400" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search by ICD-10 code or description..."
          className="flex-1 px-3 py-2 text-sm outline-none bg-transparent"
        />
        {isLoading && (
          <div className="pr-3">
            <IconLoader className="w-4 h-4 text-slate-400" />
          </div>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200/80 rounded-xl shadow-xl shadow-slate-200/50 max-h-72 overflow-y-auto">
          {results.map((result) => {
            const isSelected = selected.some((s) => s.code === result.code);
            return (
              <button
                type="button"
                key={result.code}
                onClick={() => addResult(result)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-start gap-2 ${
                  isSelected ? "text-teal-700 bg-teal-50/50" : "text-slate-700"
                }`}
              >
                <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-600 flex-shrink-0">
                  {result.code}
                </span>
                <span className="flex-1">{result.description}</span>
                {isSelected && (
                  <IconCheck className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {isOpen &&
        searchTerm.length >= 2 &&
        !isLoading &&
        results.length === 0 && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200/80 rounded-xl shadow-xl shadow-slate-200/50">
            <div className="px-4 py-3 text-sm text-slate-500">
              No results found
            </div>
          </div>
        )}
    </div>
  );
}

// Combined Diagnoses Selector
function DiagnosesSelector({
  selected,
  onChange,
}: {
  selected: SelectedCondition[];
  onChange: (selected: SelectedCondition[]) => void;
}) {
  const [activeTab, setActiveTab] = useState<"common" | "icd10">("common");

  const removeCondition = (id: string) => {
    onChange(selected.filter((s) => s.id !== id));
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-1 p-1 bg-slate-100/80 rounded-xl w-fit">
        <button
          type="button"
          onClick={() => setActiveTab("common")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
            activeTab === "common"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
          }`}
        >
          Common Conditions
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("icd10")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
            activeTab === "icd10"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
          }`}
        >
          Search ICD-10
        </button>
      </div>

      {activeTab === "common" ? (
        <CommonConditionsSelect selected={selected} onChange={onChange} />
      ) : (
        <ICD10Search selected={selected} onChange={onChange} />
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-3">
          {selected.map((condition) => (
            <span
              key={condition.id}
              className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium shadow-sm transition-all hover:shadow ${
                condition.type === "icd10"
                  ? "bg-gradient-to-r from-blue-50 to-blue-100/80 text-blue-700 border border-blue-200/50"
                  : "bg-gradient-to-r from-teal-50 to-teal-100/80 text-teal-700 border border-teal-200/50"
              }`}
            >
              {condition.type === "icd10" && condition.code && (
                <span className="font-mono text-xs bg-blue-100/80 px-1.5 py-0.5 rounded">
                  {condition.code}
                </span>
              )}
              <span>
                {condition.type === "icd10"
                  ? condition.label.split(" - ")[1]
                  : condition.label}
              </span>
              <button
                type="button"
                onClick={() => removeCondition(condition.id)}
                className={`rounded-md p-0.5 transition-colors ${
                  condition.type === "icd10"
                    ? "hover:bg-blue-200/60"
                    : "hover:bg-teal-200/60"
                }`}
              >
                <IconX className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Insurance Company Visual Pill Selector
function InsuranceSelect({
  value,
  onChange,
  onRegionalTrigger,
  planType,
  onPlanTypeChange,
  availablePlans,
}: {
  value: string;
  onChange: (value: string) => void;
  onRegionalTrigger?: (insurerKey: string) => void;
  planType?: string;
  onPlanTypeChange?: (value: string) => void;
  availablePlans?: { value: string; label: string; description: string }[];
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showAllInsurers, setShowAllInsurers] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Get the top insurers from our constant, maintaining order
  const topInsurers = TOP_INSURERS.map((key) =>
    INSURANCE_COMPANIES.find((ins) => ins.value === key)
  ).filter((ins): ins is InsuranceCompany => ins !== undefined);

  // Get all insurers for the expanded list
  const allInsurers = INSURANCE_COMPANIES;

  // Filter insurers by search term (applies to both top and all)
  const filterBySearch = (insurers: InsuranceCompany[]) => {
    if (!searchTerm.trim()) return insurers;
    const term = searchTerm.toLowerCase();
    return insurers.filter(
      (ins) =>
        ins.label.toLowerCase().includes(term) ||
        ins.searchTerms?.some((t) => t.toLowerCase().includes(term))
    );
  };

  const filteredTopInsurers = filterBySearch(topInsurers);
  const filteredAllInsurers = filterBySearch(allInsurers);

  // Group filtered insurers by category for "All insurers" section
  const groupedAllInsurers = INSURANCE_CATEGORIES.map((category) => ({
    category,
    options: filteredAllInsurers.filter((o) => o.category === category),
  })).filter((group) => group.options.length > 0);

  const selectInsurer = (insurerValue: string) => {
    // Toggle: if already selected, deselect
    if (value === insurerValue) {
      onChange("");
      onPlanTypeChange?.("UNKNOWN");
      return;
    }

    // Check if this insurer has regional plans
    if (shouldShowRegionalSelector(insurerValue) && onRegionalTrigger) {
      onRegionalTrigger(insurerValue);
      return;
    }
    onChange(insurerValue);
    setSearchTerm("");

    // Apply smart default for plan type
    const suggestedPlanType = getSuggestedPlanType(insurerValue);
    onPlanTypeChange?.(suggestedPlanType);
  };

  const handleCustomSubmit = () => {
    if (searchTerm.trim()) {
      onChange(searchTerm.trim());
      setSearchTerm("");
      // Custom insurers default to UNKNOWN
      onPlanTypeChange?.("UNKNOWN");
    }
  };

  // Check if search has no results
  const noSearchResults = searchTerm.trim() && filteredAllInsurers.length === 0;

  return (
    <div className="space-y-3">
      {/* Always-visible Search Input */}
      <div className="relative">
        <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          ref={searchInputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && noSearchResults) {
              e.preventDefault();
              handleCustomSubmit();
            }
          }}
          placeholder="Search insurer..."
          className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
        />
      </div>

      {/* Plan Type Chips - shown when insurer is selected */}
      {value && (
        <div className="p-3 bg-slate-50/80 rounded-lg border border-slate-200/60">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-600">
              Plan type{" "}
              <span className="text-slate-400 font-normal">(optional)</span>
            </span>
            <span className="text-xs text-slate-400">
              Helps tailor criteria + appeal language
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PLAN_TYPE_CHIPS.map((chip) => {
              const isSelected = (planType || "UNKNOWN") === chip.value;
              return (
                <button
                  key={chip.value}
                  type="button"
                  onClick={() => {
                    // Single-select: clicking selected chip keeps it (don't toggle to empty)
                    // UNKNOWN serves as the empty state
                    if (!isSelected) {
                      onPlanTypeChange?.(chip.value);
                    }
                  }}
                  className={`
                      px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150
                      ${
                        isSelected
                          ? "bg-teal-500 text-white shadow-sm"
                          : "bg-white text-slate-600 border border-slate-200 hover:border-teal-300 hover:text-teal-700"
                      }
                    `}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Top Insurers Grid */}
      {!searchTerm.trim() && (
        <>
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
              Top Insurers
            </p>
            <div className="grid grid-cols-3 gap-2">
              {topInsurers.map((insurer) => {
                const isSelected = value === insurer.value;
                const hasRegionalPlans = shouldShowRegionalSelector(
                  insurer.value
                );

                return (
                  <button
                    key={insurer.value}
                    type="button"
                    onClick={() => selectInsurer(insurer.value)}
                    className={`
                        min-h-[44px] sm:h-11 px-2 sm:px-3 py-2 sm:py-0 rounded-lg text-xs sm:text-sm font-medium transition-all duration-150
                        border-2 flex items-center justify-center gap-1.5 text-center leading-tight sm:leading-normal
                        ${
                          isSelected
                            ? "bg-teal-500 text-white border-teal-500 shadow-sm"
                            : "bg-white text-slate-700 border-slate-200 hover:border-teal-400 hover:bg-teal-50"
                        }
                      `}
                  >
                    <span className="sm:hidden">
                      {TOP_INSURER_SHORT_LABELS[insurer.value] || insurer.label}
                    </span>
                    <span className="hidden sm:inline">{insurer.label}</span>
                    {hasRegionalPlans && !isSelected && (
                      <IconChevronDown className="w-3.5 h-3.5 opacity-50 flex-shrink-0" />
                    )}
                    {isSelected && (
                      <IconCheck className="w-4 h-4 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Show All Insurers Toggle */}
          <button
            type="button"
            onClick={() => setShowAllInsurers(!showAllInsurers)}
            className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
          >
            <IconChevronDown
              className={`w-4 h-4 transition-transform ${
                showAllInsurers ? "rotate-180" : ""
              }`}
            />
            {showAllInsurers
              ? "Hide all insurers"
              : `All insurers (${allInsurers.length})`}
          </button>
        </>
      )}

      {/* Search Results or All Insurers List */}
      {(searchTerm.trim() || showAllInsurers) && (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          <div className="max-h-64 overflow-y-auto">
            {noSearchResults ? (
              <div className="px-4 py-3">
                <p className="text-sm text-slate-500 mb-2">
                  No insurers match "{searchTerm}"
                </p>
                <button
                  type="button"
                  onClick={handleCustomSubmit}
                  className="text-sm text-teal-600 hover:text-teal-800 font-medium flex items-center gap-1"
                >
                  <IconEdit className="w-3.5 h-3.5" />
                  Add custom insurer: "{searchTerm}"
                </button>
              </div>
            ) : (
              groupedAllInsurers.map((group) => (
                <div key={group.category}>
                  <div className="px-3 py-1.5 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider sticky top-0 border-b border-slate-100">
                    {group.category}
                  </div>
                  <div className="p-2 grid grid-cols-3 gap-1.5">
                    {group.options.map((option) => {
                      const isSelected = value === option.value;
                      const hasRegionalPlans = shouldShowRegionalSelector(
                        option.value
                      );

                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => selectInsurer(option.value)}
                          className={`
                              min-h-[36px] sm:h-9 px-2 py-1.5 sm:py-0 rounded-lg text-xs sm:text-sm transition-all border text-center
                              flex items-center justify-center leading-tight sm:leading-normal
                              ${
                                isSelected
                                  ? "bg-teal-500 text-white border-teal-500"
                                  : "bg-white text-slate-700 border-slate-200 hover:border-teal-400 hover:bg-teal-50"
                              }
                            `}
                        >
                          {option.label}
                          {hasRegionalPlans && !isSelected && (
                            <span className="ml-1 text-xs opacity-50">→</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Specific Request Search Input with autocomplete
function SpecificRequestInput({
  value,
  onChange,
  selectedCategory,
  onCategoryDetected,
  contrastSelection,
  onContrastChange,
}: {
  value: string;
  onChange: (value: string) => void;
  selectedCategory: string;
  onCategoryDetected: (category: string) => void;
  contrastSelection?: string;
  onContrastChange?: (contrast: string) => void;
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CommonRequest | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Check if current value matches a known item with contrast
  const currentItemWithContrast = useMemo(() => {
    if (!value) return null;
    const match = COMMON_REQUESTS.find(
      (item) => item.name === value && item.contrastApplicable
    );
    return match || selectedItem;
  }, [value, selectedItem]);

  const showContrastOptions =
    currentItemWithContrast?.contrastApplicable &&
    selectedCategory === "Imaging";

  // Filter suggestions based on input and selected category
  const suggestions = useMemo(() => {
    const searchTerm = value.toLowerCase().trim();
    if (!searchTerm) return [];

    return COMMON_REQUESTS.filter((item) => {
      // Filter by category if one is selected
      if (selectedCategory && item.category !== selectedCategory) {
        return false;
      }

      // Match by name or aliases
      const nameMatch = item.name.toLowerCase().includes(searchTerm);
      const aliasMatch = item.aliases?.some((alias) =>
        alias.toLowerCase().includes(searchTerm)
      );

      return nameMatch || aliasMatch;
    }).slice(0, 8); // Limit to 8 suggestions
  }, [value, selectedCategory]);

  // Auto-detect category based on input
  useEffect(() => {
    if (!value.trim() || selectedCategory) return;

    const searchTerm = value.toLowerCase().trim();

    // Find matching items
    const matches = COMMON_REQUESTS.filter((item) => {
      const nameMatch = item.name.toLowerCase().includes(searchTerm);
      const aliasMatch = item.aliases?.some((alias) =>
        alias.toLowerCase().includes(searchTerm)
      );
      return nameMatch || aliasMatch;
    });

    // If all matches are the same category, auto-select it
    if (matches.length > 0) {
      const categories = [...new Set(matches.map((m) => m.category))];
      if (categories.length === 1) {
        onCategoryDetected(categories[0]);
      }
    }
  }, [value, selectedCategory, onCategoryDetected]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item: CommonRequest) => {
    onChange(item.name);
    onCategoryDetected(item.category);
    setSelectedItem(item);
    setShowSuggestions(false);
    inputRef.current?.blur();

    // Reset contrast if switching to non-contrast item
    if (!item.contrastApplicable && onContrastChange) {
      onContrastChange("");
    }
  };

  const handleClear = () => {
    onChange("");
    setSelectedItem(null);
    if (onContrastChange) {
      onContrastChange("");
    }
  };

  const contrastOptions = [
    { value: "without", label: "Without contrast" },
    { value: "with", label: "With contrast" },
    { value: "with_and_without", label: "With & without contrast" },
  ];

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setShowSuggestions(true);
            setSelectedItem(null); // Clear selected item when typing
          }}
          onFocus={() => {
            setIsFocused(true);
            if (value.trim()) setShowSuggestions(true);
          }}
          onBlur={() => setIsFocused(false)}
          placeholder="e.g., Humira, MRI Lumbar Spine, TVUS, TKA..."
          className={`w-full border rounded-lg px-4 py-3 text-sm bg-white outline-none transition-all ${
            isFocused
              ? "border-teal-500 ring-2 ring-teal-500/20"
              : "border-slate-200 hover:border-slate-300"
          }`}
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <IconX className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Autocomplete suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden">
          {suggestions.map((item, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSelect(item)}
              className="w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center justify-between group"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-700 group-hover:text-slate-900">
                  {item.name}
                </span>
                {item.contrastApplicable && (
                  <span className="text-[10px] text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">
                    contrast options
                  </span>
                )}
              </div>
              <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                {item.category === "DME (Durable Medical Equipment)"
                  ? "DME"
                  : item.category}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Contrast options for applicable imaging studies */}
      {showContrastOptions && (
        <div className="mt-3">
          <p className="text-xs text-slate-500 mb-2">Contrast:</p>
          <div className="flex flex-wrap gap-2">
            {contrastOptions.map((option) => {
              const isSelected = contrastSelection === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    onContrastChange?.(isSelected ? "" : option.value)
                  }
                  className={`
                      px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-150
                      ${
                        isSelected
                          ? "bg-blue-500 text-white shadow-sm"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }
                    `}
                >
                  {option.label}
                  {isSelected && <IconCheck className="w-3 h-3 ml-1 inline" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Small edit icon for custom input button
function IconEdit({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  );
}

// Dynamic Question Field Component
function DynamicQuestionField({
  question,
  value,
  onChange,
}: {
  question: DynamicQuestion;
  value: string;
  onChange: (value: string) => void;
}) {
  const addSuggestion = (suggestion: string) => {
    const currentValue = value.trim();
    const separator = currentValue ? "; " : "";
    onChange(currentValue + separator + suggestion);
  };

  return (
    <div className="p-4 bg-gradient-to-r from-amber-50/50 to-transparent border border-amber-200/50 rounded-xl">
      <div className="flex items-start gap-2 mb-2">
        <div
          className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
            question.priority === "high"
              ? "bg-rose-100 text-rose-600"
              : "bg-slate-100 text-slate-500"
          }`}
        >
          {question.priority === "high" ? (
            <IconExclamation className="w-3 h-3" />
          ) : (
            <IconLightBulb className="w-3 h-3" />
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                question.priority === "high"
                  ? "bg-rose-100 text-rose-700"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              {question.priority === "high" ? "Important" : question.category}
            </span>
          </div>
          <label className="block text-sm font-medium text-slate-800">
            {question.question}
          </label>
        </div>
      </div>

      {question.type === "textarea" ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 h-20 text-sm bg-white focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none"
        />
      ) : question.type === "select" && question.options ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
        >
          <option value="">Select...</option>
          {question.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={question.type === "number" ? "number" : "text"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
        />
      )}

      {question.suggestions && question.suggestions.length > 0 && (
        <div className="mt-2">
          <div className="flex flex-wrap gap-1.5">
            {question.suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => addSuggestion(suggestion)}
                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-white text-slate-600 hover:bg-teal-50 hover:text-teal-700 border border-slate-200 hover:border-teal-200 transition-all"
              >
                <IconPlus className="w-3 h-3 mr-1 text-slate-400" />
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// STRENGTHEN YOUR CASE - TYPES & LOGIC
// ============================================

interface StrengthenerItem {
  id: string;
  label: string;
  placeholder: string;
  hint: string; // Micro-hint shown under label
  category: "clinical" | "documentation" | "history";
}

interface StrengthenerValue {
  id: string;
  label: string;
  value: string;
}

// Generate dynamic suggestions based on case data
function generateStrengthenerSuggestions(
  requestType: string,
  denialReason: string,
  requestDetails: string
): StrengthenerItem[] {
  const suggestions: StrengthenerItem[] = [];
  const requestLower = requestType.toLowerCase();
  const denialLower = denialReason.toLowerCase();
  const detailsLower = requestDetails.toLowerCase();

  // Request type specific suggestions
  if (
    requestLower.includes("imaging") ||
    detailsLower.includes("mri") ||
    detailsLower.includes("ct") ||
    detailsLower.includes("scan")
  ) {
    suggestions.push({
      id: "exam-findings",
      label: "Recent exam findings",
      placeholder: "e.g. vitals, abnormal exam, functional limits…",
      hint: "Vitals, exam, functional status",
      category: "clinical",
    });
    suggestions.push({
      id: "conservative-duration",
      label: "Conservative care tried",
      placeholder: "e.g. PT x6 weeks, NSAIDs, injections, home exercise…",
      hint: "PT, meds, injections, duration",
      category: "history",
    });
    suggestions.push({
      id: "prior-imaging",
      label: "Prior imaging results",
      placeholder: "e.g. MRI shows L4–L5 stenosis, progression since prior…",
      hint: "MRI/CT/X-ray findings",
      category: "documentation",
    });
    suggestions.push({
      id: "red-flags",
      label: "Red flags present",
      placeholder: "e.g. neuro deficit, fever, weight loss, trauma…",
      hint: "Serious or urgent indicators",
      category: "clinical",
    });
  }

  if (requestLower.includes("medication") || requestLower.includes("drug")) {
    suggestions.push({
      id: "failed-meds",
      label: "Medications already tried",
      placeholder: "e.g. Metformin 1000mg × 6 months, A1c still 8.2%",
      hint: "Prior meds, doses, duration, response",
      category: "history",
    });
    suggestions.push({
      id: "adverse-effects",
      label: "Adverse effects experienced",
      placeholder: "e.g. GI intolerance to metformin, rash with sulfonylurea",
      hint: "Side effects, intolerances",
      category: "history",
    });
    suggestions.push({
      id: "current-metrics",
      label: "Current lab values or scores",
      placeholder: "e.g. A1c 8.5%, eGFR 45, LDL 180",
      hint: "Labs, vitals, objective measures",
      category: "clinical",
    });
    suggestions.push({
      id: "guideline-support",
      label: "Guideline recommendation",
      placeholder: "e.g. ADA recommends GLP-1 as second-line for T2DM with CVD",
      hint: "Society guidelines, FDA indications",
      category: "documentation",
    });
  }

  if (
    requestLower.includes("procedure") ||
    requestLower.includes("surgery") ||
    detailsLower.includes("injection") ||
    detailsLower.includes("arthroplasty") ||
    detailsLower.includes("fusion")
  ) {
    suggestions.push({
      id: "functional-impact",
      label: "Functional limitations",
      placeholder:
        "e.g. Cannot walk > 1 block, unable to work, needs assist for ADLs",
      hint: "Daily activities, work, mobility",
      category: "clinical",
    });
    suggestions.push({
      id: "conservative-failed",
      label: "Conservative treatments failed",
      placeholder: "e.g. PT × 12 weeks, 3 ESIs, NSAIDs, gabapentin",
      hint: "PT, meds, injections tried",
      category: "history",
    });
    suggestions.push({
      id: "objective-scores",
      label: "Objective scores",
      placeholder: "e.g. ODI 48%, VAS 8/10, KOOS pain 35",
      hint: "Validated outcome measures",
      category: "clinical",
    });
    suggestions.push({
      id: "imaging-confirmation",
      label: "Imaging confirming pathology",
      placeholder:
        "e.g. MRI shows large disc herniation at L5-S1 with nerve compression",
      hint: "Imaging findings, structural pathology",
      category: "documentation",
    });
  }

  if (
    requestLower.includes("dme") ||
    detailsLower.includes("cpap") ||
    detailsLower.includes("wheelchair") ||
    detailsLower.includes("oxygen")
  ) {
    suggestions.push({
      id: "functional-need",
      label: "Functional need",
      placeholder: "e.g. Cannot ambulate > 50 feet, requires O2 for exertion",
      hint: "Mobility, respiratory, daily needs",
      category: "clinical",
    });
    suggestions.push({
      id: "test-results",
      label: "Test results",
      placeholder: "e.g. AHI 32, SpO2 drops to 84% on ambulation",
      hint: "Objective testing, studies",
      category: "documentation",
    });
    suggestions.push({
      id: "home-limitations",
      label: "Home limitations",
      placeholder: "e.g. Multi-level home, cannot navigate stairs safely",
      hint: "Home environment, safety concerns",
      category: "clinical",
    });
  }

  // Denial reason specific additions
  if (
    denialLower.includes("not medically necessary") ||
    denialLower.includes("medical necessity")
  ) {
    if (!suggestions.find((s) => s.id === "objective-scores")) {
      suggestions.push({
        id: "severity-evidence",
        label: "Severity documentation",
        placeholder:
          "e.g. Pain score 8/10, PHQ-9 score 18, ejection fraction 30%",
        hint: "Quantifiable severity measures",
        category: "clinical",
      });
    }
    if (!suggestions.find((s) => s.id === "functional-impact")) {
      suggestions.push({
        id: "functional-impact",
        label: "Functional limitations",
        placeholder: "e.g. Unable to work, needs assist for bathing/dressing",
        hint: "Daily activities, work, mobility",
        category: "clinical",
      });
    }
  }

  if (denialLower.includes("step therapy")) {
    if (!suggestions.find((s) => s.id === "failed-meds")) {
      suggestions.push({
        id: "step-therapy-history",
        label: "Step therapy completed",
        placeholder:
          "e.g. Tried Drug A × 8 weeks (ineffective), Drug B × 6 weeks (adverse effect)",
        hint: "Prior drugs tried, outcomes",
        category: "history",
      });
    }
    suggestions.push({
      id: "intolerance-details",
      label: "Intolerance or contraindication",
      placeholder:
        "e.g. Allergic reaction, drug interaction with warfarin, renal contraindication",
      hint: "Allergies, interactions, contraindications",
      category: "history",
    });
  }

  if (
    denialLower.includes("experimental") ||
    denialLower.includes("investigational")
  ) {
    suggestions.push({
      id: "fda-status",
      label: "FDA approval status",
      placeholder: "e.g. FDA approved for this indication in 2022",
      hint: "Approval date, approved indications",
      category: "documentation",
    });
    suggestions.push({
      id: "guideline-support",
      label: "Guideline support",
      placeholder:
        "e.g. Recommended in NCCN guidelines, Class I ACC/AHA recommendation",
      hint: "Society guidelines, evidence level",
      category: "documentation",
    });
  }

  // Referral-specific suggestions
  if (requestLower.includes("referral")) {
    suggestions.push({
      id: "specialist-rationale",
      label: "Specialist rationale",
      placeholder:
        "e.g. Requires subspecialist expertise not available in-network",
      hint: "Why specialist is needed",
      category: "clinical",
    });
    suggestions.push({
      id: "attempted-care",
      label: "Prior care attempted",
      placeholder:
        "e.g. PCP and local specialist unable to manage, needs tertiary center",
      hint: "Care already attempted",
      category: "history",
    });
  }

  // Always add a general "other details" option at the end
  suggestions.push({
    id: "other-details",
    label: "Other relevant details",
    placeholder: "Anything else that strengthens your argument…",
    hint: "Anything supporting medical necessity",
    category: "clinical",
  });

  // Dedupe by id and limit to 6 suggestions
  const seen = new Set<string>();
  return suggestions
    .filter((s) => {
      if (seen.has(s.id)) return false;
      seen.add(s.id);
      return true;
    })
    .slice(0, 6);
}

// ============================================
// STRENGTHEN YOUR CASE - COMPONENT
// ============================================

function StrengthenYourCase({
  suggestions,
  values,
  onChange,
  onSkip,
  onGenerate,
  isSubmitting,
}: {
  suggestions: StrengthenerItem[];
  values: Record<string, string>;
  onChange: (id: string, value: string) => void;
  onSkip: () => void;
  onGenerate: () => void;
  isSubmitting: boolean;
}) {
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());

  const toggleItem = (id: string) => {
    setCheckedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
        // Clear value when unchecked
        onChange(id, "");
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Auto-resize textarea handler
  const handleTextareaInput = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    id: string
  ) => {
    const textarea = e.target;
    onChange(id, textarea.value);

    // Auto-resize: reset height then set to scrollHeight (capped at 5 rows ~120px)
    textarea.style.height = "auto";
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
  };

  const enabledCount = checkedItems.size;
  const filledCount = Array.from(checkedItems).filter((id) =>
    values[id]?.trim()
  ).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-br from-blue-50 to-teal-50 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-teal-500 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">
                Strengthen your case
                <span className="ml-2 text-sm font-normal text-slate-400">
                  (optional)
                </span>
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Add quick details to strengthen your argument on the call. Takes
                ~10–20 seconds.
              </p>
            </div>
          </div>
        </div>

        {/* Suggestions */}
        <div className="px-6 py-4 max-h-[50vh] overflow-y-auto">
          <div className="space-y-2">
            {suggestions.map((item) => {
              const isChecked = checkedItems.has(item.id);
              const hasValue = values[item.id]?.trim();

              return (
                <div key={item.id} className="group">
                  {/* Checkbox row */}
                  <button
                    type="button"
                    onClick={() => toggleItem(item.id)}
                    className={`w-full flex items-start gap-3 p-3 rounded-xl border transition-all text-left ${
                      isChecked
                        ? "bg-teal-50/50 border-teal-200"
                        : "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50/50"
                    }`}
                  >
                    {/* Checkbox */}
                    <span
                      className={`mt-0.5 w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                        isChecked
                          ? "bg-teal-500 border-teal-500"
                          : "border-slate-300 group-hover:border-teal-400"
                      }`}
                    >
                      {isChecked && (
                        <IconCheck className="w-3 h-3 text-white" />
                      )}
                    </span>

                    {/* Label and hint */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-sm font-medium ${
                            isChecked ? "text-teal-800" : "text-slate-700"
                          }`}
                        >
                          {item.label}
                        </span>
                        {hasValue && (
                          <span className="text-xs text-teal-600 bg-teal-100 px-1.5 py-0.5 rounded">
                            ✓ Added
                          </span>
                        )}
                      </div>
                      {/* Micro-hint - hide when expanded to reduce clutter */}
                      {!isChecked && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          {item.hint}
                        </p>
                      )}
                    </div>
                  </button>

                  {/* Expandable input - smooth height transition */}
                  <div
                    className={`overflow-hidden transition-all duration-200 ease-in-out ${
                      isChecked
                        ? "max-h-40 opacity-100 mt-2"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="ml-8">
                      <textarea
                        value={values[item.id] || ""}
                        onChange={(e) => handleTextareaInput(e, item.id)}
                        placeholder={item.placeholder}
                        rows={2}
                        className="w-full text-sm border border-slate-200 bg-slate-50/50 rounded-lg px-3 py-2 
                            focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20 focus:bg-white 
                            outline-none resize-none placeholder:text-slate-400
                            transition-colors duration-150"
                        style={{ minHeight: "52px", maxHeight: "120px" }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <div className="text-sm text-slate-500">
            {enabledCount > 0 ? (
              <span className="text-teal-600">
                <span className="font-medium">{enabledCount}</span> selected
                {filledCount > 0 && (
                  <span className="text-slate-400">
                    {" "}
                    · {filledCount} with details
                  </span>
                )}
              </span>
            ) : (
              <span className="text-slate-400">Check items to add details</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Skip - secondary/ghost style */}
            <button
              type="button"
              onClick={onSkip}
              disabled={isSubmitting}
              className="px-5 py-2.5 text-sm font-medium text-slate-500 bg-white border border-slate-200 
                  hover:text-slate-700 hover:border-slate-300 hover:bg-slate-50 
                  rounded-xl transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <IconLoader className="w-4 h-4 text-slate-400 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <IconSparkles className="w-4 h-4 text-slate-400" />
                  Skip & Generate
                </>
              )}
            </button>
            {/* Add & Generate - primary solid green */}
            <button
              type="button"
              onClick={onGenerate}
              disabled={isSubmitting}
              className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium 
                  rounded-xl hover:from-emerald-600 hover:to-teal-600 
                  shadow-md shadow-emerald-500/25 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <IconLoader className="w-4 h-4" />
                  Generating...
                </>
              ) : (
                <>
                  <IconSparkles className="w-4 h-4" />
                  Add & Generate
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// MEDICATION INPUT COMPONENT (INLINE)
// ============================================

interface MedicationInputInlineProps {
  value: MedicationEntry[];
  onChange: (medications: MedicationEntry[]) => void;
  placeholder?: string;
  maxSelections?: number;
}

function MedicationInputInline({
  value = [],
  onChange,
  placeholder = "Search medications...",
  maxSelections = 15,
}: MedicationInputInlineProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<MedicationMatch[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Search medications as user types
  useEffect(() => {
    if (query.trim().length > 0) {
      const matches = searchMedications(query, 10);
      setResults(matches);
      setIsOpen(true);
      setHighlightedIndex(0);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addMedication = (match: MedicationMatch) => {
    if (value.length >= maxSelections) return;

    const exists = value.some(
      (m) => m.name.toLowerCase() === match.medication.name.toLowerCase()
    );
    if (exists) {
      setQuery("");
      setIsOpen(false);
      return;
    }

    const entry: MedicationEntry = {
      name: match.medication.name,
      displayName: match.medication.generic_name,
      classId: match.medication.class_id,
      className: match.medicationClass?.display_name || null,
      isCustom: false,
    };

    onChange([...value, entry]);
    setQuery("");
    setIsOpen(false);
  };

  const addCustomMedication = () => {
    if (!query.trim() || value.length >= maxSelections) return;

    const exists = value.some(
      (m) => m.name.toLowerCase() === query.toLowerCase()
    );
    if (exists) {
      setQuery("");
      setIsOpen(false);
      return;
    }

    // Try to infer class
    const inferred = inferMedicationClass(query);

    const entry: MedicationEntry = {
      name: query,
      displayName: query,
      classId: inferred?.id || null,
      className: inferred?.display_name || "Other",
      isCustom: true,
    };

    onChange([...value, entry]);
    setQuery("");
    setIsOpen(false);
  };

  const removeMedication = (index: number) => {
    const updated = [...value];
    updated.splice(index, 1);
    onChange(updated);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "Enter" && query.trim() && results.length === 0) {
        e.preventDefault();
        addCustomMedication();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, results.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (results[highlightedIndex]) {
          addMedication(results[highlightedIndex]);
        } else if (query.trim()) {
          addCustomMedication();
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
    }
  };

  return (
    <div className="space-y-2">
      {/* Selected medications */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((med, index) => (
            <span
              key={`${med.name}-${index}`}
              className="inline-flex items-center gap-1 px-2 py-1 bg-teal-50 border border-teal-200 rounded-lg text-sm"
            >
              <span className="text-slate-800">{med.displayName}</span>
              {med.className && (
                <span className="text-xs text-teal-600 bg-teal-100 px-1.5 py-0.5 rounded">
                  {med.className}
                </span>
              )}
              {med.isCustom && (
                <span className="text-xs text-amber-500">*</span>
              )}
              <button
                type="button"
                onClick={() => removeMedication(index)}
                className="ml-1 text-slate-400 hover:text-rose-500 transition-colors"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim() && setIsOpen(true)}
          placeholder={
            value.length >= maxSelections ? "Maximum reached" : placeholder
          }
          disabled={value.length >= maxSelections}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all disabled:bg-slate-100 disabled:cursor-not-allowed"
        />

        {/* Dropdown */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto"
          >
            {results.length > 0 ? (
              <>
                {results.map((match, index) => (
                  <button
                    key={match.medication.name}
                    type="button"
                    onClick={() => addMedication(match)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between ${
                      index === highlightedIndex
                        ? "bg-teal-50"
                        : "hover:bg-slate-50"
                    }`}
                  >
                    <span className="font-medium text-slate-900">
                      {match.medication.generic_name}
                    </span>
                    <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                      {match.medicationClass?.display_name}
                    </span>
                  </button>
                ))}
                {query.trim() && (
                  <button
                    type="button"
                    onClick={addCustomMedication}
                    className="w-full px-3 py-2 text-left text-sm text-teal-600 hover:bg-teal-50 border-t border-slate-100"
                  >
                    + Add "{query}" as custom medication
                  </button>
                )}
              </>
            ) : query.trim() ? (
              <div className="px-3 py-2">
                <p className="text-sm text-slate-500 mb-2">
                  No medications found
                </p>
                <button
                  type="button"
                  onClick={addCustomMedication}
                  className="text-sm text-teal-600 hover:text-teal-800"
                >
                  + Add as custom medication
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function NewCase() {
  const { user } = useUser();
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editCaseId = searchParams.get("edit");
  const modeParam = searchParams.get("mode");
  const priorAuthMode = modeParam === "prior-auth" || modeParam === "appeal";
  const appealMode = modeParam === "appeal";
  const { generateLetter } = usePriorAuthGeneration();
  const { isEssential, checkout } = useSubscription();
  const { count: monthlyLetterCount, refetch: refetchLetterCount } =
    useLetterCount();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // Auth gate: show interstitial modal before redirecting to sign-up
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [redirectToSignIn, setRedirectToSignIn] = useState(false);
  // Guest mode: store generated letter in memory (no database)
  const [guestLetter, setGuestLetter] = useState<{
    clinical_justification: string;
    criteria_citations: string[];
    literature_references: string[];
    risks_of_denial: string[];
    recommended_additions: string[];
    regulatory_leverage?: string[];
  } | null>(null);
  const [guestAssembledLetter, setGuestAssembledLetter] = useState("");
  const [isGuestGenerating, setIsGuestGenerating] = useState(false);
  const [guestCopied, setGuestCopied] = useState(false);
  const [guestGenerationSeconds, setGuestGenerationSeconds] = useState<
    number | null
  >(null);

  // Generate a unique case ID for internal tracking (not displayed prominently)
  // Case display name is computed at submission from request_details + diagnoses
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingCase, setIsLoadingCase] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedConditions, setSelectedConditions] = useState<
    SelectedCondition[]
  >([]);
  const [selectedTreatments, setSelectedTreatments] = useState<
    SelectedTreatment[]
  >([]);
  const [dynamicAnswers, setDynamicAnswers] = useState<Record<string, string>>(
    {}
  );
  const [showTemplates, setShowTemplates] = useState(true);

  // Two-tier form state
  const [showMoreDetails, setShowMoreDetails] = useState(false);

  // Prior medications state (for step therapy documentation)
  const [priorMedications, setPriorMedications] = useState<MedicationEntry[]>(
    []
  );

  // Strengthen Your Case step
  const [showStrengthenStep, setShowStrengthenStep] = useState(false);
  const [strengthenerValues, setStrengthenerValues] = useState<
    Record<string, string>
  >({});

  // Regional plan selector state
  const [showRegionalSelector, setShowRegionalSelector] = useState(false);
  const [pendingRegionalInsurer, setPendingRegionalInsurer] = useState<
    string | null
  >(null);

  // Scheduling state
  const [scheduling, setScheduling] = useState<SchedulingData>({
    scheduledCallAt: null,
    appealDeadline: null,
  });

  // Physician specialties for template ordering
  const [physicianSpecialties, setPhysicianSpecialties] = useState<string[]>(
    []
  );

  // Fetch physician specialties on mount
  useEffect(() => {
    async function loadPhysicianSpecialties() {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from("physician_profiles")
          .select("specialties")
          .eq("user_id", user.id)
          .maybeSingle();

        if (!error && data?.specialties) {
          setPhysicianSpecialties(data.specialties);
        }
      } catch (err) {
        console.error("Error loading physician specialties:", err);
      }
    }

    loadPhysicianSpecialties();
  }, [user]);

  // Reorder templates based on physician's specialties
  const orderedTemplates = useMemo(() => {
    if (physicianSpecialties.length === 0) return CASE_TEMPLATES;

    // Get priority template IDs based on specialties
    const priorityScores: Record<string, number> = {};

    physicianSpecialties.forEach((specialtyId) => {
      const priorities = SPECIALTY_TEMPLATE_PRIORITIES[specialtyId] || [];
      priorities.forEach((templateId, index) => {
        // Higher score for templates that appear earlier and in multiple specialties
        const score = priorities.length - index;
        priorityScores[templateId] = (priorityScores[templateId] || 0) + score;
      });
    });

    // Sort templates: prioritized ones first (by score), then alphabetically
    return [...CASE_TEMPLATES].sort((a, b) => {
      const aScore = priorityScores[a.id] || 0;
      const bScore = priorityScores[b.id] || 0;

      if (aScore !== bScore) {
        return bScore - aScore; // Higher score first
      }
      return a.name.localeCompare(b.name); // Alphabetical fallback
    });
  }, [physicianSpecialties]);

  // Available plan types based on selected insurer
  const [availablePlans, setAvailablePlans] = useState(PLAN_TYPES);

  // Hospital justification factors (for site-of-care arguments)
  const [hospitalJustifications, setHospitalJustifications] = useState<
    string[]
  >([]);
  const [otherJustification, setOtherJustification] = useState("");

  // Site of Service detailed inputs
  const [sosInputs, setSosInputs] = useState<SiteOfServiceInputs>({
    variant: "PROACTIVE",
    rescueWindow: "",
    monitoringPreferred: "",
    monitoringRedirected: "",
    patientVulnerability: "",
    timingSensitivity: "",
    procedureCoupling: "",
    teamProtocol: "",
    priorAttempts: "",
    failureReasons: "",
    elapsedDelay: "",
    clinicalDeterioration: "",
    alternativeSiteCapability: "",
  });

  // Option A: Direct trigger — "Has this procedure been canceled at the approved facility?"
  const [priorCancellation, setPriorCancellation] = useState<
    "yes" | "no" | null
  >(null);

  // Force SoS expansion when salvage is triggered
  const [forceSosExpanded, setForceSosExpanded] = useState(false);

  // Level of Care (LOC) context
  const [locContext, setLocContext] = useState({
    requestedLevelOfCare: "",
    payerSuggestedLevel: "",
    reviewType: "",
    diagnoses: "",
    objectiveFindings: "",
    interventions: "",
    monitoringRequirements: "",
    risksIfDowngraded: "",
    caseSummary: "",
  });

  const updateLocField = (field: string, value: string) => {
    setLocContext((prev) => ({ ...prev, [field]: value }));
  };

  // Denial text analysis state
  const [showDenialAnalysisModal, setShowDenialAnalysisModal] = useState(false);
  const [denialPasteText, setDenialPasteText] = useState("");
  const [denialClassificationResult, setDenialClassificationResult] =
    useState<DenialClassificationResult | null>(null);
  const [showClassificationConfirmation, setShowClassificationConfirmation] =
    useState(false);

  // ============================================
  // PASTE DETECTION + AI FIELD EXTRACTION
  // ============================================
  const [pasteDetected, setPasteDetected] = useState(false);
  const [pasteCharCount, setPasteCharCount] = useState(0);
  const pasteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Denial letter paste for prior auth mode
  const [showPriorAuthDenialModal, setShowPriorAuthDenialModal] =
    useState(false);
  const [priorAuthDenialText, setPriorAuthDenialText] = useState("");
  const [priorAuthDenialResult, setPriorAuthDenialResult] = useState<{
    reason: string;
    confidence: string;
  } | null>(null);
  const [priorAuthDenialAccepted, setPriorAuthDenialAccepted] = useState(false);
  const [isAnalyzingPriorAuthDenial, setIsAnalyzingPriorAuthDenial] =
    useState(false);

  const handleClinicalContextPaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const pasted = e.clipboardData?.getData("text") || "";
      if (pasted.length > 100) {
        setPasteDetected(true);
        setPasteCharCount(pasted.length);
        if (pasteTimerRef.current) clearTimeout(pasteTimerRef.current);
        pasteTimerRef.current = setTimeout(() => setPasteDetected(false), 8000);
      }
    },
    []
  );

  // AI field extraction from clinical context (debounced)
  const handleAnalyzeDenial = () => {
    if (!denialPasteText.trim()) return;

    const result = classifyDenialText(denialPasteText);
    setDenialClassificationResult(result);

    if (
      result.confidence === "low" &&
      result.alternativeReasons &&
      result.alternativeReasons.length > 0
    ) {
      // Low confidence - show options to user
      setShowClassificationConfirmation(true);
    } else {
      // Medium/high confidence - auto-select
      updateField("denialReason", result.reasonKey);
      setShowDenialAnalysisModal(false);
      setShowClassificationConfirmation(true);
      // Keep the pasted text in state in case user wants to change
    }
  };

  const handleSelectClassifiedReason = (reason: string) => {
    updateField("denialReason", reason);
    setShowDenialAnalysisModal(false);
    setShowClassificationConfirmation(true);
  };

  const handleChangeClassification = () => {
    setShowClassificationConfirmation(false);
    setDenialClassificationResult(null);
    // Don't clear denialPasteText - preserve it
  };

  const [formData, setFormData] = useState({
    age: "",
    sex: "",
    requestType: "",
    requestDetails: "",
    facilityType: "", // Where will this be performed
    insuranceCompany: "",
    insuranceCompanyLabel: "", // Store display label for regional plans
    planType: "UNKNOWN", // Default to Unknown
    denialReason: "",
    denialText: "",
    denialDate: "",
    originalSubmissionSummary: "",
    additionalContext: "", // Combined single free-text field
    keyFacts: "", // Physician-verified facts to cite during P2P calls
    medicationName: "",
    medicationDose: "",
    medicationFrequency: "",
    medicationClass: "",
    formularyStatus: "",
    imagingType: "",
    bodyRegion: "",
    contrastRequired: "",
    priorImagingFindings: "",
    procedureName: "",
    cptCode: "",
    procedureUrgency: "",
    surgicalHistory: "",
    referralSpecialty: "",
    referralReason: "",
    admissionType: "",
    lengthOfStay: "",
    dmeCategory: "",
    dmeSpecificItem: "",
    hcpcsCode: "",
  });

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ============================================
  // SESSION STORAGE: Restore form state after auth redirect
  // ============================================
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    const saved = sessionStorage.getItem("peerprep_draft");
    if (!saved) return;

    try {
      const draft = JSON.parse(saved);
      if (draft.formData)
        setFormData((prev) => ({ ...prev, ...draft.formData }));
      if (draft.selectedConditions)
        setSelectedConditions(draft.selectedConditions);
      if (draft.selectedTreatments)
        setSelectedTreatments(draft.selectedTreatments);
      if (draft.priorMedications) setPriorMedications(draft.priorMedications);
      // Clean up after restore
      sessionStorage.removeItem("peerprep_draft");
    } catch (err) {
      console.warn("Failed to restore draft from sessionStorage:", err);
      sessionStorage.removeItem("peerprep_draft");
    }
  }, []);
  const handleAnalyzePriorAuthDenial = useCallback(() => {
    if (!priorAuthDenialText.trim()) return;
    setIsAnalyzingPriorAuthDenial(true);
    setTimeout(() => {
      const lower = priorAuthDenialText.toLowerCase();
      let result = { reason: "Not medically necessary", confidence: "low" };
      if (
        lower.includes("not medically necessary") ||
        lower.includes("medical necessity")
      )
        result = { reason: "Not medically necessary", confidence: "high" };
      else if (lower.includes("step therapy") || lower.includes("tried first"))
        result = { reason: "Step therapy required", confidence: "high" };
      else if (
        lower.includes("experimental") ||
        lower.includes("investigational")
      )
        result = {
          reason: "Experimental / Investigational",
          confidence: "high",
        };
      else if (
        lower.includes("clinical criteria") ||
        lower.includes("does not meet")
      )
        result = {
          reason: "Does not meet clinical criteria",
          confidence: "high",
        };
      else if (lower.includes("site of") || lower.includes("facility"))
        result = { reason: "Site of care denial", confidence: "medium" };
      setPriorAuthDenialResult(result);
      setIsAnalyzingPriorAuthDenial(false);
    }, 700);
  }, [priorAuthDenialText]);

  const handleUpgrade = async (tier: "essential" | "pro") => {
    await checkout(tier);
  };

  // Load existing case data when editing
  useEffect(() => {
    async function loadCaseForEdit() {
      if (!editCaseId || !user) return;

      setIsLoadingCase(true);
      setShowTemplates(false); // Hide templates when editing

      try {
        const { data: caseData, error } = await supabase
          .from("cases")
          .select("*")
          .eq("id", editCaseId)
          .eq("user_id", user.id)
          .single();

        if (error || !caseData) {
          console.error("Error loading case:", error);
          setError("Could not load case for editing");
          return;
        }

        // Note: friendly_name is computed from request_details + diagnoses at save time

        // Parse diagnoses back into selected conditions
        if (caseData.diagnoses) {
          const conditions = caseData.diagnoses.split(",").map((d: string) => ({
            label: d.trim(),
            value: d.trim().toLowerCase().replace(/\s+/g, "_"),
          }));
          setSelectedConditions(conditions);
        }

        // Parse tried_already back into selected treatments
        if (caseData.tried_already) {
          const treatments = caseData.tried_already
            .split(",")
            .map((t: string) => ({
              label: t.trim(),
              value: t.trim().toLowerCase().replace(/\s+/g, "_"),
            }));
          setSelectedTreatments(treatments);
        }

        // Extract request details - need to parse back from formatted string
        let requestDetails = caseData.request_details || "";
        // Try to extract the core request from formatted strings like "Study: MRI Lumbar Spine"
        const studyMatch = requestDetails.match(
          /^(?:Study|Medication|Procedure|Referral|DME):\s*([^;]+)/
        );
        if (studyMatch) {
          requestDetails = studyMatch[1].replace(/\s*\([^)]+\)\s*$/, "").trim(); // Remove contrast info in parens
        }

        // Extract facility info from additional_context if present
        let facilityType = "";
        let restoredJustifications: string[] = [];
        const additionalContext = caseData.additional_context || "";

        const facilityMatch = additionalContext.match(/Facility:\s*([^\n]+)/);
        if (facilityMatch) {
          const facilityLabel = facilityMatch[1].trim();
          const facilityOption = FACILITY_TYPES.find(
            (f) => f.label === facilityLabel
          );
          if (facilityOption) {
            facilityType = facilityOption.value;
          }
        }

        const justificationMatch = additionalContext.match(
          /Hospital justification factors:\s*([^\n]+)/
        );
        if (justificationMatch) {
          const factorLabels = justificationMatch[1]
            .split(";")
            .map((s) => s.trim());
          restoredJustifications = HOSPITAL_JUSTIFICATION_FACTORS.filter((f) =>
            factorLabels.includes(f.label)
          ).map((f) => f.id);
        }

        if (restoredJustifications.length > 0) {
          setHospitalJustifications(restoredJustifications);
        }

        // Restore Site of Service inputs from additional_context
        const restoredSos = parseSoSFromContext(additionalContext);
        if (restoredSos) {
          setSosInputs(restoredSos);
          if (restoredSos.variant === "SALVAGE") {
            setPriorCancellation("yes");
            setForceSosExpanded(true);
          }
        }

        // Set form data
        setFormData({
          age: caseData.age_range || "",
          sex: caseData.sex || "",
          requestType: caseData.request_type || "",
          requestDetails: requestDetails,
          facilityType: facilityType,
          insuranceCompany: caseData.insurance_company || "",
          insuranceCompanyLabel: caseData.insurance_company || "",
          planType: caseData.plan_type || "UNKNOWN",
          denialReason: caseData.denial_reason || "",
          denialText: "",
          denialDate: "",
          originalSubmissionSummary: "",
          additionalContext: caseData.additional_context || "",
          keyFacts: caseData.key_facts || "",
          medicationName: "",
          medicationDose: "",
          medicationFrequency: "",
          medicationClass: "",
          formularyStatus: "",
          imagingType: "",
          bodyRegion: "",
          contrastRequired: "",
          priorImagingFindings: "",
          procedureName: "",
          cptCode: "",
          procedureUrgency: "",
          surgicalHistory: "",
          referralSpecialty: "",
          referralReason: "",
          admissionType: "",
          lengthOfStay: "",
          dmeCategory: "",
          dmeSpecificItem: "",
          hcpcsCode: "",
        });

        // Set scheduling data if present
        if (caseData.scheduled_call_at || caseData.appeal_deadline) {
          setScheduling({
            scheduledCallAt: caseData.scheduled_call_at,
            appealDeadline: caseData.appeal_deadline,
          });
        }

        // Parse prior_medications if present
        if (caseData.prior_medications) {
          try {
            const meds = JSON.parse(caseData.prior_medications);
            setPriorMedications(meds);
          } catch (e) {
            console.error("Failed to parse prior medications:", e);
          }
        }

        // Parse loc_context if present
        if (caseData.loc_context) {
          try {
            const loc = JSON.parse(caseData.loc_context);
            setLocContext(loc);
          } catch (e) {
            console.error("Failed to parse LOC context:", e);
          }
        }

        // Expand details section if there's content
        if (caseData.additional_context || caseData.key_facts) {
          setShowMoreDetails(true);
        }
      } catch (err) {
        console.error("Error loading case:", err);
        setError("Could not load case for editing");
      } finally {
        setIsLoadingCase(false);
      }
    }

    loadCaseForEdit();
  }, [editCaseId, user]);

  // Smart default: Show treatments section if Step Therapy is selected
  const isStepTherapyDenial = formData.denialReason === "Step therapy required";

  // Smart default: Show facility/site-of-care section prominently if Site of Care denial
  const isSiteOfCareDenial =
    formData.denialReason === "Site of care / Facility denial";

  // Smart default: Show LOC context form if Level of Care denial
  const isLevelOfCareDenial = formData.denialReason === "Level of Care";

  // Option B: Keyword detection for failed-access / delay language
  const SALVAGE_KEYWORDS = [
    "cancel",
    "cancelled",
    "canceled",
    "rescheduled",
    "reschedule",
    "postponed",
    "bumped",
    "couldn't get or",
    "couldn't get OR",
    "no or time",
    "no OR time",
    "or unavailable",
    "OR unavailable",
    "surgery delayed",
    "procedure delayed",
    "case delayed",
    "failed to schedule",
    "couldn't schedule",
    "safety hold",
    "infection control hold",
    "case bumped",
    "access failure",
    "couldn't get on the schedule",
  ];

  const salvageKeywordDetected = useMemo(() => {
    if (priorCancellation === "yes") return false; // Already triggered, no need for banner
    if (sosInputs.variant === "SALVAGE") return false; // Already in salvage mode
    const textToScan =
      `${formData.additionalContext} ${formData.keyFacts}`.toLowerCase();
    if (textToScan.trim().length < 10) return false;
    return SALVAGE_KEYWORDS.some((kw) => textToScan.includes(kw.toLowerCase()));
  }, [
    formData.additionalContext,
    formData.keyFacts,
    priorCancellation,
    sosInputs.variant,
  ]);

  // Handler: activate salvage mode
  const activateSalvageMode = useCallback(() => {
    setPriorCancellation("yes");
    setForceSosExpanded(true);
    if (sosInputs.variant !== "SALVAGE") {
      const fresh = createEmptySoSInputs("SALVAGE");
      // Preserve any shared fields already filled
      fresh.rescueWindow = sosInputs.rescueWindow;
      fresh.monitoringPreferred = sosInputs.monitoringPreferred;
      fresh.monitoringRedirected = sosInputs.monitoringRedirected;
      fresh.patientVulnerability = sosInputs.patientVulnerability;
      setSosInputs(fresh);
    }
  }, [sosInputs]);

  // SoS section should show if: site-of-care denial, facility type selected, OR salvage triggered
  const showSosSection =
    isSiteOfCareDenial ||
    !!formData.facilityType ||
    priorCancellation === "yes";

  // Whether the trigger question is relevant (procedure/surgery/admission type)
  const showCancellationQuestion =
    FACILITY_RELEVANT_REQUEST_TYPES.some((type) =>
      formData.requestType.toLowerCase().includes(type.toLowerCase())
    ) && priorCancellation !== "no"; // Hide after explicitly clicking "No"

  // Update available plan types when insurance company changes
  useEffect(() => {
    if (formData.insuranceCompany) {
      const plans = getAvailablePlanTypes(formData.insuranceCompany);
      setAvailablePlans(plans);

      // Clear plan type if current selection is no longer valid
      if (
        formData.planType &&
        !plans.find((p) => p.value === formData.planType)
      ) {
        updateField("planType", "");
      }
    } else {
      setAvailablePlans(PLAN_TYPES);
    }
  }, [formData.insuranceCompany, formData.planType]);

  // Handle regional insurer selection trigger
  const handleRegionalTrigger = (insurerKey: string) => {
    setPendingRegionalInsurer(insurerKey);
    setShowRegionalSelector(true);
  };

  // Handle regional plan selection
  const handleRegionalSelect = (planValue: string, planLabel: string) => {
    setFormData((prev) => ({
      ...prev,
      insuranceCompany: planValue,
      insuranceCompanyLabel: planLabel,
    }));
    setShowRegionalSelector(false);
    setPendingRegionalInsurer(null);
  };

  // Handle regional selector cancel
  const handleRegionalCancel = () => {
    setShowRegionalSelector(false);
    setPendingRegionalInsurer(null);
  };

  // Handle template selection
  const handleSelectTemplate = (template: CaseTemplate) => {
    setFormData((prev) => ({
      ...prev,
      ...template.formData,
    }));

    if (template.suggestedConditions.length > 0) {
      const conditionsToAdd = template.suggestedConditions
        .map((conditionId) => {
          const found = COMORBIDITIES.find((c) => c.value === conditionId);
          if (found) {
            return {
              id: found.value,
              label: found.label,
              type: "common",
            } as SelectedCondition;
          }
          return null;
        })
        .filter((c): c is SelectedCondition => c !== null);

      setSelectedConditions(conditionsToAdd);
    }

    if (template.suggestedTreatments.length > 0) {
      const treatmentsToAdd: SelectedTreatment[] = template.suggestedTreatments
        .map((treatmentId) => {
          const found = TRIED_TREATMENTS.find((t) => t.value === treatmentId);
          if (found) {
            return {
              id: found.value,
              label: found.label,
            };
          }
          return null;
        })
        .filter((t): t is SelectedTreatment => t !== null);

      setSelectedTreatments(treatmentsToAdd);
    }

    setShowTemplates(false);
    // Auto-expand details if template has substantial data
    if (
      template.suggestedConditions.length > 0 ||
      template.suggestedTreatments.length > 0
    ) {
      setShowMoreDetails(true);
    }
  };

  const handleStartFresh = () => {
    setShowTemplates(false);
  };

  const dynamicQuestions = useMemo(() => {
    return generateDynamicQuestions(
      formData,
      selectedConditions,
      selectedTreatments
    );
  }, [formData, selectedConditions, selectedTreatments]);

  const showDynamicQuestions = useMemo(() => {
    return formData.requestType && dynamicQuestions.length > 0;
  }, [formData.requestType, dynamicQuestions]);

  // Appeal-specific dynamic questions
  const appealQuestions = useMemo(() => {
    if (!appealMode || !formData.denialReason) return [];
    return generateAppealQuestions(formData, formData.denialReason);
  }, [appealMode, formData.denialReason, formData]);

  const [appealAnswers, setAppealAnswers] = useState<Record<string, string>>(
    {}
  );

  // Structured rows for formulary alternatives tried
  const [formularyRows, setFormularyRows] = useState<
    { drug: string; dose: string; outcome: string }[]
  >([{ drug: "", dose: "", outcome: "" }]);

  const addFormularyRow = () => {
    setFormularyRows((prev) => [...prev, { drug: "", dose: "", outcome: "" }]);
  };

  const updateFormularyRow = (
    index: number,
    field: "drug" | "dose" | "outcome",
    value: string
  ) => {
    setFormularyRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const removeFormularyRow = (index: number) => {
    setFormularyRows((prev) => prev.filter((_, i) => i !== index));
  };

  // Whether the manual form is expanded in appeal mode (before denial reason is set)
  const [appealFormExpanded, setAppealFormExpanded] = useState(false);

  // Denial text extraction state
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionResult, setExtractionResult] = useState<{
    requested_service: string | null;
    request_category: string | null;
    medication_name: string | null;
    medication_dose: string | null;
    medication_frequency: string | null;
    insurance_company: string | null;
    denial_reason_summary: string;
    insurer_requirements: string[];
    mentioned_alternatives: string[];
    required_step_drugs: string[];
    diagnosis_mentioned: string | null;
  } | null>(null);
  const [showExtractionReview, setShowExtractionReview] = useState(false);

  // Clinical note extraction state
  const [clinicalNoteText, setClinicalNoteText] = useState("");
  const [isExtractingNote, setIsExtractingNote] = useState(false);
  const [noteExtracted, setNoteExtracted] = useState(false);

  // Handle "Analyze & Continue" — scrub, classify, extract, pre-fill
  const handleAnalyzeAndContinue = useCallback(async () => {
    if (!formData.denialText.trim()) return;

    setIsExtracting(true);

    try {
      // 1. Classify denial reason (instant, client-side)
      const classification = classifyDenialText(formData.denialText);

      // 2. Scrub PHI from denial text before sending to extraction endpoint
      let scrubbedText = formData.denialText;
      try {
        const { checkScrubberHealth } = await import("../services/scrubber");
        const healthResult = await checkScrubberHealth();
        const scrubberUp =
          typeof healthResult === "object"
            ? healthResult.healthy
            : !!healthResult;
        if (scrubberUp) {
          scrubbedText = await scrubText(formData.denialText);
        } else if (containsObviousPHI(formData.denialText)) {
          setError(
            "Please remove patient-identifying information from the denial text first."
          );
          setIsExtracting(false);
          return;
        }
      } catch (_e) {
        if (containsObviousPHI(formData.denialText)) {
          setError(
            "Please remove patient-identifying information from the denial text first."
          );
          setIsExtracting(false);
          return;
        }
      }

      // 3. Call denial extraction endpoint
      let extraction = null;
      try {
        const resp = await fetch(
          `${
            import.meta.env.VITE_SUPABASE_URL
          }/functions/v1/extract-denial-fields`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({ scrubbed_denial_text: scrubbedText }),
          }
        );
        if (resp.ok) {
          const data = await resp.json();
          extraction = data.extraction;
        } else {
          console.warn(
            "[Appeal] Extraction endpoint failed, falling back to classification only"
          );
        }
      } catch (extractErr) {
        console.warn("[Appeal] Extraction call failed:", extractErr);
      }

      // 3b. If clinical note provided, extract from it too (in parallel-ish)
      let noteExtraction = null;
      if (clinicalNoteText.trim()) {
        try {
          // Scrub the note
          let scrubbedNote = clinicalNoteText;
          try {
            const { checkScrubberHealth } = await import(
              "../services/scrubber"
            );
            const healthResult = await checkScrubberHealth();
            const scrubberUp =
              typeof healthResult === "object"
                ? healthResult.healthy
                : !!healthResult;
            if (scrubberUp) {
              scrubbedNote = await scrubText(clinicalNoteText);
            }
          } catch (_e) {
            // Continue with unscrubbed if scrubber fails — note extraction is optional
          }

          const noteResp = await fetch(
            `${
              import.meta.env.VITE_SUPABASE_URL
            }/functions/v1/extract-denial-fields`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${
                  import.meta.env.VITE_SUPABASE_ANON_KEY
                }`,
              },
              body: JSON.stringify({
                mode: "clinical_note",
                scrubbed_clinical_note: scrubbedNote,
              }),
            }
          );
          if (noteResp.ok) {
            const noteData = await noteResp.json();
            noteExtraction = noteData.note_extraction;
            console.log("[Appeal] Note extraction result:", noteExtraction);
          } else {
            console.warn(
              "[Appeal] Note extraction HTTP error:",
              noteResp.status
            );
          }
        } catch (noteErr) {
          console.warn("[Appeal] Note extraction failed:", noteErr);
        }
      }

      // 4. Set denial reason from classification
      if (
        classification.reasonKey !== "Other" ||
        classification.confidence !== "low"
      ) {
        updateField("denialReason", classification.reasonKey);
      }

      // 5. Pre-fill form fields from extraction (if available)
      if (extraction) {
        setExtractionResult(extraction);

        if (extraction.requested_service) {
          updateField("requestDetails", extraction.requested_service);
        }
        if (extraction.request_category) {
          updateField("requestType", extraction.request_category);
        }
        if (extraction.medication_name) {
          updateField("medicationName", extraction.medication_name);
        }
        if (extraction.medication_dose) {
          updateField("medicationDose", extraction.medication_dose);
        }
        if (extraction.medication_frequency) {
          updateField("medicationFrequency", extraction.medication_frequency);
        }
        if (extraction.insurance_company) {
          // Try to match to a known insurer value
          const matchedInsurer = INSURANCE_COMPANIES.find(
            (ic) =>
              ic.label
                .toLowerCase()
                .includes(extraction.insurance_company!.toLowerCase()) ||
              ic.searchTerms?.some((t) =>
                extraction
                  .insurance_company!.toLowerCase()
                  .includes(t.toLowerCase())
              )
          );
          if (matchedInsurer) {
            updateField("insuranceCompany", matchedInsurer.value);
            updateField("insuranceCompanyLabel", matchedInsurer.label);
          }
        }

        // Pre-fill step therapy drugs into appeal answers
        if (extraction.required_step_drugs.length > 0) {
          setAppealAnswers((prev) => ({
            ...prev,
            appeal_step_therapy_detail:
              prev.appeal_step_therapy_detail ||
              `Insurer requires: ${extraction.required_step_drugs.join(
                ", "
              )}.\n\nPatient's experience with each:\n`,
          }));
        }

        // Pre-fill formulary rows from mentioned alternatives
        if (extraction.mentioned_alternatives.length > 0) {
          setFormularyRows(
            extraction.mentioned_alternatives.map((alt) => ({
              drug: alt,
              dose: "",
              outcome: "",
            }))
          );
        }

        // Pre-fill diagnosis from denial extraction
        // (will be overwritten by note extraction if available)
        if (extraction.diagnosis_mentioned && selectedConditions.length === 0) {
          setSelectedConditions([
            {
              id: extraction.diagnosis_mentioned
                .toLowerCase()
                .replace(/\s+/g, "_"),
              label: extraction.diagnosis_mentioned,
              type: "common" as const,
            },
          ]);
        }

        // Show extraction review
        setShowExtractionReview(true);
      } else {
        // No extraction — just expand form with classification
        if (
          !classification.reasonKey ||
          (classification.reasonKey === "Other" &&
            classification.confidence === "low")
        ) {
          setAppealFormExpanded(true);
        }
      }

      // 6. Pre-fill from clinical note extraction (if available)
      if (noteExtraction) {
        setNoteExtracted(true);

        // Diagnosis — note extraction has definitive data, always prefer over denial inference
        if (noteExtraction.diagnoses?.length > 0) {
          console.log(
            "[Appeal] Overwriting diagnosis with note data:",
            noteExtraction.diagnoses
          );
          setSelectedConditions(
            noteExtraction.diagnoses.map((dx: string) => ({
              id: dx.toLowerCase().replace(/\s+/g, "_"),
              label: dx,
              type: "common" as const,
            }))
          );
          // Also update extractionResult so the review card shows the right diagnosis
          if (extraction) {
            extraction.diagnosis_mentioned =
              noteExtraction.diagnoses.join(", ");
            setExtractionResult({ ...extraction });
          }
        }

        // Age/sex
        if (noteExtraction.age && !formData.age)
          updateField("age", noteExtraction.age);
        if (noteExtraction.sex && !formData.sex)
          updateField("sex", noteExtraction.sex);

        // Prior treatments → formulary rows + step therapy pills
        if (noteExtraction.prior_treatments?.length > 0) {
          const treatmentRows = noteExtraction.prior_treatments.map(
            (tx: any) => ({
              drug: tx.name || "",
              dose: [tx.dose, tx.duration].filter(Boolean).join(" x ") || "",
              outcome: tx.outcome || "",
            })
          );

          setFormularyRows((prev) => {
            // If denial extraction already set rows with drug names but no outcomes,
            // merge note data into those rows
            const hasUserData = prev.some((r) => r.outcome.trim());
            if (hasUserData) return prev; // Don't overwrite user entries

            // Try to merge: match by drug name
            const merged = prev.map((existingRow) => {
              const match = treatmentRows.find(
                (tr: any) =>
                  tr.drug.toLowerCase() === existingRow.drug.toLowerCase()
              );
              if (match) {
                return {
                  drug: existingRow.drug,
                  dose: existingRow.dose || match.dose,
                  outcome: existingRow.outcome || match.outcome,
                };
              }
              return existingRow;
            });

            // Add any treatments from note not already in rows
            const existingDrugs = new Set(
              merged.map((r) => r.drug.toLowerCase())
            );
            const newRows = treatmentRows.filter(
              (r: any) => r.drug && !existingDrugs.has(r.drug.toLowerCase())
            );

            return [...merged, ...newRows];
          });

          // Also pre-fill step therapy drug inputs
          for (const tx of noteExtraction.prior_treatments) {
            if (tx.name && (tx.outcome || tx.dose)) {
              const answerKey = `step_drug_${tx.name
                .toLowerCase()
                .replace(/\s+/g, "_")}`;
              setAppealAnswers((prev) => {
                if (prev[answerKey]) return prev;
                const parts = [
                  tx.dose,
                  tx.duration ? `x ${tx.duration}` : null,
                  tx.outcome,
                ]
                  .filter(Boolean)
                  .join(" — ");
                return { ...prev, [answerKey]: parts };
              });
            }
          }
        }

        // Exam findings + labs → clinical context
        const contextParts: string[] = [];
        if (noteExtraction.exam_findings?.length > 0) {
          contextParts.push(`Exam: ${noteExtraction.exam_findings.join(", ")}`);
        }
        if (noteExtraction.lab_values?.length > 0) {
          contextParts.push(`Labs: ${noteExtraction.lab_values.join(", ")}`);
        }
        if (contextParts.length > 0 && !formData.additionalContext.trim()) {
          updateField("additionalContext", contextParts.join(". "));
        }
      }
    } catch (err: any) {
      console.error("[Appeal] Analysis failed:", err);
      setError("Analysis failed — you can still fill in the form manually.");
      setAppealFormExpanded(true);
    } finally {
      setIsExtracting(false);
    }
  }, [formData.denialText, clinicalNoteText]);

  // Accept extraction and proceed to full form
  const handleAcceptExtraction = () => {
    setShowExtractionReview(false);
    // Form will auto-expand since denialReason is set
  };

  // Reject extraction and start fresh
  const handleRejectExtraction = () => {
    setShowExtractionReview(false);
    setExtractionResult(null);
    setAppealFormExpanded(true);
  };

  // Clinical note extraction handler (state declared above, before handleAnalyzeAndContinue)
  const handleExtractClinicalNote = useCallback(async () => {
    if (!clinicalNoteText.trim()) return;
    setIsExtractingNote(true);

    try {
      // Scrub PHI
      let scrubbedNote = clinicalNoteText;
      try {
        const { checkScrubberHealth } = await import("../services/scrubber");
        const healthResult = await checkScrubberHealth();
        const scrubberUp =
          typeof healthResult === "object"
            ? healthResult.healthy
            : !!healthResult;
        if (scrubberUp) {
          scrubbedNote = await scrubText(clinicalNoteText);
        } else if (containsObviousPHI(clinicalNoteText)) {
          setError(
            "Please remove patient-identifying information from the note first."
          );
          setIsExtractingNote(false);
          return;
        }
      } catch (_e) {
        if (containsObviousPHI(clinicalNoteText)) {
          setError(
            "Please remove patient-identifying information from the note first."
          );
          setIsExtractingNote(false);
          return;
        }
      }

      // Call extraction endpoint in clinical_note mode
      const resp = await fetch(
        `${
          import.meta.env.VITE_SUPABASE_URL
        }/functions/v1/extract-denial-fields`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            mode: "clinical_note",
            scrubbed_clinical_note: scrubbedNote,
          }),
        }
      );

      if (!resp.ok) {
        console.warn("[Appeal] Note extraction failed");
        setIsExtractingNote(false);
        return;
      }

      const data = await resp.json();
      const note = data.note_extraction;
      if (!note) {
        setIsExtractingNote(false);
        return;
      }

      // Pre-fill diagnosis
      if (note.diagnoses?.length > 0 && selectedConditions.length === 0) {
        setSelectedConditions(
          note.diagnoses.map((dx: string) => ({
            id: dx.toLowerCase().replace(/\s+/g, "_"),
            label: dx,
            type: "common" as const,
          }))
        );
      }

      // Pre-fill age/sex
      if (note.age && !formData.age) updateField("age", note.age);
      if (note.sex && !formData.sex) updateField("sex", note.sex);

      // Pre-fill formulary rows from prior treatments
      if (note.prior_treatments?.length > 0) {
        const treatmentRows = note.prior_treatments.map((tx: any) => ({
          drug: tx.name || "",
          dose: [tx.dose, tx.duration].filter(Boolean).join(" x ") || "",
          outcome: tx.outcome || "",
        }));

        // Merge with existing rows — don't overwrite if user already entered data
        setFormularyRows((prev) => {
          const hasUserData = prev.some(
            (r) => r.drug.trim() || r.outcome.trim()
          );
          if (hasUserData) {
            // Append new treatments that aren't already listed
            const existingDrugs = new Set(
              prev.map((r) => r.drug.toLowerCase())
            );
            const newRows = treatmentRows.filter(
              (r: any) => !existingDrugs.has(r.drug.toLowerCase())
            );
            return [...prev, ...newRows];
          }
          return treatmentRows;
        });

        // Also pre-fill step therapy drug inputs if applicable
        for (const tx of note.prior_treatments) {
          if (tx.name && tx.outcome) {
            const answerKey = `step_drug_${tx.name
              .toLowerCase()
              .replace(/\s+/g, "_")}`;
            setAppealAnswers((prev) => {
              if (prev[answerKey]) return prev; // Don't overwrite
              const parts = [
                tx.dose,
                tx.duration ? `x ${tx.duration}` : null,
                tx.outcome,
              ]
                .filter(Boolean)
                .join(" — ");
              return { ...prev, [answerKey]: parts };
            });
          }
        }
      }

      // Pre-fill clinical context with exam findings and lab values
      const contextParts: string[] = [];
      if (note.exam_findings?.length > 0) {
        contextParts.push(`Exam: ${note.exam_findings.join(", ")}`);
      }
      if (note.lab_values?.length > 0) {
        contextParts.push(`Labs: ${note.lab_values.join(", ")}`);
      }
      if (contextParts.length > 0 && !formData.additionalContext.trim()) {
        updateField("additionalContext", contextParts.join(". "));
      }

      setNoteExtracted(true);
    } catch (err) {
      console.error("[Appeal] Note extraction failed:", err);
    } finally {
      setIsExtractingNote(false);
    }
  }, [
    clinicalNoteText,
    selectedConditions.length,
    formData.age,
    formData.sex,
    formData.additionalContext,
  ]);

  // Generate strengthener suggestions based on form data
  const strengthenerSuggestions = useMemo(() => {
    return generateStrengthenerSuggestions(
      formData.requestType,
      formData.denialReason,
      formData.requestDetails
    );
  }, [formData.requestType, formData.denialReason, formData.requestDetails]);

  // Handle the initial submit button click - show strengthen step first
  const handleInitialSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowStrengthenStep(true);
  };

  // Handle skipping the strengthen step - generate without adding details
  const handleSkipStrengthen = async () => {
    // Clear any partially entered strengtheners and close modal
    setStrengthenerValues({});
    setShowStrengthenStep(false);
    try {
      await performSubmit();
    } catch (err) {
      console.error("Error in handleSkipStrengthen:", err);
      setError("An unexpected error occurred. Please try again.");
      setIsSubmitting(false);
    }
  };

  // Handle generating after strengthen step - include the details
  const handleGenerateAfterStrengthen = async () => {
    setShowStrengthenStep(false);
    try {
      await performSubmit();
    } catch (err) {
      console.error("Error in handleGenerateAfterStrengthen:", err);
      setError("An unexpected error occurred. Please try again.");
      setIsSubmitting(false);
    }
  };

  // Update strengthener value
  const updateStrengthenerValue = (id: string, value: string) => {
    setStrengthenerValues((prev) => ({ ...prev, [id]: value }));
  };

  // Actual submission logic
  const performSubmit = async () => {
    if (!user) {
      // ── GUEST MODE: Generate letter without saving to database ──
      setIsGuestGenerating(true);
      setIsSubmitting(true);
      setError(null);
      setGuestGenerationSeconds(null);
      const guestStartTime = performance.now();

      try {
        // Build the same clinical data we'd send for an authenticated user
        const diagnosesText = selectedConditions.map((c) => c.label).join(", ");
        const triedText = selectedTreatments.map((t) => t.label).join(", ");
        const diagParts = diagnosesText
          .split(/[;,]/)
          .map((s) => s.trim())
          .filter(Boolean);
        const codes: string[] = [];
        const descriptions: string[] = [];
        for (const part of diagParts) {
          const match = part.match(/^([A-Z]\d{2}\.?\d{0,4})\s*[-—:]\s*(.+)/i);
          if (match) {
            codes.push(match[1]);
            descriptions.push(match[2].trim());
          } else if (/^[A-Z]\d{2}/i.test(part)) {
            codes.push(part);
          } else {
            descriptions.push(part);
          }
        }
        const diagnosisCodes = codes.length > 0 ? codes : descriptions;

        let requestDetailsText = formData.requestDetails;
        if (formData.requestType === "Medication") {
          const medDetails = [
            formData.requestDetails && `Medication: ${formData.requestDetails}`,
            !formData.requestDetails &&
              formData.medicationName &&
              `Medication: ${formData.medicationName}`,
            formData.medicationDose && `Dose: ${formData.medicationDose}`,
            formData.medicationFrequency &&
              `Frequency: ${formData.medicationFrequency}`,
          ]
            .filter(Boolean)
            .join("; ");
          requestDetailsText = medDetails || formData.requestDetails;
        }

        // Scrub free-text fields before sending
        let scrubbedRequestDetails = requestDetailsText;
        let scrubbedAdditionalContext = formData.additionalContext || "";
        let scrubbedDenialText = formData.denialText || "";
        try {
          const { checkScrubberHealth } = await import("../services/scrubber");
          const healthResult = await checkScrubberHealth();
          const scrubberAvailable =
            typeof healthResult === "object"
              ? healthResult.healthy
              : !!healthResult;
          if (scrubberAvailable) {
            if (requestDetailsText?.trim())
              scrubbedRequestDetails = await scrubText(requestDetailsText);
            if (formData.additionalContext?.trim())
              scrubbedAdditionalContext = await scrubText(
                formData.additionalContext
              );
            if (formData.denialText?.trim())
              scrubbedDenialText = await scrubText(formData.denialText);
          } else {
            if (
              containsObviousPHI(requestDetailsText) ||
              containsObviousPHI(formData.additionalContext || "") ||
              containsObviousPHI(formData.denialText || "")
            ) {
              setError(
                "Please remove patient-identifying information before generating."
              );
              setIsSubmitting(false);
              setIsGuestGenerating(false);
              return;
            }
          }
        } catch (_e) {
          if (
            containsObviousPHI(requestDetailsText) ||
            containsObviousPHI(formData.additionalContext || "") ||
            containsObviousPHI(formData.denialText || "")
          ) {
            setError(
              "Please remove patient-identifying information before generating."
            );
            setIsSubmitting(false);
            setIsGuestGenerating(false);
            return;
          }
        }

        const insuranceCompanyForStorage =
          formData.insuranceCompanyLabel || formData.insuranceCompany;
        const isAppeal = formData.denialReason ? "appeal" : "initial";

        let medicationName = formData.medicationName;
        if (
          !medicationName &&
          formData.requestType?.toLowerCase() === "medication"
        ) {
          const medMatch = scrubbedRequestDetails?.match(
            /medication:\s*([^;,]+)/i
          );
          if (medMatch) medicationName = medMatch[1].trim();
        }

        const resp = await fetch(
          `${
            import.meta.env.VITE_SUPABASE_URL
          }/functions/v1/generate-prior-auth-letter`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              guest: true,
              diagnosis_codes: diagnosisCodes,
              diagnosis_descriptions: descriptions.length
                ? descriptions
                : undefined,
              procedure_description: scrubbedRequestDetails,
              medication_name: medicationName,
              insurance_company: insuranceCompanyForStorage,
              plan_type:
                formData.planType === "UNKNOWN" ? undefined : formData.planType,
              request_type: isAppeal,
              denial_reason: formData.denialReason || undefined,
              denial_text: scrubbedDenialText || undefined,
              denial_date: formData.denialDate || undefined,
              original_submission_summary:
                formData.originalSubmissionSummary || undefined,
              conservative_treatments: triedText || undefined,
              clinical_notes: scrubbedAdditionalContext || undefined,
            }),
          }
        );

        const data = await resp.json();
        if (!resp.ok) {
          throw new Error(data.error || "Letter generation failed");
        }

        const elapsedSeconds = Math.round(
          (performance.now() - guestStartTime) / 1000
        );
        setGuestGenerationSeconds(elapsedSeconds);

        setGuestLetter(data.clinical_argument);

        // Assemble into a formatted letter using the same template as authenticated users
        try {
          const diagnosesText = selectedConditions
            .map((c) => c.label)
            .join(", ");
          const diagnosisDisplay =
            diagnosesText ||
            (extractionResult?.diagnosis_mentioned
              ? extractionResult.diagnosis_mentioned
              : "[Diagnosis]");
          const insuranceCompanyForDisplay =
            formData.insuranceCompanyLabel || formData.insuranceCompany;
          const templateId = formData.denialReason
            ? "appeal_v1"
            : "standard_v1";

          const slots: LetterSlots = {
            practice_name: "[Practice Name]",
            practice_address: "[Practice Address]",
            practice_phone: "[Phone]",
            practice_fax: "[Fax]",
            physician_name: "[Physician Name]",
            physician_npi: "[NPI]",
            physician_specialty: "[Specialty]",
            patient_name: "[Patient Name]",
            insurance_company: normalizeInsurer(insuranceCompanyForDisplay),
            plan_type:
              formData.planType === "UNKNOWN" ? undefined : formData.planType,
            diagnosis_display: diagnosisDisplay,
            procedure_display: formData.requestDetails,
            denial_reason: formData.denialReason || undefined,
            denial_date: formData.denialDate || undefined,
            clinical_justification:
              data.clinical_argument.clinical_justification,
            criteria_citations: data.clinical_argument.criteria_citations,
            literature_references: data.clinical_argument.literature_references,
            risks_of_denial: data.clinical_argument.risks_of_denial,
            regulatory_leverage:
              data.clinical_argument.regulatory_leverage || [],
            date_generated: new Date().toISOString(),
          };

          setGuestAssembledLetter(assembleLetter(templateId, slots));
        } catch (assembleErr) {
          console.warn(
            "[PriorAuth] Guest letter assembly failed, showing raw:",
            assembleErr
          );
          // guestLetter is already set — modal will fall back to raw display
        }
      } catch (err: any) {
        console.error("[PriorAuth] Guest generation failed:", err);
        setError(err.message || "Failed to generate letter. Please try again.");
      } finally {
        setIsSubmitting(false);
        setIsGuestGenerating(false);
      }
      return;
    }

    // Validate site-of-care denial has facility selected
    if (isSiteOfCareDenial && !formData.facilityType) {
      setError("Please select a facility type for site-of-care denials");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const diagnosesText = selectedConditions.map((c) => c.label).join(", ");

    const triedAlreadyText = selectedTreatments.map((t) => t.label).join(", ");

    let requestDetailsText = formData.requestDetails;

    if (formData.requestType === "Medication") {
      const medDetails = [
        formData.requestDetails && `Medication: ${formData.requestDetails}`,
        !formData.requestDetails &&
          formData.medicationName &&
          `Medication: ${formData.medicationName}`,
        formData.medicationDose && `Dose: ${formData.medicationDose}`,
        formData.medicationFrequency &&
          `Frequency: ${formData.medicationFrequency}`,
        formData.medicationClass && `Class: ${formData.medicationClass}`,
        formData.formularyStatus &&
          `Formulary status: ${
            FORMULARY_STATUS.find((f) => f.value === formData.formularyStatus)
              ?.label || formData.formularyStatus
          }`,
      ]
        .filter(Boolean)
        .join("; ");
      requestDetailsText = medDetails || formData.requestDetails;
    } else if (formData.requestType === "Imaging") {
      // Format contrast display text
      const contrastText = formData.contrastRequired
        ? {
            without: "without contrast",
            with: "with contrast",
            with_and_without: "with and without contrast",
          }[formData.contrastRequired]
        : null;

      const imagingDetails = [
        formData.requestDetails &&
          `Study: ${formData.requestDetails}${
            contrastText ? ` (${contrastText})` : ""
          }`,
        formData.imagingType &&
          !formData.requestDetails &&
          `Study: ${formData.imagingType}`,
        formData.bodyRegion && `Region: ${formData.bodyRegion}`,
        formData.priorImagingFindings &&
          `Prior imaging findings: ${formData.priorImagingFindings}`,
      ]
        .filter(Boolean)
        .join("; ");
      requestDetailsText = imagingDetails || formData.requestDetails;
    } else if (formData.requestType === "Procedure") {
      const procDetails = [
        formData.requestDetails && `Procedure: ${formData.requestDetails}`,
        !formData.requestDetails &&
          formData.procedureName &&
          `Procedure: ${formData.procedureName}`,
        formData.cptCode && `CPT: ${formData.cptCode}`,
        formData.procedureUrgency &&
          `Urgency: ${
            PROCEDURE_URGENCY.find((u) => u.value === formData.procedureUrgency)
              ?.label || formData.procedureUrgency
          }`,
        formData.surgicalHistory &&
          `Relevant surgical history: ${formData.surgicalHistory}`,
      ]
        .filter(Boolean)
        .join("; ");
      requestDetailsText = procDetails || formData.requestDetails;
    } else if (formData.requestType === "Referral") {
      const refDetails = [
        formData.requestDetails && `Referral: ${formData.requestDetails}`,
        !formData.requestDetails &&
          formData.referralSpecialty &&
          `Specialty: ${formData.referralSpecialty}`,
        formData.referralReason && `Reason: ${formData.referralReason}`,
      ]
        .filter(Boolean)
        .join("; ");
      requestDetailsText = refDetails || formData.requestDetails;
    } else if (formData.requestType === "Admission") {
      const admissionTypeLabels: Record<string, string> = {
        inpatient: "Inpatient",
        observation: "Observation",
        icu: "ICU / Critical Care",
        stepdown: "Step-down Unit",
        snf: "Skilled Nursing Facility",
        rehab: "Inpatient Rehabilitation",
        ltac: "Long-Term Acute Care",
        psych: "Psychiatric",
      };
      const admissionDetails = [
        formData.requestDetails && `Admission: ${formData.requestDetails}`,
        formData.admissionType &&
          `Type: ${
            admissionTypeLabels[formData.admissionType] ||
            formData.admissionType
          }`,
        formData.lengthOfStay && `Length of stay: ${formData.lengthOfStay}`,
      ]
        .filter(Boolean)
        .join("; ");
      requestDetailsText = admissionDetails || formData.requestDetails;
    } else if (formData.requestType === "DME (Durable Medical Equipment)") {
      const dmeDetails = [
        formData.requestDetails && `DME: ${formData.requestDetails}`,
        !formData.requestDetails &&
          formData.dmeCategory &&
          `Category: ${formData.dmeCategory}`,
        !formData.requestDetails &&
          formData.dmeSpecificItem &&
          `Item: ${formData.dmeSpecificItem}`,
        formData.hcpcsCode && `HCPCS: ${formData.hcpcsCode}`,
      ]
        .filter(Boolean)
        .join("; ");
      requestDetailsText = dmeDetails || formData.requestDetails;
    }

    const dynamicContext = Object.entries(dynamicAnswers)
      .filter(([_, answer]) => answer.trim())
      .map(([questionId, answer]) => {
        const question = dynamicQuestions.find((q) => q.id === questionId);
        return question ? `${question.question}: ${answer}` : answer;
      })
      .join("\n\n");

    // Build appeal-specific context from appeal questions
    const formularyContext = formularyRows
      .filter((row) => row.drug.trim())
      .map(
        (row) =>
          `Formulary alternative tried — ${row.drug}${
            row.dose ? ` (${row.dose})` : ""
          }: ${row.outcome || "outcome not specified"}`
      )
      .join("\n");

    const appealContext = [
      formularyContext,
      ...Object.entries(appealAnswers)
        .filter(([_, answer]) => answer.trim())
        .map(([questionId, answer]) => {
          // Individual step drug entries (step_drug_metformin, etc.)
          if (questionId.startsWith("step_drug_")) {
            const drugName = questionId
              .replace("step_drug_", "")
              .replace(/_/g, " ");
            return `Step therapy — ${drugName}: ${answer}`;
          }
          const question = appealQuestions.find((q) => q.id === questionId);
          return question ? `${question.question}: ${answer}` : answer;
        }),
    ]
      .filter(Boolean)
      .join("\n\n");

    // Build strengtheners context
    const strengthenersContext = Object.entries(strengthenerValues)
      .filter(([_, value]) => value.trim())
      .map(([id, value]) => {
        const suggestion = strengthenerSuggestions.find((s) => s.id === id);
        return suggestion ? `${suggestion.label}: ${value}` : value;
      })
      .join("\n");

    const strengthenersSection = strengthenersContext
      ? `Additional case details provided by physician:\n${strengthenersContext}`
      : "";

    // Build facility context for site-of-care justification
    let facilitySection = "";
    if (formData.facilityType) {
      const facilityLabel =
        FACILITY_TYPES.find((f) => f.value === formData.facilityType)?.label ||
        formData.facilityType;
      facilitySection = `Facility: ${facilityLabel}`;

      if (
        formData.facilityType === "hospital" &&
        hospitalJustifications.length > 0
      ) {
        const justificationLabels = hospitalJustifications
          .map((id) => {
            if (id === "other" && otherJustification) {
              return otherJustification;
            }
            return HOSPITAL_JUSTIFICATION_FACTORS.find((f) => f.id === id)
              ?.label;
          })
          .filter(Boolean);
        facilitySection += `\nHospital justification factors: ${justificationLabels.join(
          "; "
        )}`;
      }
    }

    // Build Site of Service context (detailed clinical inputs)
    const sosSection = serializeSoSInputs(sosInputs);

    // Build medication context for AI (class-based reasoning)
    let medicationSection = "";
    if (priorMedications.length > 0) {
      const medSummaries = analyzeMedications(
        priorMedications.map((m) => m.name)
      );
      medicationSection = `Prior medications tried (by therapeutic class):\n${medSummaries
        .map(
          (s) => `- ${s.class_name} (${s.count}): ${s.medications.join(", ")}`
        )
        .join("\n")}`;
    }

    // Build Level of Care context for AI
    let locSection = "";
    if (
      isLevelOfCareDenial &&
      (locContext.requestedLevelOfCare || locContext.diagnoses)
    ) {
      const locParts: string[] = [];

      if (locContext.reviewType) {
        const reviewLabel = LOC_REVIEW_TYPES.find(
          (t) => t.value === locContext.reviewType
        )?.label;
        locParts.push(`Review Type: ${reviewLabel || locContext.reviewType}`);
      }
      if (locContext.requestedLevelOfCare) {
        const levelLabel = LOC_LEVELS.find(
          (l) => l.value === locContext.requestedLevelOfCare
        )?.label;
        locParts.push(
          `Requested Level of Care: ${
            levelLabel || locContext.requestedLevelOfCare
          }`
        );
      }
      if (locContext.payerSuggestedLevel) {
        const payerLabel = LOC_LEVELS.find(
          (l) => l.value === locContext.payerSuggestedLevel
        )?.label;
        locParts.push(
          `Payer Suggested Level: ${
            payerLabel || locContext.payerSuggestedLevel
          }`
        );
      }
      if (locContext.diagnoses) {
        locParts.push(`Primary Diagnoses: ${locContext.diagnoses}`);
      }
      if (locContext.objectiveFindings) {
        locParts.push(
          `Objective Findings/Vitals: ${locContext.objectiveFindings}`
        );
      }
      if (locContext.interventions) {
        locParts.push(`Current Interventions: ${locContext.interventions}`);
      }
      if (locContext.monitoringRequirements) {
        locParts.push(
          `Monitoring Requirements: ${locContext.monitoringRequirements}`
        );
      }
      if (locContext.risksIfDowngraded) {
        locParts.push(`Risks if Downgraded: ${locContext.risksIfDowngraded}`);
      }
      if (locContext.caseSummary) {
        locParts.push(`Case Summary: ${locContext.caseSummary}`);
      }

      locSection = `LEVEL OF CARE JUSTIFICATION:\n${locParts.join("\n")}`;
    }

    const fullAdditionalContext = [
      formData.additionalContext,
      dynamicContext,
      appealContext,
      strengthenersSection,
      facilitySection,
      sosSection,
      medicationSection,
      locSection,
    ]
      .filter(Boolean)
      .join("\n\n---\n\n");

    // ============================================
    // PHI SCRUBBING — scrub all free-text before storage/LLM
    // ============================================
    const fieldsToScrub: Array<{ label: string; value: string }> = [
      { label: "Request Details", value: requestDetailsText },
      { label: "Clinical Context (full)", value: fullAdditionalContext },
      { label: "Key Facts", value: formData.keyFacts },
      { label: "Denial Text", value: formData.denialText },
    ];

    const scrubLog: Array<{
      label: string;
      original: string;
      scrubbed: string;
      entities: number;
      entityCounts: Record<string, number>;
      duration_ms: number;
      blocked: boolean;
      skipped: boolean;
    }> = [];
    let scrubbedRequestDetails = requestDetailsText;
    let scrubbedAdditionalContext = fullAdditionalContext;
    let scrubbedKeyFacts = formData.keyFacts;
    let scrubbedClinicalContext = formData.additionalContext;
    let scrubbedDenialText = formData.denialText;
    let scrubBlocked = false;

    // Check if scrubber is available before attempting
    let scrubberAvailable = false;
    try {
      const { checkScrubberHealth } = await import("../services/scrubber");
      const healthResult = await checkScrubberHealth();
      scrubberAvailable =
        typeof healthResult === "object"
          ? healthResult.healthy
          : !!healthResult;
    } catch (_e) {
      console.warn(
        "[Scrubber] Could not check health — scrubber module may not be available"
      );
    }

    if (scrubberAvailable) {
      // Scrubber is up — scrub all fields
      for (const field of fieldsToScrub) {
        if (!field.value?.trim()) {
          scrubLog.push({
            label: field.label,
            original: "",
            scrubbed: "",
            entities: 0,
            entityCounts: {},
            duration_ms: 0,
            blocked: false,
            skipped: true,
          });
          continue;
        }
        const start = performance.now();
        try {
          const scrubbedResult = await scrubText(field.value);
          // scrubText returns a plain string; derive entity counts by scanning placeholders
          const placeholderMatches =
            scrubbedResult.match(/\[\[[A-Z_]+\]\]/g) || [];
          const entityCounts: Record<string, number> = {};
          for (const match of placeholderMatches) {
            entityCounts[match] = (entityCounts[match] || 0) + 1;
          }
          const entityTotal = placeholderMatches.length;
          scrubLog.push({
            label: field.label,
            original: field.value,
            scrubbed: scrubbedResult,
            entities: entityTotal,
            entityCounts,
            duration_ms: Math.round(performance.now() - start),
            blocked: false,
            skipped: false,
          });
          if (field.label === "Request Details")
            scrubbedRequestDetails = scrubbedResult;
          if (field.label === "Clinical Context (full)")
            scrubbedAdditionalContext = scrubbedResult;
          if (field.label === "Key Facts") scrubbedKeyFacts = scrubbedResult;
          if (field.label === "Denial Text")
            scrubbedDenialText = scrubbedResult;
        } catch (e) {
          const hasObviousPHI = containsObviousPHI(field.value);
          scrubLog.push({
            label: field.label,
            original: field.value,
            scrubbed: hasObviousPHI ? "[BLOCKED]" : field.value,
            entities: 0,
            entityCounts: {},
            duration_ms: Math.round(performance.now() - start),
            blocked: hasObviousPHI,
            skipped: false,
          });
          if (hasObviousPHI) scrubBlocked = true;
        }
      }

      // Also scrub raw clinical context for generateLetter
      if (formData.additionalContext?.trim()) {
        try {
          scrubbedClinicalContext = await scrubText(formData.additionalContext);
        } catch (e) {
          if (containsObviousPHI(formData.additionalContext))
            scrubBlocked = true;
        }
      }
    } else {
      // Scrubber is NOT available — check for obvious PHI as safety net
      console.warn(
        "[Scrubber] Service unavailable — using local PHI pattern check only"
      );
      for (const field of fieldsToScrub) {
        if (!field.value?.trim()) continue;
        const hasObviousPHI = containsObviousPHI(field.value);
        scrubLog.push({
          label: field.label,
          original: field.value,
          scrubbed: hasObviousPHI
            ? "[WOULD BLOCK]"
            : "(passed through — scrubber offline)",
          entities: 0,
          entityCounts: {},
          duration_ms: 0,
          blocked: hasObviousPHI,
          skipped: false,
        });
        if (hasObviousPHI) scrubBlocked = true;
      }
    }

    // DEV: Log scrub results to console for verification
    if (import.meta.env.DEV) {
      console.group(
        `%c🛡 PHI Scrub Audit (submit) — scrubber ${
          scrubberAvailable ? "ONLINE" : "OFFLINE"
        }`,
        "font-weight:bold; color:#059669"
      );
      for (const entry of scrubLog) {
        if (!entry.original) continue;
        const modified = entry.original !== entry.scrubbed;
        console.group(
          `%c${entry.label}%c ${
            entry.blocked
              ? "⛔ BLOCKED"
              : modified
              ? `→ ${entry.entities} entities`
              : "✓ clean"
          } %c(${entry.duration_ms}ms)`,
          "font-weight:bold",
          entry.blocked
            ? "color:#dc2626; font-weight:bold"
            : modified
            ? "color:#2563eb; font-weight:bold"
            : "color:#16a34a",
          "color:#9ca3af"
        );
        if (modified && !entry.blocked) {
          console.log(
            "%cBEFORE:",
            "color:#dc2626; font-weight:bold",
            entry.original
          );
          console.log(
            "%cAFTER:",
            "color:#2563eb; font-weight:bold",
            entry.scrubbed
          );
          if (Object.keys(entry.entityCounts).length > 0)
            console.table(entry.entityCounts);
        } else {
          console.log(
            "Text:",
            entry.original.slice(0, 200) +
              (entry.original.length > 200 ? "…" : "")
          );
        }
        console.groupEnd();
      }
      console.groupEnd();
    }

    // SAFETY: Block submission if obvious PHI detected and scrubber couldn't clean it
    if (scrubBlocked) {
      setError(
        "Submission blocked: patient identifiers detected but the PHI scrubber is unavailable. Please remove PHI manually or try again shortly."
      );
      setIsSubmitting(false);
      return;
    }

    const ageValue = formData.age ? parseInt(formData.age) : null;
    const normalizedAge =
      ageValue !== null ? (ageValue >= 90 ? "90+" : formData.age) : "";

    // Use label for display, value for storage
    const insuranceCompanyForStorage =
      formData.insuranceCompanyLabel || formData.insuranceCompany;

    const casePayload = {
      user_id: user.id,
      friendly_name: generateCaseDisplayName({
        request_details: scrubbedRequestDetails,
        diagnoses: diagnosesText,
      }),
      age_range: normalizedAge,
      sex: formData.sex,
      diagnoses: diagnosesText,
      request_type: formData.requestType,
      request_details: scrubbedRequestDetails,
      insurance_company: insuranceCompanyForStorage,
      plan_type: formData.planType === "UNKNOWN" ? "" : formData.planType,
      denial_reason: formData.denialReason,
      denial_details: "", // No longer separate field
      tried_already: triedAlreadyText,
      additional_context: scrubbedAdditionalContext,
      key_facts: scrubbedKeyFacts,
      scheduled_call_at: scheduling.scheduledCallAt,
      appeal_deadline: scheduling.appealDeadline,
      prior_medications:
        priorMedications.length > 0 ? JSON.stringify(priorMedications) : null,
      // Note: loc_context would go here once the column is added to the database
    };

    console.log("Submitting case payload:", casePayload);

    let data;
    let saveError;

    if (editCaseId) {
      // Update existing case
      const result = await supabase
        .from("cases")
        .update(casePayload)
        .eq("id", editCaseId)
        .eq("user_id", user.id)
        .select()
        .single();

      data = result.data;
      saveError = result.error;
    } else {
      // Insert new case
      const result = await supabase
        .from("cases")
        .insert(casePayload)
        .select()
        .single();

      data = result.data;
      saveError = result.error;
    }

    setIsSubmitting(false);

    if (saveError) {
      console.error("Error saving case:", saveError);
      console.error("Error details:", JSON.stringify(saveError, null, 2));
      console.error("Error message:", saveError.message);
      console.error("Error code:", saveError.code);
      setError(
        `Failed to save case: ${saveError.message || "Please try again."}`
      );
      return;
    }

    if (!data?.id) {
      console.error("No case ID returned after save");
      setError("Failed to save case. Please try again.");
      return;
    }

    // Gate: free tier limited to 2 PA letters/month
    if (priorAuthMode && !editCaseId && !isEssential) {
      if (monthlyLetterCount >= 2) {
        setShowUpgradeModal(true);
        setIsSubmitting(false);
        return;
      }
    }

    // If prior-auth mode, kick off letter generation in background (don't await)
    if (priorAuthMode) {
      const diagnosesText = selectedConditions.map((c) => c.label).join(", ");
      const triedText = selectedTreatments.map((t) => t.label).join(", ");

      // Fire and forget — CaseView/PriorAuthLetterPanel will poll for the result
      generateLetter({
        caseId: data.id,
        userId: user.id,
        diagnosesText,
        requestType: formData.requestType,
        requestDetails: scrubbedRequestDetails,
        insuranceCompany: insuranceCompanyForStorage,
        planType:
          formData.planType === "UNKNOWN" ? undefined : formData.planType,
        denialReason: formData.denialReason || undefined,
        denialText: scrubbedDenialText || undefined,
        denialDate: formData.denialDate || undefined,
        originalSubmissionSummary:
          formData.originalSubmissionSummary || undefined,
        conservativeTreatments: triedText || undefined,
        additionalContext: scrubbedClinicalContext || undefined,
        medicationName: formData.medicationName || undefined,
      }).catch((err) => {
        console.warn("[PriorAuth] Background letter generation failed:", err);
      });
    }

    // Log PA letter generation for free tier tracking
    supabase
      .from("letter_generations")
      .insert({
        clerk_user_id: user.id,
      })
      .then(() => refetchLetterCount());

    navigate(`/case/${data.id}`, {
      state: { letterGenerating: !!priorAuthMode },
    });
  };

  const ageNumber = formData.age ? parseInt(formData.age) : null;
  const showAgeWarning = ageNumber !== null && ageNumber >= 90;

  // Get display value for insurance field
  const insuranceDisplayValue =
    formData.insuranceCompanyLabel || formData.insuranceCompany;

  // Dynamic case title: procedure-first with one contextual clue
  const dynamicCaseTitle = useMemo(() => {
    if (!formData.requestDetails.trim()) {
      return editCaseId
        ? "Edit Case"
        : appealMode
        ? "Appeal Letter"
        : priorAuthMode
        ? "Prior Auth Letter"
        : "New Case";
    }

    // Build the primary title: request + category
    const requestText = formData.requestDetails.trim();
    const categoryText = formData.requestType
      ? ` — ${formData.requestType}`
      : "";
    let baseTitle = `${requestText}${categoryText}`;

    // Add one contextual clue (priority: denial reason > facility > insurer)
    let clue = "";
    if (formData.denialReason) {
      // Shorten some denial reasons for display
      const shortDenial = formData.denialReason
        .replace("Site of care / Facility denial", "Site of care")
        .replace("Does not meet clinical criteria", "Clinical criteria")
        .replace("Not medically necessary", "Medical necessity")
        .replace("Experimental/investigational", "Experimental");
      clue = shortDenial;
    } else if (formData.facilityType) {
      const facilityLabel = FACILITY_TYPES.find(
        (f) => f.value === formData.facilityType
      )?.label;
      if (facilityLabel) clue = facilityLabel;
    } else if (insuranceDisplayValue) {
      // Get short insurer name
      const insurer = INSURANCE_COMPANIES.find(
        (i) => i.value === formData.insuranceCompany
      );
      clue = insurer?.label || insuranceDisplayValue;
    }

    if (clue) {
      baseTitle += ` (${clue})`;
    }

    return baseTitle;
  }, [
    formData.requestDetails,
    formData.requestType,
    formData.denialReason,
    formData.facilityType,
    formData.insuranceCompany,
    insuranceDisplayValue,
    editCaseId,
    priorAuthMode,
  ]);

  // DEV: Build scrub audit fields
  const scrubAuditFields = buildAuditFields(
    formData as unknown as Record<string, string>,
    {
      dynamicAnswers,
      strengthenerValues,
      denialPasteText: priorAuthDenialText || denialPasteText,
    }
  );

  // If user clicked "Generate" while unauthenticated, redirect to Clerk sign-up.
  // After sign-up, Clerk redirects to /new?mode=prior-auth (or appeal) where draft restores.
  if (redirectToSignIn) {
    const redirectMode = appealMode ? "appeal" : "prior-auth";
    return (
      <RedirectToSignIn
        afterSignInUrl={`/new?mode=${redirectMode}`}
        afterSignUpUrl={`/new?mode=${redirectMode}`}
      />
    );
  }

  return (
    <div
      className={
        priorAuthMode
          ? "min-h-screen"
          : "min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30"
      }
      style={
        priorAuthMode
          ? {
              background: "#fafaf9",
              fontFamily: "'DM Sans', system-ui, sans-serif",
            }
          : undefined
      }
    >
      {/* Font imports for premium prior auth mode */}
      {priorAuthMode && (
        <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,400;6..72,500;6..72,600;6..72,700&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Mono:wght@400;500&display=swap');
          `}</style>
      )}
      {/* Regional Plan Selector Modal */}
      {showRegionalSelector && pendingRegionalInsurer && (
        <RegionalPlanSelector
          parentInsurer={pendingRegionalInsurer}
          onSelect={handleRegionalSelect}
          onCancel={handleRegionalCancel}
        />
      )}

      {/* Strengthen Your Case Modal */}
      {showStrengthenStep && (
        <StrengthenYourCase
          suggestions={strengthenerSuggestions}
          values={strengthenerValues}
          onChange={updateStrengthenerValue}
          onSkip={handleSkipStrengthen}
          onGenerate={handleGenerateAfterStrengthen}
          isSubmitting={isSubmitting}
        />
      )}

      <header className="bg-white/80 border-b border-slate-200 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-lg font-semibold text-slate-900">
              <span className="text-teal-500">Peer</span>Prep
            </Link>
            <span className="text-slate-300">/</span>
            <span className="text-sm text-slate-500">
              {editCaseId
                ? "Edit Case"
                : appealMode
                ? "Appeal Letter"
                : priorAuthMode
                ? "Prior Auth Letter"
                : "New Case"}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Appeal / PA Mode Toggle */}
        {priorAuthMode && !editCaseId && (
          <div className="mb-6">
            <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl w-fit">
              <button
                type="button"
                onClick={() => {
                  if (appealMode) {
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set("mode", "prior-auth");
                    window.history.replaceState(
                      {},
                      "",
                      `?${newParams.toString()}`
                    );
                    updateField("denialText", "");
                    updateField("denialDate", "");
                    updateField("originalSubmissionSummary", "");
                    setAppealAnswers({});
                    setAppealFormExpanded(false);
                    setExtractionResult(null);
                    setShowExtractionReview(false);
                    window.location.search = newParams.toString();
                  }
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  !appealMode
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                New PA Letter
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!appealMode) {
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set("mode", "appeal");
                    window.history.replaceState(
                      {},
                      "",
                      `?${newParams.toString()}`
                    );
                    window.location.search = newParams.toString();
                  }
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  appealMode
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Appeal a Denial
              </button>
            </div>

            {/* Appeal mode: unified entry card — paste denial + optional note */}
            {appealMode && !formData.denialReason && !appealFormExpanded && (
              <div className="mt-5 p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-1">
                  <IconSparkles className="w-5 h-5 text-teal-500" />
                  <h3 className="text-base font-semibold text-slate-800">
                    Start your appeal
                  </h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                  Paste your denial letter and we'll identify the reason and
                  tailor your rebuttal.
                </p>
                <div className="px-3 py-1.5 rounded-lg bg-emerald-50/60 border border-emerald-200/40 flex items-center gap-2 mb-3">
                  <IconShield className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  <p className="text-xs text-emerald-700">
                    PHI will be automatically scrubbed before processing
                  </p>
                </div>

                {/* Denial letter paste */}
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    Denial Letter
                  </label>
                  <textarea
                    value={formData.denialText}
                    onChange={(e) => updateField("denialText", e.target.value)}
                    placeholder="Paste the denial letter, relevant excerpts, or key denial language here..."
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 h-32 text-sm bg-slate-50/50 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none"
                  />
                </div>

                {/* Clinical note paste — optional */}
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    Clinical Note
                    <span className="font-normal normal-case tracking-normal text-slate-400 ml-1">
                      (optional — we'll extract diagnosis, treatments, and
                      findings)
                    </span>
                  </label>
                  <textarea
                    value={clinicalNoteText}
                    onChange={(e) => setClinicalNoteText(e.target.value)}
                    placeholder="Paste a recent office note, progress note, or clinical summary..."
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 h-28 text-sm bg-slate-50/50 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none"
                  />
                </div>

                {formData.denialText.length > 0 && (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      disabled={isExtracting || isExtractingNote}
                      onClick={handleAnalyzeAndContinue}
                      className="px-4 py-2 text-sm font-semibold bg-teal-500 text-white rounded-lg hover:bg-teal-600 shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isExtracting || isExtractingNote ? (
                        <>
                          <IconLoader className="w-4 h-4" />
                          Analyzing...
                        </>
                      ) : (
                        "Analyze & Continue →"
                      )}
                    </button>
                    <span className="text-[11px] text-slate-400 font-mono">
                      {formData.denialText.length} chars
                      {clinicalNoteText.length > 0 &&
                        ` + ${clinicalNoteText.length} note`}
                    </span>
                  </div>
                )}

                {/* Divider + manual fallback */}
                <div className="flex items-center gap-3 mt-5 mb-1">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400">or</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <button
                  type="button"
                  onClick={() => setAppealFormExpanded(true)}
                  className="w-full mt-2 px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all text-center"
                >
                  Select a denial reason manually ↓
                </button>
              </div>
            )}
          </div>
        )}

        {/* Extraction review — compact confirmation of what was extracted */}
        {appealMode && showExtractionReview && extractionResult && (
          <div className="max-w-3xl mx-auto px-6 mb-6">
            <div className="p-5 bg-white rounded-xl border border-teal-200 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <IconCheck className="w-5 h-5 text-teal-500" />
                <h3 className="text-base font-semibold text-slate-800">
                  Here's what we found
                </h3>
              </div>

              <div className="space-y-3 mb-5">
                {extractionResult.requested_service && (
                  <div className="flex items-start gap-3">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider w-28 flex-shrink-0 pt-0.5">
                      Service
                    </span>
                    <span className="text-sm text-slate-800 font-medium">
                      {extractionResult.requested_service}
                    </span>
                  </div>
                )}
                {formData.denialReason && (
                  <div className="flex items-start gap-3">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider w-28 flex-shrink-0 pt-0.5">
                      Denial
                    </span>
                    <span className="text-sm">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-teal-50 text-teal-700 font-medium text-xs">
                        {formData.denialReason}
                      </span>
                    </span>
                  </div>
                )}
                {extractionResult.denial_reason_summary && (
                  <div className="flex items-start gap-3">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider w-28 flex-shrink-0 pt-0.5">
                      Summary
                    </span>
                    <span className="text-sm text-slate-600">
                      {extractionResult.denial_reason_summary}
                    </span>
                  </div>
                )}
                {extractionResult.insurance_company && (
                  <div className="flex items-start gap-3">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider w-28 flex-shrink-0 pt-0.5">
                      Insurer
                    </span>
                    <span className="text-sm text-slate-800">
                      {extractionResult.insurance_company}
                    </span>
                  </div>
                )}
                {extractionResult.diagnosis_mentioned && (
                  <div className="flex items-start gap-3">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider w-28 flex-shrink-0 pt-0.5">
                      Diagnosis
                    </span>
                    <span className="text-sm text-slate-800">
                      {extractionResult.diagnosis_mentioned}
                    </span>
                  </div>
                )}
                {extractionResult.required_step_drugs.length > 0 && (
                  <div className="flex items-start gap-3">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider w-28 flex-shrink-0 pt-0.5">
                      Required
                    </span>
                    <span className="text-sm text-slate-600">
                      Insurer requires trial of:{" "}
                      {extractionResult.required_step_drugs.join(", ")}
                    </span>
                  </div>
                )}
                {extractionResult.insurer_requirements.length > 0 &&
                  extractionResult.required_step_drugs.length === 0 && (
                    <div className="flex items-start gap-3">
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider w-28 flex-shrink-0 pt-0.5">
                        Required
                      </span>
                      <span className="text-sm text-slate-600">
                        {extractionResult.insurer_requirements.join("; ")}
                      </span>
                    </div>
                  )}
              </div>

              <p className="text-xs text-slate-500 mb-4">
                We've pre-filled the form below. Review and add any details we
                missed — especially your patient's specific clinical history.
              </p>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleAcceptExtraction}
                  className="px-4 py-2 text-sm font-semibold bg-teal-500 text-white rounded-lg hover:bg-teal-600 shadow-sm transition-all"
                >
                  Looks good — continue ↓
                </button>
                <button
                  type="button"
                  onClick={handleRejectExtraction}
                  className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition-all"
                >
                  Start over
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading state for edit mode */}
        {isLoadingCase ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
            <span className="ml-3 text-slate-600">Loading case...</span>
          </div>
        ) : (
          <form onSubmit={handleInitialSubmit}>
            {/* Case Header + PHI Banner (hidden when appeal entry card is showing) */}
            {!(appealMode && !formData.denialReason && !appealFormExpanded) && (
              <>
                <div className="mb-6 relative">
                  {!priorAuthMode && (
                    <div className="absolute inset-0 bg-gradient-to-r from-teal-500/10 via-teal-500/5 to-transparent rounded-2xl -z-10" />
                  )}
                  <div className={priorAuthMode ? "pb-2" : "p-6"}>
                    {priorAuthMode ? (
                      <>
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-500/10 mb-3">
                          <IconSparkles className="w-3.5 h-3.5 text-teal-600" />
                          <span className="text-xs font-semibold text-teal-600 tracking-wide">
                            {appealMode ? "Appeal Letter" : "Prior Auth Letter"}
                          </span>
                        </div>
                        <h1
                          className="text-2xl font-bold text-slate-900"
                          style={{ fontFamily: "'Newsreader', Georgia, serif" }}
                        >
                          {dynamicCaseTitle}
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                          {appealMode
                            ? "Enter clinical details — AI will generate a targeted rebuttal"
                            : "Enter clinical details — AI will generate the medical necessity argument"}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              editCaseId
                                ? "bg-gradient-to-br from-amber-400 to-amber-600"
                                : "bg-gradient-to-br from-teal-400 to-teal-600"
                            }`}
                          >
                            {editCaseId ? (
                              <svg
                                className="w-4 h-4 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                              </svg>
                            ) : (
                              <IconSparkles className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <p
                            className={`text-xs font-semibold uppercase tracking-wider ${
                              editCaseId ? "text-amber-600" : "text-teal-600"
                            }`}
                          >
                            {editCaseId ? "Editing" : "New Case"}
                          </p>
                        </div>
                        <h1 className="text-2xl font-bold text-slate-900">
                          {dynamicCaseTitle}
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                          {editCaseId
                            ? "Update the case details below"
                            : "Fill out the essentials below — add more details if you have them"}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {/* PHI Protection Banner */}
                <div className="mb-8 py-3 px-4 rounded-xl bg-emerald-50/60 border border-emerald-200/40 flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                    <IconShield className="w-3.5 h-3.5 text-white" />
                  </div>
                  <p className="text-sm text-emerald-700">
                    <span className="font-semibold">PHI Protection Active</span>{" "}
                    — patient identifiers are automatically removed before
                    processing.
                  </p>
                </div>
              </>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <IconExclamation className="w-4 h-4 text-red-600" />
                </div>
                {error}
              </div>
            )}

            {/* Edit Mode Notice */}
            {editCaseId && (
              <div className="mb-6 p-4 bg-blue-50/80 rounded-xl border border-blue-200/60 flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-800 mb-0.5">
                    Editing Case
                  </p>
                  <p className="text-sm text-blue-700">
                    Updating this case will regenerate the AI preparation guide
                    with the new information.
                  </p>
                </div>
              </div>
            )}

            {/* Template Selector — hidden in prior auth mode */}
            {showTemplates && !priorAuthMode && (
              <TemplateSelector
                onSelectTemplate={handleSelectTemplate}
                onStartFresh={handleStartFresh}
                orderedTemplates={orderedTemplates}
              />
            )}

            {/* Show template button when collapsed */}
            {!showTemplates && !priorAuthMode && (
              <button
                type="button"
                onClick={() => setShowTemplates(true)}
                className="mb-4 text-sm text-slate-400 hover:text-slate-500 font-medium flex items-center gap-1"
              >
                <IconSparkles className="w-4 h-4" />
                Use a quick start template
              </button>
            )}

            {/* ============================================ */}
            {/* ESSENTIAL FIELDS */}
            {/* (In appeal mode, collapsed until denial reason is set or user expands) */}
            {/* ============================================ */}
            {(!appealMode || formData.denialReason || appealFormExpanded) && (
              <>
                <div
                  className={
                    priorAuthMode
                      ? "bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden mb-5"
                      : "bg-white rounded-2xl border-2 border-teal-200 shadow-md shadow-teal-100/50 overflow-hidden mb-5 ring-1 ring-teal-100"
                  }
                >
                  {!priorAuthMode && (
                    <div className="px-6 py-5 bg-gradient-to-r from-teal-50 via-teal-50/80 to-white border-b border-teal-100">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center shadow-lg shadow-teal-500/30">
                          <IconClipboard className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h2 className="text-base font-bold text-slate-800">
                            Essential Information
                          </h2>
                          <p className="text-sm text-teal-600">
                            Just these few fields to get started
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div
                    className={`p-6 ${
                      priorAuthMode ? "space-y-0" : "space-y-6"
                    }`}
                  >
                    {/* Request Type - Category pills (optional) + Specific Request (required) */}
                    {/* In appeal mode with extraction: compact confirmed display */}
                    {appealMode &&
                    extractionResult &&
                    formData.requestDetails ? (
                      <div className="pb-4 opacity-70 hover:opacity-100 transition-opacity">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span
                              className="text-[11px] font-bold text-slate-300 tracking-wider flex-shrink-0"
                              style={{ fontFamily: "'DM Mono', monospace" }}
                            >
                              01
                            </span>
                            <IconCheck className="w-3.5 h-3.5 text-teal-400 flex-shrink-0" />
                            <span className="text-sm text-slate-600 truncate">
                              {formData.requestDetails}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-[10px] font-medium text-slate-400 flex-shrink-0">
                              {formData.requestType ===
                              "DME (Durable Medical Equipment)"
                                ? "DME"
                                : formData.requestType}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setExtractionResult(null)}
                            className="text-xs text-slate-400 hover:text-slate-600 flex-shrink-0"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={
                          priorAuthMode
                            ? "pb-6"
                            : "p-5 bg-gradient-to-r from-slate-50 to-white rounded-xl border border-slate-200/80 shadow-sm"
                        }
                      >
                        <label className="block text-sm font-medium text-slate-700 mb-3">
                          <span className="inline-flex items-center gap-2">
                            {priorAuthMode ? (
                              <span
                                className="text-[11px] font-bold text-slate-400 tracking-wider"
                                style={{ fontFamily: "'DM Mono', monospace" }}
                              >
                                01
                              </span>
                            ) : (
                              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-white text-xs font-bold shadow-md shadow-teal-500/30 ring-2 ring-white">
                                1
                              </span>
                            )}
                            <span
                              style={
                                priorAuthMode
                                  ? {
                                      fontFamily:
                                        "'Newsreader', Georgia, serif",
                                    }
                                  : undefined
                              }
                            >
                              What's Being Requested?{" "}
                              <span className="text-rose-500">*</span>
                            </span>
                          </span>
                        </label>

                        {/* Specific Request Input (Required) */}
                        <div className="mb-4">
                          <SpecificRequestInput
                            value={formData.requestDetails}
                            onChange={(value) =>
                              updateField("requestDetails", value)
                            }
                            selectedCategory={formData.requestType}
                            onCategoryDetected={(category) => {
                              if (!formData.requestType) {
                                updateField("requestType", category);
                              }
                            }}
                            contrastSelection={formData.contrastRequired}
                            onContrastChange={(contrast) =>
                              updateField("contrastRequired", contrast)
                            }
                          />
                        </div>

                        {/* Category Pills (Optional helpers) */}
                        <div>
                          <p className="text-xs text-slate-400 mb-2">
                            Category{" "}
                            {formData.requestType && (
                              <span className="text-teal-600">
                                (auto-detected)
                              </span>
                            )}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {REQUEST_TYPES.map((type) => {
                              const isSelected = formData.requestType === type;
                              const shortLabel =
                                type === "DME (Durable Medical Equipment)"
                                  ? "DME"
                                  : type;
                              return (
                                <button
                                  key={type}
                                  type="button"
                                  onClick={() =>
                                    updateField(
                                      "requestType",
                                      isSelected ? "" : type
                                    )
                                  }
                                  className={`
                              px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-150
                              ${
                                isSelected
                                  ? "bg-teal-500 text-white shadow-sm"
                                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                              }
                            `}
                                >
                                  {shortLabel}
                                  {isSelected && (
                                    <IconCheck className="w-3 h-3 ml-1 inline" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Facility Selection - Only for site-of-care relevant requests */}
                    {FACILITY_RELEVANT_REQUEST_TYPES.some((type) =>
                      formData.requestType
                        .toLowerCase()
                        .includes(type.toLowerCase())
                    ) && (
                      <div className="p-5 bg-gradient-to-r from-slate-50/50 to-white rounded-xl border border-slate-200/60">
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-sm font-medium text-slate-600 flex items-center gap-2">
                            <svg
                              className="w-4 h-4 text-slate-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={1.5}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                              />
                            </svg>
                            Where will this be performed?
                          </label>
                          <span className="text-xs text-slate-400">
                            (optional)
                          </span>
                        </div>

                        {/* Facility type pills */}
                        <div className="flex flex-wrap gap-2">
                          {FACILITY_TYPES.map((facility) => {
                            const isSelected =
                              formData.facilityType === facility.value;
                            return (
                              <button
                                key={facility.value}
                                type="button"
                                onClick={() => {
                                  updateField(
                                    "facilityType",
                                    isSelected ? "" : facility.value
                                  );
                                  if (facility.value !== "hospital") {
                                    setHospitalJustifications([]);
                                  }
                                }}
                                className={`px-3 py-1.5 text-sm rounded-lg border transition-all ${
                                  isSelected
                                    ? "bg-teal-50 border-teal-300 text-teal-700 shadow-sm"
                                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                                }`}
                              >
                                {facility.label}
                              </button>
                            );
                          })}
                        </div>

                        {/* Hospital justification helper - appears when Hospital selected */}
                        {formData.facilityType === "hospital" && (
                          <div className="mt-4 p-3 bg-amber-50/70 rounded-lg border border-amber-200/60">
                            <div className="flex items-start gap-2 mb-2">
                              <svg
                                className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                                />
                              </svg>
                              <div>
                                <p className="text-sm font-medium text-amber-800">
                                  Clinical factors that support hospital-based
                                  care
                                </p>
                                <p className="text-xs text-amber-600 mt-0.5">
                                  Select any that apply — strengthens
                                  site-of-care justification
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {HOSPITAL_JUSTIFICATION_FACTORS.map((factor) => {
                                const isChecked =
                                  hospitalJustifications.includes(factor.id);
                                return (
                                  <label
                                    key={factor.id}
                                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all text-sm ${
                                      isChecked
                                        ? "bg-amber-100/80 border border-amber-300"
                                        : "bg-white/60 border border-transparent hover:bg-white"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => {
                                        if (isChecked) {
                                          setHospitalJustifications((prev) =>
                                            prev.filter(
                                              (id) => id !== factor.id
                                            )
                                          );
                                        } else {
                                          setHospitalJustifications((prev) => [
                                            ...prev,
                                            factor.id,
                                          ]);
                                        }
                                      }}
                                      className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                                    />
                                    <span className="text-amber-900">
                                      {factor.label}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Option A: Prior cancellation trigger question */}
                    {showCancellationQuestion && priorCancellation === null && (
                      <div className="p-4 bg-gradient-to-r from-rose-50/60 to-amber-50/40 rounded-xl border border-rose-200/50">
                        <div className="flex items-start gap-3">
                          <div className="w-7 h-7 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg
                              className="w-4 h-4 text-rose-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={1.5}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-slate-700">
                              Has this procedure been previously scheduled and
                              canceled at the approved facility?
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              If yes, we'll help you document why continued care
                              at that facility is clinically unsafe.
                            </p>
                            <div className="flex gap-2 mt-3">
                              <button
                                type="button"
                                onClick={activateSalvageMode}
                                className="px-4 py-1.5 text-sm font-medium rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors shadow-sm"
                              >
                                Yes — case was canceled
                              </button>
                              <button
                                type="button"
                                onClick={() => setPriorCancellation("no")}
                                className="px-4 py-1.5 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors"
                              >
                                No
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Site of Service — Detailed Clinical Inputs */}
                    {showSosSection && (
                      <SiteOfServiceIntake
                        inputs={sosInputs}
                        onChange={setSosInputs}
                        defaultExpanded={isSiteOfCareDenial || forceSosExpanded}
                        requestCategory={formData.requestType}
                      />
                    )}

                    {/* Insurance Company — compact when extracted in appeal mode */}
                    {appealMode &&
                    extractionResult &&
                    formData.insuranceCompany ? (
                      <div className="py-4 opacity-70 hover:opacity-100 transition-opacity">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span
                              className="text-[11px] font-bold text-slate-300 tracking-wider flex-shrink-0"
                              style={{ fontFamily: "'DM Mono', monospace" }}
                            >
                              02
                            </span>
                            <IconCheck className="w-3.5 h-3.5 text-teal-400 flex-shrink-0" />
                            <span className="text-sm text-slate-600">
                              {formData.insuranceCompanyLabel ||
                                INSURANCE_COMPANIES.find(
                                  (ic) => ic.value === formData.insuranceCompany
                                )?.label ||
                                formData.insuranceCompany}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setExtractionResult(null)}
                            className="text-xs text-slate-400 hover:text-slate-600 flex-shrink-0"
                          >
                            Change
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {priorAuthMode && (
                          <div
                            className={`border-t border-slate-100${
                              priorAuthMode && !formData.requestDetails.trim()
                                ? " hidden"
                                : ""
                            }`}
                          />
                        )}
                        <div
                          className={`${
                            priorAuthMode
                              ? "py-6"
                              : "p-5 bg-gradient-to-r from-slate-50 to-white rounded-xl border border-slate-200/80 shadow-sm"
                          }${
                            priorAuthMode && !formData.requestDetails.trim()
                              ? " hidden"
                              : ""
                          }`}
                        >
                          <label className="block text-sm font-medium text-slate-700 mb-3">
                            <span className="inline-flex items-center gap-2">
                              {priorAuthMode ? (
                                <span
                                  className="text-[11px] font-bold text-slate-400 tracking-wider"
                                  style={{ fontFamily: "'DM Mono', monospace" }}
                                >
                                  02
                                </span>
                              ) : (
                                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-white text-xs font-bold shadow-md shadow-teal-500/30 ring-2 ring-white">
                                  2
                                </span>
                              )}
                              <span
                                style={
                                  priorAuthMode
                                    ? {
                                        fontFamily:
                                          "'Newsreader', Georgia, serif",
                                      }
                                    : undefined
                                }
                              >
                                Insurance Company{" "}
                                <span className="text-rose-500">*</span>
                              </span>
                            </span>
                          </label>
                          <InsuranceSelect
                            value={insuranceDisplayValue}
                            onChange={(value) => {
                              updateField("insuranceCompany", value);
                              updateField("insuranceCompanyLabel", "");
                            }}
                            onRegionalTrigger={handleRegionalTrigger}
                            planType={formData.planType}
                            onPlanTypeChange={(value) =>
                              updateField("planType", value)
                            }
                            availablePlans={availablePlans}
                          />
                        </div>
                      </>
                    )}

                    {/* Denial Reason - Hidden in prior auth mode, shown in appeal mode */}
                    {(!priorAuthMode || appealMode) && (
                      <>
                        {/* Appeal mode with extraction: muted confirmed row */}
                        {appealMode &&
                        extractionResult &&
                        formData.denialReason ? (
                          <div className="py-3 opacity-70 hover:opacity-100 transition-opacity">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span
                                  className="text-[11px] font-bold text-slate-300 tracking-wider flex-shrink-0"
                                  style={{ fontFamily: "'DM Mono', monospace" }}
                                >
                                  03
                                </span>
                                <IconCheck className="w-3.5 h-3.5 text-teal-400 flex-shrink-0" />
                                <span className="text-sm text-slate-600">
                                  {DENIAL_REASON_DISPLAY_LABELS[
                                    formData.denialReason
                                  ] || formData.denialReason}
                                </span>
                              </div>
                              <select
                                value={formData.denialReason}
                                onChange={(e) => {
                                  updateField("denialReason", e.target.value);
                                  if (
                                    e.target.value !==
                                    "Site of care / Facility denial"
                                  ) {
                                    updateField("facilityType", "");
                                    setHospitalJustifications([]);
                                  }
                                  if (e.target.value !== "Level of Care") {
                                    setLocContext({
                                      requestedLevelOfCare: "",
                                      payerSuggestedLevel: "",
                                      reviewType: "",
                                      diagnoses: "",
                                      objectiveFindings: "",
                                      interventions: "",
                                      monitoringRequirements: "",
                                      risksIfDowngraded: "",
                                      caseSummary: "",
                                    });
                                  }
                                }}
                                className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white text-slate-400 hover:border-slate-300 focus:border-teal-500 outline-none cursor-pointer flex-shrink-0"
                              >
                                {DENIAL_REASONS.map((reason) => (
                                  <option key={reason} value={reason}>
                                    {DENIAL_REASON_DISPLAY_LABELS[reason] ||
                                      reason}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        ) : (
                          <div className="p-5 bg-gradient-to-r from-slate-50 to-white rounded-xl border border-slate-200/80 shadow-sm">
                            <label className="block text-sm font-medium text-slate-700 mb-3">
                              <span className="inline-flex items-center gap-3">
                                {appealMode ? (
                                  <span
                                    className="text-[11px] font-bold text-slate-400 tracking-wider"
                                    style={{
                                      fontFamily: "'DM Mono', monospace",
                                    }}
                                  >
                                    03
                                  </span>
                                ) : (
                                  <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-white text-xs font-bold shadow-md shadow-teal-500/30 ring-2 ring-white">
                                    3
                                  </span>
                                )}
                                Denial Reason{" "}
                                <span className="text-rose-500">*</span>
                              </span>
                            </label>
                            {showClassificationConfirmation &&
                              denialClassificationResult &&
                              formData.denialReason && (
                                <div className="mb-4 p-3 bg-teal-50 border border-teal-200 rounded-lg flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <IconCheck className="w-4 h-4 text-teal-600" />
                                    <span className="text-sm text-teal-800">
                                      Selected:{" "}
                                      <span className="font-medium">
                                        {DENIAL_REASON_DISPLAY_LABELS[
                                          formData.denialReason
                                        ] || formData.denialReason}
                                      </span>
                                      {denialClassificationResult.confidence !==
                                        "high" && (
                                        <span className="text-teal-600 ml-1">
                                          (
                                          {
                                            denialClassificationResult.confidence
                                          }{" "}
                                          confidence)
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={handleChangeClassification}
                                    className="text-sm text-teal-600 hover:text-teal-800 font-medium"
                                  >
                                    Change
                                  </button>
                                </div>
                              )}

                            <div className="space-y-4">
                              {DENIAL_REASON_GROUPS.map((group, groupIndex) => (
                                <div key={group.label}>
                                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                                    {group.label}
                                  </p>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {group.reasons.map((reason) => {
                                      const isSelected =
                                        formData.denialReason === reason;
                                      const isSiteOfCare =
                                        reason ===
                                        "Site of care / Facility denial";
                                      const isLOC = reason === "Level of Care";
                                      const displayLabel =
                                        DENIAL_REASON_DISPLAY_LABELS[reason] ||
                                        reason;
                                      const helperText =
                                        DENIAL_REASON_HELPER_TEXT[reason];
                                      const isHighlightedAlternative =
                                        denialClassificationResult?.confidence ===
                                          "low" &&
                                        denialClassificationResult.alternativeReasons?.some(
                                          (a) => a.reasonKey === reason
                                        );

                                      return (
                                        <button
                                          key={reason}
                                          type="button"
                                          onClick={() => {
                                            updateField(
                                              "denialReason",
                                              isSelected ? "" : reason
                                            );
                                            setShowClassificationConfirmation(
                                              false
                                            );
                                            setDenialClassificationResult(null);
                                            // Clear facility selections when deselecting site-of-care denial
                                            if (isSelected && isSiteOfCare) {
                                              updateField("facilityType", "");
                                              setHospitalJustifications([]);
                                              setOtherJustification("");
                                            }
                                            // Clear LOC context when deselecting level-of-care denial
                                            if (isSelected && isLOC) {
                                              setLocContext({
                                                requestedLevelOfCare: "",
                                                payerSuggestedLevel: "",
                                                reviewType: "",
                                                diagnoses: "",
                                                objectiveFindings: "",
                                                interventions: "",
                                                monitoringRequirements: "",
                                                risksIfDowngraded: "",
                                                caseSummary: "",
                                              });
                                            }
                                          }}
                                          className={`
                                  px-3 py-2.5 rounded-lg text-sm transition-all duration-150
                                  border-2 flex flex-col items-center justify-center text-center min-h-[60px]
                                  ${
                                    isSelected
                                      ? isSiteOfCare
                                        ? "bg-amber-500 text-white border-amber-500 shadow-sm"
                                        : isLOC
                                        ? "bg-violet-500 text-white border-violet-500 shadow-sm"
                                        : "bg-teal-500 text-white border-teal-500 shadow-sm"
                                      : isHighlightedAlternative
                                      ? "bg-yellow-50 text-slate-700 border-yellow-400 ring-2 ring-yellow-200"
                                      : isSiteOfCare
                                      ? "bg-white text-amber-700 border-amber-200 hover:border-amber-400 hover:bg-amber-50"
                                      : isLOC
                                      ? "bg-white text-violet-700 border-violet-200 hover:border-violet-400 hover:bg-violet-50"
                                      : "bg-white text-slate-700 border-slate-200 hover:border-teal-400 hover:bg-teal-50"
                                  }
                                `}
                                        >
                                          <span className="font-medium flex items-center gap-1">
                                            {displayLabel}
                                            {isSelected && (
                                              <IconCheck className="w-4 h-4 flex-shrink-0" />
                                            )}
                                          </span>
                                          {helperText && (
                                            <span
                                              className={`text-xs mt-0.5 ${
                                                isSelected
                                                  ? "text-white/80"
                                                  : "text-slate-500"
                                              }`}
                                            >
                                              {helperText}
                                            </span>
                                          )}
                                        </button>
                                      );
                                    })}

                                    {/* Add "Unsure / Paste denial text" button in the Other group */}
                                    {group.label === "Other" && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setShowDenialAnalysisModal(true)
                                        }
                                        className="px-3 py-2.5 rounded-lg text-sm transition-all duration-150
                                border-2 border-dashed flex flex-col items-center justify-center text-center min-h-[60px]
                                bg-slate-50 text-slate-600 border-slate-300 hover:border-teal-400 hover:bg-teal-50 hover:text-teal-700"
                                      >
                                        <span className="font-medium flex items-center gap-1">
                                          <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                            />
                                          </svg>
                                          Unsure / Paste denial
                                        </span>
                                        <span className="text-xs mt-0.5 text-slate-500">
                                          Auto-classify from denial text
                                        </span>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* end denial reason confirmed/tile ternary */}
                        {appealMode && formData.denialReason && (
                          <div className="mt-5 space-y-4 p-5 bg-white rounded-xl border-2 border-teal-200 shadow-sm ring-1 ring-teal-100">
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className="text-[11px] font-bold text-teal-500 tracking-wider"
                                style={{ fontFamily: "'DM Mono', monospace" }}
                              >
                                YOUR INPUT
                              </span>
                            </div>

                            {/* Collapsed denial letter indicator (if already pasted) */}
                            {formData.denialText && (
                              <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-200/60">
                                <div className="flex items-center gap-2">
                                  <IconCheck className="w-4 h-4 text-teal-500" />
                                  <span className="text-sm text-slate-600">
                                    Denial letter attached
                                  </span>
                                  <span className="text-xs text-slate-400 font-mono">
                                    ({formData.denialText.length} chars)
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const el = document.getElementById(
                                      "appeal-denial-text-area"
                                    );
                                    if (el)
                                      el.style.display =
                                        el.style.display === "none"
                                          ? "block"
                                          : "none";
                                  }}
                                  className="text-xs text-slate-400 hover:text-slate-600"
                                >
                                  Edit
                                </button>
                              </div>
                            )}
                            {/* Hidden-by-default full textarea (for editing) */}
                            <div
                              id="appeal-denial-text-area"
                              style={
                                formData.denialText
                                  ? { display: "none" }
                                  : undefined
                              }
                            >
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                Denial language from insurer
                              </label>
                              <textarea
                                value={formData.denialText}
                                onChange={(e) =>
                                  updateField("denialText", e.target.value)
                                }
                                placeholder="Paste the denial letter or relevant excerpts..."
                                className="w-full border border-slate-200 rounded-xl px-4 py-3 h-28 text-sm bg-slate-50/50 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none"
                              />
                            </div>

                            {/* Denial date — inline, compact */}
                            <div className="flex items-center gap-3">
                              <label className="text-sm text-slate-600 flex-shrink-0">
                                Denial date
                              </label>
                              <input
                                type="date"
                                value={formData.denialDate}
                                onChange={(e) =>
                                  updateField("denialDate", e.target.value)
                                }
                                className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 outline-none transition-all"
                              />
                            </div>

                            {/* Clinical note confirmation (if extracted at start) */}
                            {noteExtracted && (
                              <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-teal-50/60 border border-teal-200/40">
                                <div className="flex items-center gap-2">
                                  <IconCheck className="w-4 h-4 text-teal-500" />
                                  <span className="text-sm text-teal-700">
                                    Clinical note extracted — fields pre-filled
                                    below
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNoteExtracted(false);
                                    setClinicalNoteText("");
                                  }}
                                  className="text-xs text-teal-500 hover:text-teal-700"
                                >
                                  Clear
                                </button>
                              </div>
                            )}

                            {/* Diagnosis — always ask, pre-filled if extracted */}
                            <div>
                              <label className="block text-sm font-semibold text-slate-800 mb-1.5">
                                Diagnosis
                              </label>
                              <input
                                type="text"
                                value={
                                  selectedConditions.length > 0
                                    ? selectedConditions
                                        .map((c) => c.label)
                                        .join(", ")
                                    : ""
                                }
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val.trim()) {
                                    setSelectedConditions([
                                      {
                                        id: val
                                          .toLowerCase()
                                          .replace(/\s+/g, "_"),
                                        label: val,
                                        type: "common" as const,
                                      },
                                    ]);
                                  } else {
                                    setSelectedConditions([]);
                                  }
                                }}
                                placeholder="e.g., L4-L5 radiculopathy, Type 2 diabetes mellitus (E11.9)"
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 outline-none transition-all"
                              />
                              <p className="text-xs text-slate-400 mt-1">
                                Include ICD-10 code if known
                              </p>
                            </div>

                            {/* Divider */}
                            <div className="h-px bg-slate-100" />

                            {/* STEP THERAPY: Individual drug pills with text inputs */}
                            {extractionResult &&
                            extractionResult.required_step_drugs.length > 0 &&
                            formData.denialReason ===
                              "Step therapy required" ? (
                              <div>
                                <label className="block text-sm font-semibold text-slate-800 mb-3">
                                  What happened with each required drug?
                                </label>
                                <div className="space-y-3">
                                  {extractionResult.required_step_drugs.map(
                                    (drug) => {
                                      const answerKey = `step_drug_${drug
                                        .toLowerCase()
                                        .replace(/\s+/g, "_")}`;
                                      return (
                                        <div
                                          key={drug}
                                          className="flex items-start gap-3"
                                        >
                                          <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-slate-100 text-sm font-medium text-slate-700 flex-shrink-0 mt-0.5 min-w-[110px] justify-center">
                                            {drug}
                                          </span>
                                          <input
                                            type="text"
                                            value={
                                              appealAnswers[answerKey] || ""
                                            }
                                            onChange={(e) =>
                                              setAppealAnswers((prev) => ({
                                                ...prev,
                                                [answerKey]: e.target.value,
                                              }))
                                            }
                                            placeholder={`e.g., Tried 1500mg x 4mo — GI intolerance`}
                                            className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 outline-none transition-all"
                                          />
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                                <p className="text-xs text-slate-400 mt-2">
                                  Include dose, duration, and why each was
                                  stopped (side effects, inadequate response,
                                  contraindicated, etc.)
                                </p>
                              </div>
                            ) : (
                              /* Generic high-priority questions for non-step-therapy denials */
                              <>
                                {appealQuestions
                                  .filter(
                                    (q) =>
                                      q.id !== "denial_text" &&
                                      q.id !== "denial_date" &&
                                      q.id !== "original_submission_summary" &&
                                      q.priority === "high"
                                  )
                                  .map((question) => (
                                    <div key={question.id}>
                                      {/* Formulary failures: structured rows */}
                                      {question.id ===
                                      "appeal_formulary_failures" ? (
                                        <div>
                                          <label className="block text-sm font-semibold text-slate-800 mb-3">
                                            {question.question}
                                          </label>
                                          <div className="space-y-2.5">
                                            {formularyRows.map((row, index) => (
                                              <div
                                                key={index}
                                                className="flex items-start gap-2"
                                              >
                                                <input
                                                  type="text"
                                                  value={row.drug}
                                                  onChange={(e) =>
                                                    updateFormularyRow(
                                                      index,
                                                      "drug",
                                                      e.target.value
                                                    )
                                                  }
                                                  placeholder="Drug name"
                                                  className="w-[140px] border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 outline-none transition-all flex-shrink-0"
                                                />
                                                <input
                                                  type="text"
                                                  value={row.dose}
                                                  onChange={(e) =>
                                                    updateFormularyRow(
                                                      index,
                                                      "dose",
                                                      e.target.value
                                                    )
                                                  }
                                                  placeholder="Dose & duration"
                                                  className="w-[160px] border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 outline-none transition-all flex-shrink-0"
                                                />
                                                <input
                                                  type="text"
                                                  value={row.outcome}
                                                  onChange={(e) =>
                                                    updateFormularyRow(
                                                      index,
                                                      "outcome",
                                                      e.target.value
                                                    )
                                                  }
                                                  placeholder="What happened"
                                                  className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 outline-none transition-all"
                                                />
                                                {formularyRows.length > 1 && (
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      removeFormularyRow(index)
                                                    }
                                                    className="p-1.5 text-slate-300 hover:text-slate-500 transition-colors flex-shrink-0 mt-0.5"
                                                  >
                                                    <IconX className="w-3.5 h-3.5" />
                                                  </button>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                          <button
                                            type="button"
                                            onClick={addFormularyRow}
                                            className="mt-2.5 text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 transition-colors"
                                          >
                                            <IconPlus className="w-3.5 h-3.5" />
                                            Add another medication
                                          </button>
                                        </div>
                                      ) : (
                                        /* Default: textarea for other high-priority questions */
                                        <>
                                          <label className="block text-sm font-semibold text-slate-800 mb-1.5">
                                            {question.question}
                                          </label>
                                          <textarea
                                            value={
                                              appealAnswers[question.id] || ""
                                            }
                                            onChange={(e) =>
                                              setAppealAnswers((prev) => ({
                                                ...prev,
                                                [question.id]: e.target.value,
                                              }))
                                            }
                                            placeholder={question.placeholder}
                                            className="w-full border border-slate-200 rounded-xl px-4 py-3 h-28 text-sm bg-slate-50/50 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none"
                                          />
                                        </>
                                      )}
                                    </div>
                                  ))}
                              </>
                            )}

                            {/* Secondary questions — collapsed by default */}
                            {appealQuestions.filter(
                              (q) =>
                                q.id !== "denial_text" &&
                                q.id !== "denial_date" &&
                                q.id !== "original_submission_summary" &&
                                q.priority !== "high"
                            ).length > 0 && (
                              <details className="group">
                                <summary className="text-sm text-slate-500 cursor-pointer hover:text-teal-600 transition-colors flex items-center gap-1">
                                  <IconChevronDown className="w-3.5 h-3.5 group-open:rotate-180 transition-transform" />
                                  More details (optional)
                                </summary>
                                <div className="mt-3 space-y-4">
                                  {/* Original submission */}
                                  <div>
                                    <label className="block text-sm text-slate-600 mb-1.5">
                                      What was in your original PA submission?
                                    </label>
                                    <textarea
                                      value={formData.originalSubmissionSummary}
                                      onChange={(e) =>
                                        updateField(
                                          "originalSubmissionSummary",
                                          e.target.value
                                        )
                                      }
                                      placeholder="e.g., Clinical notes, A1c of 9.2%, PT records..."
                                      className="w-full border border-slate-200 rounded-xl px-4 py-3 h-20 text-sm bg-slate-50/50 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none"
                                    />
                                  </div>
                                  {/* Non-high-priority appeal questions */}
                                  {appealQuestions
                                    .filter(
                                      (q) =>
                                        q.id !== "denial_text" &&
                                        q.id !== "denial_date" &&
                                        q.id !==
                                          "original_submission_summary" &&
                                        q.priority !== "high"
                                    )
                                    .map((question) => (
                                      <div key={question.id}>
                                        <label className="block text-sm text-slate-600 mb-1.5">
                                          {question.question}
                                        </label>
                                        <textarea
                                          value={
                                            appealAnswers[question.id] || ""
                                          }
                                          onChange={(e) =>
                                            setAppealAnswers((prev) => ({
                                              ...prev,
                                              [question.id]: e.target.value,
                                            }))
                                          }
                                          placeholder={question.placeholder}
                                          className="w-full border border-slate-200 rounded-xl px-4 py-3 h-20 text-sm bg-slate-50/50 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none"
                                        />
                                      </div>
                                    ))}
                                </div>
                              </details>
                            )}
                          </div>
                        )}

                        {/* Denial Analysis Modal */}
                        {showDenialAnalysisModal && (
                          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                              <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                  <h3 className="text-lg font-semibold text-slate-900">
                                    Analyze Denial Text
                                  </h3>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowDenialAnalysisModal(false);
                                      setDenialClassificationResult(null);
                                    }}
                                    className="text-slate-400 hover:text-slate-600"
                                  >
                                    <IconX className="w-5 h-5" />
                                  </button>
                                </div>

                                <p className="text-sm text-slate-600 mb-4">
                                  Paste the denial letter text below and we'll
                                  help identify the denial reason.
                                </p>

                                <textarea
                                  value={denialPasteText}
                                  onChange={(e) =>
                                    setDenialPasteText(e.target.value)
                                  }
                                  placeholder="Paste denial letter text here..."
                                  className="w-full border border-slate-200 rounded-xl px-4 py-3 h-40 text-sm bg-slate-50 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none"
                                />

                                {/* Classification Result - Low Confidence */}
                                {denialClassificationResult &&
                                  denialClassificationResult.confidence ===
                                    "low" && (
                                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                                      <p className="text-sm font-medium text-yellow-800 mb-2">
                                        Multiple possible matches found. Please
                                        select:
                                      </p>
                                      <div className="space-y-2">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleSelectClassifiedReason(
                                              denialClassificationResult.reasonKey
                                            )
                                          }
                                          className="w-full text-left px-3 py-2 bg-white border border-yellow-300 rounded-lg text-sm hover:bg-yellow-50 transition-colors"
                                        >
                                          <span className="font-medium">
                                            {DENIAL_REASON_DISPLAY_LABELS[
                                              denialClassificationResult
                                                .reasonKey
                                            ] ||
                                              denialClassificationResult.reasonKey}
                                          </span>
                                          <span className="text-yellow-700 ml-2">
                                            (matched:{" "}
                                            {denialClassificationResult.matchedSignals
                                              .slice(0, 2)
                                              .join(", ")}
                                            )
                                          </span>
                                        </button>
                                        {denialClassificationResult.alternativeReasons?.map(
                                          (alt) => (
                                            <button
                                              key={alt.reasonKey}
                                              type="button"
                                              onClick={() =>
                                                handleSelectClassifiedReason(
                                                  alt.reasonKey
                                                )
                                              }
                                              className="w-full text-left px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm hover:bg-slate-50 transition-colors"
                                            >
                                              <span className="font-medium">
                                                {DENIAL_REASON_DISPLAY_LABELS[
                                                  alt.reasonKey
                                                ] || alt.reasonKey}
                                              </span>
                                              <span className="text-slate-500 ml-2">
                                                (matched:{" "}
                                                {alt.matchedSignals
                                                  .slice(0, 2)
                                                  .join(", ")}
                                                )
                                              </span>
                                            </button>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}

                                <div className="mt-4 flex gap-3">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowDenialAnalysisModal(false);
                                      setDenialClassificationResult(null);
                                    }}
                                    className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleAnalyzeDenial}
                                    disabled={!denialPasteText.trim()}
                                    className="flex-1 px-4 py-2.5 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                                  >
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                      />
                                    </svg>
                                    Analyze denial
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Smart Default: Show treatments if Step Therapy (hide in appeal mode when drug pills are shown) */}
                    {isStepTherapyDenial &&
                      !(
                        appealMode &&
                        extractionResult &&
                        extractionResult.required_step_drugs.length > 0
                      ) && (
                        <div className="p-5 bg-gradient-to-r from-amber-50 to-amber-50/50 rounded-xl border border-amber-200/80 shadow-sm">
                          <label className="block text-sm font-medium text-slate-700 mb-3">
                            <span className="inline-flex items-center gap-3">
                              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white text-xs font-bold shadow-md shadow-amber-500/30 ring-2 ring-white">
                                !
                              </span>
                              What's Already Been Tried?{" "}
                              <span className="text-rose-500">*</span>
                            </span>
                            <span className="block text-xs font-normal text-amber-600 ml-10 mt-1">
                              Required for step therapy denials
                            </span>
                          </label>
                          <TriedTreatmentsSelect
                            selected={selectedTreatments}
                            onChange={setSelectedTreatments}
                          />
                        </div>
                      )}

                    {/* Smart Default: Show Site of Care Justification if Site of Care denial */}
                    {isSiteOfCareDenial && (
                      <div className="p-5 bg-gradient-to-r from-amber-50 to-amber-50/50 rounded-xl border border-amber-200/80 shadow-sm">
                        <label className="block text-sm font-medium text-slate-700 mb-3">
                          <span className="inline-flex items-center gap-3">
                            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white text-xs font-bold shadow-md shadow-amber-500/30 ring-2 ring-white">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                />
                              </svg>
                            </span>
                            Where is the procedure requested?{" "}
                            <span className="text-rose-500">*</span>
                          </span>
                          <span className="block text-xs font-normal text-amber-600 ml-10 mt-1">
                            Required for site-of-care denials
                          </span>
                        </label>

                        {/* Facility type pills */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {FACILITY_TYPES.filter(
                            (f) => f.value !== "not_sure"
                          ).map((facility) => {
                            const isSelected =
                              formData.facilityType === facility.value;
                            return (
                              <button
                                key={facility.value}
                                type="button"
                                onClick={() => {
                                  updateField(
                                    "facilityType",
                                    isSelected ? "" : facility.value
                                  );
                                  if (facility.value !== "hospital") {
                                    setHospitalJustifications([]);
                                    setOtherJustification("");
                                  }
                                }}
                                className={`px-4 py-2.5 text-sm rounded-lg border-2 transition-all font-medium ${
                                  isSelected
                                    ? "bg-amber-500 border-amber-500 text-white shadow-sm"
                                    : "bg-white border-slate-200 text-slate-700 hover:border-amber-400 hover:bg-amber-50"
                                }`}
                              >
                                {facility.label}
                              </button>
                            );
                          })}
                        </div>

                        {/* Hospital justification factors - appears when Hospital selected */}
                        {formData.facilityType === "hospital" && (
                          <div className="p-4 bg-white/80 rounded-xl border border-amber-200/60">
                            <div className="flex items-start gap-2 mb-3">
                              <svg
                                className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                                />
                              </svg>
                              <div>
                                <p className="text-sm font-semibold text-amber-800">
                                  Why does this patient need hospital-based
                                  care?
                                </p>
                                <p className="text-xs text-amber-600 mt-0.5">
                                  Select all that apply — these become your
                                  talking points
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {HOSPITAL_JUSTIFICATION_FACTORS.filter(
                                (f) => f.id !== "other"
                              ).map((factor) => {
                                const isChecked =
                                  hospitalJustifications.includes(factor.id);
                                return (
                                  <label
                                    key={factor.id}
                                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                      isChecked
                                        ? "bg-amber-100 border-2 border-amber-400"
                                        : "bg-slate-50 border-2 border-transparent hover:bg-amber-50"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => {
                                        if (isChecked) {
                                          setHospitalJustifications((prev) =>
                                            prev.filter(
                                              (id) => id !== factor.id
                                            )
                                          );
                                        } else {
                                          setHospitalJustifications((prev) => [
                                            ...prev,
                                            factor.id,
                                          ]);
                                        }
                                      }}
                                      className="mt-0.5 w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                                    />
                                    <div>
                                      <span className="text-sm font-medium text-slate-800">
                                        {factor.label}
                                      </span>
                                      <p className="text-xs text-slate-500 mt-0.5">
                                        {factor.description}
                                      </p>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>

                            {/* Other justification free text */}
                            <div className="mt-3">
                              <label className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border-2 border-transparent hover:bg-amber-50 transition-all">
                                <input
                                  type="checkbox"
                                  checked={hospitalJustifications.includes(
                                    "other"
                                  )}
                                  onChange={() => {
                                    if (
                                      hospitalJustifications.includes("other")
                                    ) {
                                      setHospitalJustifications((prev) =>
                                        prev.filter((id) => id !== "other")
                                      );
                                    } else {
                                      setHospitalJustifications((prev) => [
                                        ...prev,
                                        "other",
                                      ]);
                                    }
                                  }}
                                  className="mt-0.5 w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-slate-800">
                                    Other clinical factors
                                  </span>
                                  {hospitalJustifications.includes("other") && (
                                    <textarea
                                      value={otherJustification}
                                      onChange={(e) =>
                                        setOtherJustification(e.target.value)
                                      }
                                      placeholder="Describe other clinical factors that support hospital setting..."
                                      className="mt-2 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none resize-none"
                                      rows={2}
                                    />
                                  )}
                                </div>
                              </label>
                            </div>
                          </div>
                        )}

                        {/* Info note for ASC */}
                        {formData.facilityType === "asc" && (
                          <div className="p-3 bg-blue-50/70 rounded-lg border border-blue-200/60">
                            <p className="text-sm text-blue-800">
                              <span className="font-medium">Note:</span> If the
                              insurer is trying to redirect
                              <span className="font-medium"> from</span>{" "}
                              hospital to ASC, select "Hospital" above and
                              explain why the patient needs hospital-level care.
                            </p>
                          </div>
                        )}

                        {/* Summary of selected justifications */}
                        {formData.facilityType === "hospital" &&
                          hospitalJustifications.length > 0 && (
                            <div className="mt-4 p-3 bg-teal-50 rounded-lg border border-teal-200/60">
                              <p className="text-xs font-semibold text-teal-800 uppercase tracking-wide mb-1">
                                Your Talking Points (
                                {hospitalJustifications.length})
                              </p>
                              <ul className="text-sm text-teal-700 space-y-1">
                                {hospitalJustifications.map((id) => {
                                  const factor =
                                    HOSPITAL_JUSTIFICATION_FACTORS.find(
                                      (f) => f.id === id
                                    );
                                  if (id === "other" && otherJustification) {
                                    return (
                                      <li key={id}>• {otherJustification}</li>
                                    );
                                  }
                                  return factor ? (
                                    <li key={id}>• {factor.label}</li>
                                  ) : null;
                                })}
                              </ul>
                            </div>
                          )}
                      </div>
                    )}

                    {/* Smart Default: Show LOC context form if Level of Care denial */}
                    {isLevelOfCareDenial && (
                      <div className="p-5 bg-gradient-to-r from-violet-50 to-violet-50/50 rounded-xl border border-violet-200/80 shadow-sm">
                        <label className="block text-sm font-medium text-slate-700 mb-3">
                          <span className="inline-flex items-center gap-3">
                            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-violet-400 to-violet-600 text-white text-xs font-bold shadow-md shadow-violet-500/30 ring-2 ring-white">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                                />
                              </svg>
                            </span>
                            Level of Care Details
                          </span>
                          <span className="block text-xs font-normal text-violet-600 ml-10 mt-1">
                            Provide context for the level of care dispute
                          </span>
                        </label>

                        {/* Review Type and Level Selection */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1.5">
                              Review Type
                            </label>
                            <select
                              value={locContext.reviewType}
                              onChange={(e) =>
                                updateLocField("reviewType", e.target.value)
                              }
                              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
                            >
                              <option value="">Select review type...</option>
                              {LOC_REVIEW_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1.5">
                              Requested Level of Care
                            </label>
                            <select
                              value={locContext.requestedLevelOfCare}
                              onChange={(e) =>
                                updateLocField(
                                  "requestedLevelOfCare",
                                  e.target.value
                                )
                              }
                              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
                            >
                              <option value="">Select level...</option>
                              {LOC_LEVELS.map((level) => (
                                <option key={level.value} value={level.value}>
                                  {level.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Payer Suggested Level */}
                        <div className="mb-4">
                          <label className="block text-xs font-medium text-slate-600 mb-1.5">
                            Payer's Suggested Level (what they want to downgrade
                            to)
                          </label>
                          <select
                            value={locContext.payerSuggestedLevel}
                            onChange={(e) =>
                              updateLocField(
                                "payerSuggestedLevel",
                                e.target.value
                              )
                            }
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
                          >
                            <option value="">Select level (if known)...</option>
                            {LOC_LEVELS.map((level) => (
                              <option key={level.value} value={level.value}>
                                {level.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Clinical Details - Compact Text Areas */}
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1.5">
                              Primary Diagnoses
                            </label>
                            <textarea
                              value={locContext.diagnoses}
                              onChange={(e) =>
                                updateLocField("diagnoses", e.target.value)
                              }
                              placeholder="e.g., Severe sepsis, acute respiratory failure, DKA..."
                              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none resize-none"
                              rows={2}
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1.5">
                              Objective Findings / Vital Signs
                            </label>
                            <textarea
                              value={locContext.objectiveFindings}
                              onChange={(e) =>
                                updateLocField(
                                  "objectiveFindings",
                                  e.target.value
                                )
                              }
                              placeholder="e.g., BP 85/50 on pressors, HR 120s, lactate 4.2, SpO2 88% on 6L NC..."
                              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none resize-none"
                              rows={2}
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1.5">
                              Current Interventions / Treatments
                            </label>
                            <textarea
                              value={locContext.interventions}
                              onChange={(e) =>
                                updateLocField("interventions", e.target.value)
                              }
                              placeholder="e.g., Norepinephrine drip, continuous BiPAP, IV antibiotics q6h, insulin drip..."
                              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none resize-none"
                              rows={2}
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1.5">
                              Monitoring Requirements
                            </label>
                            <textarea
                              value={locContext.monitoringRequirements}
                              onChange={(e) =>
                                updateLocField(
                                  "monitoringRequirements",
                                  e.target.value
                                )
                              }
                              placeholder="e.g., Continuous telemetry, q1h neuro checks, arterial line monitoring, q2h glucose checks..."
                              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none resize-none"
                              rows={2}
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1.5">
                              Risks if Downgraded
                            </label>
                            <textarea
                              value={locContext.risksIfDowngraded}
                              onChange={(e) =>
                                updateLocField(
                                  "risksIfDowngraded",
                                  e.target.value
                                )
                              }
                              placeholder="e.g., Risk of rapid decompensation, inability to provide timely intervention, inadequate nursing ratio..."
                              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none resize-none"
                              rows={2}
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1.5">
                              Brief Case Summary{" "}
                              <span className="text-slate-400">(optional)</span>
                            </label>
                            <textarea
                              value={locContext.caseSummary}
                              onChange={(e) =>
                                updateLocField("caseSummary", e.target.value)
                              }
                              placeholder="Brief narrative of admission course and current clinical status..."
                              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none resize-none"
                              rows={3}
                            />
                          </div>
                        </div>

                        {/* Quick Summary of LOC Dispute */}
                        {(locContext.requestedLevelOfCare ||
                          locContext.payerSuggestedLevel) && (
                          <div className="mt-4 p-3 bg-violet-100/60 rounded-lg border border-violet-200/60">
                            <p className="text-xs font-semibold text-violet-800 uppercase tracking-wide mb-1">
                              Dispute Summary
                            </p>
                            <p className="text-sm text-violet-700">
                              {locContext.reviewType && (
                                <span className="font-medium">
                                  {
                                    LOC_REVIEW_TYPES.find(
                                      (t) => t.value === locContext.reviewType
                                    )?.label
                                  }
                                  :{" "}
                                </span>
                              )}
                              Requesting{" "}
                              <span className="font-semibold">
                                {LOC_LEVELS.find(
                                  (l) =>
                                    l.value === locContext.requestedLevelOfCare
                                )?.label || "—"}
                              </span>
                              {locContext.payerSuggestedLevel && (
                                <>
                                  {" "}
                                  → Payer suggests{" "}
                                  <span className="font-semibold">
                                    {
                                      LOC_LEVELS.find(
                                        (l) =>
                                          l.value ===
                                          locContext.payerSuggestedLevel
                                      )?.label
                                    }
                                  </span>
                                </>
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Combined Additional Context — reveals after Step 02 is filled */}
                    {priorAuthMode && (
                      <div
                        className={`border-t border-slate-100${
                          priorAuthMode && !formData.insuranceCompany.trim()
                            ? " hidden"
                            : ""
                        }`}
                      />
                    )}
                    <div
                      className={`${
                        priorAuthMode
                          ? "pt-6"
                          : "p-5 bg-gradient-to-r from-slate-50/50 to-white rounded-xl border border-slate-200/60 shadow-sm"
                      }${
                        priorAuthMode && !formData.insuranceCompany.trim()
                          ? " hidden"
                          : ""
                      }`}
                    >
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        <span className="inline-flex items-center gap-2 flex-wrap">
                          {priorAuthMode ? (
                            <span
                              className="text-[11px] font-bold text-slate-400 tracking-wider"
                              style={{ fontFamily: "'DM Mono', monospace" }}
                            >
                              03
                            </span>
                          ) : (
                            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 text-white text-xs font-bold shadow-md shadow-slate-400/30 ring-2 ring-white">
                              {priorAuthMode ? "3" : "4"}
                            </span>
                          )}
                          {priorAuthMode ? (
                            <span
                              style={{
                                fontFamily: "'Newsreader', Georgia, serif",
                                fontSize: "15px",
                              }}
                            >
                              Clinical Context
                            </span>
                          ) : (
                            "Additional Context"
                          )}
                          {!priorAuthMode && (
                            <span className="text-xs font-normal text-slate-400">
                              (optional)
                            </span>
                          )}
                          {priorAuthMode && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200/60 text-[11px] font-semibold text-emerald-600">
                              <IconShield className="w-2.5 h-2.5" />
                              PHI auto-removed
                            </span>
                          )}
                        </span>
                      </label>
                      <p
                        className={`text-xs text-slate-500 mb-3 ${
                          priorAuthMode ? "ml-6" : "ml-10"
                        }`}
                      >
                        {priorAuthMode
                          ? "The more clinical detail you add, the stronger your letter. Paste from your note — diagnosis, treatments tried, exam findings."
                          : "Brief story or nuance (e.g., timeline, prior attempts, insurer objection, special circumstances)"}
                      </p>
                      <textarea
                        value={formData.additionalContext}
                        onChange={(e) => {
                          updateField("additionalContext", e.target.value);
                        }}
                        onPaste={
                          priorAuthMode ? handleClinicalContextPaste : undefined
                        }
                        placeholder={
                          priorAuthMode
                            ? "52M with L4-L5 disc herniation. PT × 6 weeks failed. Progressive radiculopathy with positive SLR. Limited ADLs..."
                            : "Timeline, prior attempts, special circumstances..."
                        }
                        className="w-full border border-slate-200 rounded-xl px-4 py-3 h-24 text-sm bg-slate-50/50 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none"
                      />

                      {/* Paste detection indicator */}
                      {priorAuthMode && pasteDetected && (
                        <div className="mt-2.5 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200/60 flex items-center gap-2">
                          <IconClipboard className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                          <p className="text-xs text-blue-700">
                            <span className="font-semibold">Note pasted</span> (
                            {pasteCharCount.toLocaleString()} chars) — PHI will
                            be scrubbed on submit
                          </p>
                        </div>
                      )}

                      {/* Character count when no paste indicator */}
                      {priorAuthMode &&
                        formData.additionalContext.length > 0 &&
                        !pasteDetected && (
                          <p className="text-[11px] text-slate-400 mt-1.5 font-mono">
                            {formData.additionalContext.length} chars
                          </p>
                        )}

                      {/* Option B: Keyword-detected salvage suggestion */}
                      {salvageKeywordDetected && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200/70 rounded-lg flex items-start gap-2.5">
                          <svg
                            className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                            />
                          </svg>
                          <div className="flex-1">
                            <p className="text-sm text-amber-800">
                              It sounds like the approved facility was unable to
                              deliver care safely.
                            </p>
                            <button
                              type="button"
                              onClick={activateSalvageMode}
                              className="mt-1.5 text-sm font-medium text-amber-700 hover:text-amber-900 underline underline-offset-2 transition-colors"
                            >
                              Document clinical necessity for alternative site →
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => setPriorCancellation("no")}
                            className="text-xs text-amber-500 hover:text-amber-700 flex-shrink-0"
                            title="Dismiss"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Key Facts for P2P Calls — hidden in prior auth mode */}
                    {!priorAuthMode && (
                      <div className="p-5 bg-gradient-to-r from-slate-50/50 to-white rounded-xl border border-slate-200/60 shadow-sm">
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          <span className="inline-flex items-center gap-3">
                            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 text-white text-xs font-bold shadow-md shadow-slate-400/30 ring-2 ring-white">
                              5
                            </span>
                            Key Facts
                            <span className="text-xs font-normal text-slate-400">
                              (optional)
                            </span>
                          </span>
                        </label>
                        <p className="text-xs text-slate-500 mb-3 ml-10">
                          High-yield objective facts the reviewer must
                          acknowledge — these may be cited in real-time
                          rebuttals
                        </p>
                        <textarea
                          value={formData.keyFacts}
                          onChange={(e) =>
                            updateField("keyFacts", e.target.value)
                          }
                          placeholder="Failed Humira × 6 months, MRI shows L4-L5 herniation, HbA1c 9.2..."
                          className="w-full border border-slate-200 rounded-xl px-4 py-3 h-24 text-sm bg-slate-50/50 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* ============================================ */}
                {/* PRIOR AUTH: Denial Letter Paste Option */}
                {/* ============================================ */}
                {priorAuthMode && formData.insuranceCompany.trim() && (
                  <div className="mb-5">
                    {!priorAuthDenialAccepted ? (
                      <button
                        type="button"
                        onClick={() => setShowPriorAuthDenialModal(true)}
                        className="w-full px-5 py-3.5 rounded-xl bg-white border border-dashed border-slate-300 hover:border-slate-400 flex items-center justify-center gap-2 transition-all group"
                      >
                        <svg
                          className="w-4 h-4 text-slate-400 group-hover:text-slate-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                          />
                        </svg>
                        <span className="text-sm text-slate-500">
                          Have a denial letter?{" "}
                          <span className="text-teal-600 font-semibold">
                            Paste it to strengthen your case
                          </span>
                        </span>
                      </button>
                    ) : (
                      <div className="px-4 py-3 rounded-xl bg-white border border-teal-200/60 flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="w-6 h-6 rounded-md bg-teal-50 flex items-center justify-center">
                            <IconCheck className="w-3 h-3 text-teal-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-800">
                              {priorAuthDenialResult?.reason}
                            </p>
                            <p className="text-xs text-slate-500">
                              Letter will directly counter this objection
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setPriorAuthDenialAccepted(false);
                            setPriorAuthDenialResult(null);
                            setShowPriorAuthDenialModal(true);
                          }}
                          className="text-xs text-slate-400 hover:text-slate-600"
                        >
                          Change
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* ============================================ */}
                {/* EXPANDABLE SECTION - More Details (hidden in priorAuthMode when AI extraction handles it) */}
                {/* ============================================ */}
                {!priorAuthMode && (
                  <div className="mb-5">
                    <button
                      type="button"
                      onClick={() => setShowMoreDetails(!showMoreDetails)}
                      className="w-full px-5 py-3 text-sm text-slate-500 hover:text-slate-700 font-medium flex items-center justify-center gap-2 transition-colors"
                    >
                      <IconChevronDown
                        className={`w-4 h-4 transition-transform ${
                          showMoreDetails ? "rotate-180" : ""
                        }`}
                      />
                      {showMoreDetails
                        ? "Hide additional details"
                        : "Add more details (optional)"}
                    </button>

                    {/* Expanded Content */}
                    {showMoreDetails && (
                      <div className="mt-3 space-y-4">
                        {/* Patient Info */}
                        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                          <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                              <IconUser className="w-4 h-4 text-slate-400" />
                              <h2 className="text-sm font-medium text-slate-700">
                                Patient Information
                              </h2>
                            </div>
                          </div>

                          <div className="p-5">
                            <div className="grid grid-cols-2 gap-4 mb-5">
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                  Age
                                </label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    min="0"
                                    max="120"
                                    value={formData.age}
                                    onChange={(e) =>
                                      updateField("age", e.target.value)
                                    }
                                    placeholder="Enter age"
                                    className="w-24 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                  />
                                  <span className="text-sm text-slate-500">
                                    years
                                  </span>
                                </div>
                                {showAgeWarning && (
                                  <p className="mt-1.5 text-xs text-amber-600 flex items-center gap-1">
                                    <span className="w-1 h-1 rounded-full bg-amber-500" />
                                    Will be stored as "90+" for privacy
                                  </p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                  Sex
                                </label>
                                <select
                                  value={formData.sex}
                                  onChange={(e) =>
                                    updateField("sex", e.target.value)
                                  }
                                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all appearance-none cursor-pointer"
                                  style={{
                                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                                    backgroundPosition: "right 0.5rem center",
                                    backgroundRepeat: "no-repeat",
                                    backgroundSize: "1.5em 1.5em",
                                    paddingRight: "2.5rem",
                                  }}
                                >
                                  <option value="">Select...</option>
                                  <option value="Male">Male</option>
                                  <option value="Female">Female</option>
                                  <option value="Other">Other</option>
                                </select>
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                Diagnoses / Comorbidities
                              </label>
                              <DiagnosesSelector
                                selected={selectedConditions}
                                onChange={setSelectedConditions}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Request-Type Specific Fields */}
                        {formData.requestType && (
                          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                            <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
                              <div className="flex items-center gap-2">
                                {getCategoryIcon(
                                  formData.requestType,
                                  "w-4 h-4 text-slate-400"
                                )}
                                <h2 className="text-sm font-medium text-slate-700">
                                  {formData.requestType} Details
                                </h2>
                              </div>
                            </div>

                            <div className="p-5">
                              {/* Medication-specific fields */}
                              {formData.requestType === "Medication" && (
                                <>
                                  <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Medication Name
                                      </label>
                                      <input
                                        type="text"
                                        value={formData.medicationName}
                                        onChange={(e) =>
                                          updateField(
                                            "medicationName",
                                            e.target.value
                                          )
                                        }
                                        placeholder="e.g., Jardiance, Ozempic"
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Drug Class
                                      </label>
                                      <select
                                        value={formData.medicationClass}
                                        onChange={(e) =>
                                          updateField(
                                            "medicationClass",
                                            e.target.value
                                          )
                                        }
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                      >
                                        <option value="">Select...</option>
                                        {MEDICATION_CLASSES.map((cls) => (
                                          <option key={cls} value={cls}>
                                            {cls}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-3 gap-4">
                                    <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Dose / Strength
                                      </label>
                                      <input
                                        type="text"
                                        value={formData.medicationDose}
                                        onChange={(e) =>
                                          updateField(
                                            "medicationDose",
                                            e.target.value
                                          )
                                        }
                                        placeholder="e.g., 25mg"
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Frequency
                                      </label>
                                      <input
                                        type="text"
                                        value={formData.medicationFrequency}
                                        onChange={(e) =>
                                          updateField(
                                            "medicationFrequency",
                                            e.target.value
                                          )
                                        }
                                        placeholder="e.g., daily"
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Formulary Status
                                      </label>
                                      <select
                                        value={formData.formularyStatus}
                                        onChange={(e) =>
                                          updateField(
                                            "formularyStatus",
                                            e.target.value
                                          )
                                        }
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                      >
                                        <option value="">Select...</option>
                                        {FORMULARY_STATUS.map((status) => (
                                          <option
                                            key={status.value}
                                            value={status.value}
                                          >
                                            {status.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                </>
                              )}

                              {/* Imaging-specific fields */}
                              {formData.requestType === "Imaging" && (
                                <>
                                  <div className="grid grid-cols-3 gap-4 mb-4">
                                    <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Imaging Type
                                      </label>
                                      <select
                                        value={formData.imagingType}
                                        onChange={(e) =>
                                          updateField(
                                            "imagingType",
                                            e.target.value
                                          )
                                        }
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                      >
                                        <option value="">Select...</option>
                                        {IMAGING_TYPES.map((type) => (
                                          <option key={type} value={type}>
                                            {type}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Body Region
                                      </label>
                                      <select
                                        value={formData.bodyRegion}
                                        onChange={(e) =>
                                          updateField(
                                            "bodyRegion",
                                            e.target.value
                                          )
                                        }
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                      >
                                        <option value="">Select...</option>
                                        {BODY_REGIONS.map((region) => (
                                          <option key={region} value={region}>
                                            {region}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Contrast
                                      </label>
                                      <select
                                        value={formData.contrastRequired}
                                        onChange={(e) =>
                                          updateField(
                                            "contrastRequired",
                                            e.target.value
                                          )
                                        }
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                      >
                                        <option value="">Select...</option>
                                        <option value="without">
                                          Without contrast
                                        </option>
                                        <option value="with">
                                          With contrast
                                        </option>
                                        <option value="with_without">
                                          With and without contrast
                                        </option>
                                        <option value="na">N/A</option>
                                      </select>
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                      Prior Imaging Findings
                                    </label>
                                    <textarea
                                      value={formData.priorImagingFindings}
                                      onChange={(e) =>
                                        updateField(
                                          "priorImagingFindings",
                                          e.target.value
                                        )
                                      }
                                      placeholder="e.g., X-ray showed degenerative changes..."
                                      className="w-full border border-slate-200 rounded-lg px-3 py-2 h-16 text-sm bg-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 outline-none resize-none"
                                    />
                                  </div>
                                </>
                              )}

                              {/* Procedure-specific fields */}
                              {formData.requestType === "Procedure" && (
                                <>
                                  <div className="grid grid-cols-3 gap-4 mb-4">
                                    <div className="col-span-2">
                                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Procedure Name
                                      </label>
                                      <input
                                        type="text"
                                        value={formData.procedureName}
                                        onChange={(e) =>
                                          updateField(
                                            "procedureName",
                                            e.target.value
                                          )
                                        }
                                        placeholder="e.g., Lumbar laminectomy"
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        CPT Code(s)
                                      </label>
                                      <input
                                        type="text"
                                        value={formData.cptCode}
                                        onChange={(e) =>
                                          updateField("cptCode", e.target.value)
                                        }
                                        placeholder="e.g., 63047"
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                      />
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Urgency
                                      </label>
                                      <select
                                        value={formData.procedureUrgency}
                                        onChange={(e) =>
                                          updateField(
                                            "procedureUrgency",
                                            e.target.value
                                          )
                                        }
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                      >
                                        <option value="">Select...</option>
                                        {PROCEDURE_URGENCY.map((urgency) => (
                                          <option
                                            key={urgency.value}
                                            value={urgency.value}
                                          >
                                            {urgency.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Relevant Surgical History
                                      </label>
                                      <input
                                        type="text"
                                        value={formData.surgicalHistory}
                                        onChange={(e) =>
                                          updateField(
                                            "surgicalHistory",
                                            e.target.value
                                          )
                                        }
                                        placeholder="e.g., Prior L4-L5 fusion 2019"
                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                      />
                                    </div>
                                  </div>
                                </>
                              )}

                              {/* Referral-specific fields */}
                              {formData.requestType === "Referral" && (
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                      Specialty
                                    </label>
                                    <select
                                      value={formData.referralSpecialty}
                                      onChange={(e) =>
                                        updateField(
                                          "referralSpecialty",
                                          e.target.value
                                        )
                                      }
                                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                    >
                                      <option value="">Select...</option>
                                      {SPECIALTY_TYPES.map((specialty) => (
                                        <option
                                          key={specialty}
                                          value={specialty}
                                        >
                                          {specialty}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                      Reason for Referral
                                    </label>
                                    <input
                                      type="text"
                                      value={formData.referralReason}
                                      onChange={(e) =>
                                        updateField(
                                          "referralReason",
                                          e.target.value
                                        )
                                      }
                                      placeholder="e.g., Suspected arrhythmia"
                                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                    />
                                  </div>
                                </div>
                              )}

                              {/* Admission-specific fields */}
                              {formData.requestType === "Admission" && (
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                      Admission Type
                                    </label>
                                    <select
                                      value={formData.admissionType || ""}
                                      onChange={(e) =>
                                        updateField(
                                          "admissionType",
                                          e.target.value
                                        )
                                      }
                                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                    >
                                      <option value="">Select...</option>
                                      <option value="inpatient">
                                        Inpatient
                                      </option>
                                      <option value="observation">
                                        Observation
                                      </option>
                                      <option value="icu">
                                        ICU / Critical Care
                                      </option>
                                      <option value="stepdown">
                                        Step-down Unit
                                      </option>
                                      <option value="snf">
                                        Skilled Nursing Facility
                                      </option>
                                      <option value="rehab">
                                        Inpatient Rehabilitation
                                      </option>
                                      <option value="ltac">
                                        Long-Term Acute Care
                                      </option>
                                      <option value="psych">Psychiatric</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                      Length of Stay
                                    </label>
                                    <input
                                      type="text"
                                      value={formData.lengthOfStay || ""}
                                      onChange={(e) =>
                                        updateField(
                                          "lengthOfStay",
                                          e.target.value
                                        )
                                      }
                                      placeholder="e.g., 3 days, ongoing"
                                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                    />
                                  </div>
                                </div>
                              )}

                              {/* DME-specific fields */}
                              {formData.requestType ===
                                "DME (Durable Medical Equipment)" && (
                                <div className="grid grid-cols-3 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                      Equipment Category
                                    </label>
                                    <select
                                      value={formData.dmeCategory}
                                      onChange={(e) =>
                                        updateField(
                                          "dmeCategory",
                                          e.target.value
                                        )
                                      }
                                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                    >
                                      <option value="">Select...</option>
                                      {DME_CATEGORIES.map((category) => (
                                        <option key={category} value={category}>
                                          {category}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                      Specific Item
                                    </label>
                                    <input
                                      type="text"
                                      value={formData.dmeSpecificItem}
                                      onChange={(e) =>
                                        updateField(
                                          "dmeSpecificItem",
                                          e.target.value
                                        )
                                      }
                                      placeholder="e.g., ResMed AirSense 11"
                                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                      HCPCS Code
                                    </label>
                                    <input
                                      type="text"
                                      value={formData.hcpcsCode}
                                      onChange={(e) =>
                                        updateField("hcpcsCode", e.target.value)
                                      }
                                      placeholder="e.g., E0601"
                                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* What's Been Tried (if not already shown for Step Therapy) */}
                        {!isStepTherapyDenial && (
                          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                            <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
                              <div className="flex items-center gap-2">
                                <IconBeaker className="w-4 h-4 text-slate-400" />
                                <h2 className="text-sm font-medium text-slate-700">
                                  Prior Treatments
                                </h2>
                              </div>
                            </div>

                            <div className="p-5">
                              <TriedTreatmentsSelect
                                selected={selectedTreatments}
                                onChange={setSelectedTreatments}
                              />
                            </div>
                          </div>
                        )}

                        {/* Prior Medications Section */}
                        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                          <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                              <IconMedication className="w-4 h-4 text-slate-400" />
                              <h2 className="text-sm font-medium text-slate-700">
                                Prior Medications
                              </h2>
                              <span className="text-xs text-slate-400">
                                — for step therapy documentation
                              </span>
                            </div>
                          </div>

                          <div className="p-5">
                            <p className="text-xs text-slate-500 mb-3">
                              Search for medications the patient has already
                              tried. These are grouped by therapeutic class to
                              strengthen step therapy arguments.
                            </p>
                            <MedicationInputInline
                              value={priorMedications}
                              onChange={setPriorMedications}
                              placeholder="Search medications (e.g., ibuprofen, metformin, Humira)..."
                              maxSelections={15}
                            />
                            {priorMedications.length > 0 && (
                              <p className="text-xs text-teal-600 mt-2 font-medium">
                                Summary:{" "}
                                {generateMedicationSummaryDisplay(
                                  priorMedications.map((m) => m.name)
                                )}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Dynamic Case-Specific Questions */}
                        {showDynamicQuestions && (
                          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                            <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
                              <div className="flex items-center gap-2">
                                <IconLightBulb className="w-4 h-4 text-slate-400" />
                                <h2 className="text-sm font-medium text-slate-700">
                                  Strengthen Your Case
                                </h2>
                                <span className="text-xs text-slate-400">
                                  — AI-suggested questions
                                </span>
                              </div>
                            </div>

                            <div className="p-5 space-y-4">
                              {dynamicQuestions.map((question) => (
                                <DynamicQuestionField
                                  key={question.id}
                                  question={question}
                                  value={dynamicAnswers[question.id] || ""}
                                  onChange={(value) =>
                                    setDynamicAnswers((prev) => ({
                                      ...prev,
                                      [question.id]: value,
                                    }))
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Scheduling & Deadlines */}
                        <CaseScheduling
                          data={scheduling}
                          onChange={setScheduling}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
              </>
            )}
            <div className="flex items-center justify-between pt-6">
              <Link
                to={editCaseId ? `/case/${editCaseId}` : "/"}
                className="px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-all"
              >
                ← {editCaseId ? "Back to Case" : "Cancel"}
              </Link>
              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  !formData.requestDetails.trim() ||
                  !formData.insuranceCompany ||
                  (appealMode && !formData.denialReason) ||
                  (!priorAuthMode && !formData.denialReason)
                }
                className={
                  priorAuthMode
                    ? "px-6 py-3 bg-slate-900 text-white font-semibold rounded-xl hover:bg-slate-800 shadow-md shadow-slate-900/20 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center gap-2"
                    : "px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white font-medium rounded-xl hover:from-teal-600 hover:to-teal-700 shadow-md shadow-teal-500/25 hover:shadow-lg hover:shadow-teal-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center gap-2"
                }
              >
                {isSubmitting ? (
                  <>
                    <IconLoader className="w-4 h-4" />
                    {editCaseId ? "Updating..." : "Generating..."}
                  </>
                ) : (
                  <>
                    <IconSparkles className="w-4 h-4" />
                    {editCaseId
                      ? "Update Case"
                      : appealMode
                      ? "Generate Appeal Letter"
                      : priorAuthMode
                      ? "Generate Letter"
                      : "Generate P2P Prep"}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </main>

      {/* ============================================ */}
      {/* PRIOR AUTH DENIAL PASTE MODAL */}
      {/* ============================================ */}
      {showPriorAuthDenialModal && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPriorAuthDenialModal(false);
              setPriorAuthDenialResult(null);
            }
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-auto">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p className="text-base font-semibold text-slate-900">
                  Paste denial letter
                </p>
                <p className="text-sm text-slate-500 mt-0.5">
                  We'll identify the denial reason and tailor your letter
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowPriorAuthDenialModal(false);
                  setPriorAuthDenialResult(null);
                }}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <IconX className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="px-3 py-2 rounded-lg bg-emerald-50/60 border border-emerald-200/40 flex items-center gap-2 mb-4">
                <IconShield className="w-3.5 h-3.5 text-emerald-500" />
                <p className="text-xs text-emerald-700">
                  PHI in denial text will be scrubbed — paste the full letter
                </p>
              </div>

              <textarea
                value={priorAuthDenialText}
                onChange={(e) => {
                  setPriorAuthDenialText(e.target.value);
                  setPriorAuthDenialResult(null);
                }}
                placeholder="Paste the denial letter, relevant excerpts, or key denial language..."
                className="w-full border border-slate-200 rounded-xl px-4 py-3 h-40 text-sm bg-slate-50/50 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none"
              />

              {priorAuthDenialText.length > 0 && !priorAuthDenialResult && (
                <p className="text-[11px] text-slate-400 mt-1.5 font-mono">
                  {priorAuthDenialText.length} chars
                </p>
              )}

              {/* Analysis result */}
              {priorAuthDenialResult && (
                <div className="mt-4 rounded-xl overflow-hidden border border-emerald-200/60">
                  <div className="px-4 py-3 bg-emerald-50">
                    <div className="flex items-center gap-2 mb-1">
                      <IconCheck className="w-4 h-4 text-emerald-500" />
                      <p className="text-sm font-semibold text-slate-900">
                        {priorAuthDenialResult.reason}
                      </p>
                    </div>
                    <p className="text-xs text-slate-500 ml-6">
                      {priorAuthDenialResult.confidence === "high"
                        ? "High confidence match"
                        : priorAuthDenialResult.confidence === "medium"
                        ? "Likely match"
                        : "Low confidence — please verify"}
                    </p>
                  </div>
                  <div className="px-4 py-3 bg-white">
                    <p className="text-xs text-slate-500 mb-3">
                      Your letter will include targeted rebuttals using the
                      insurer's own clinical criteria.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setPriorAuthDenialAccepted(true);
                        setShowPriorAuthDenialModal(false);
                      }}
                      className="px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-md shadow-teal-500/25 hover:from-teal-600 hover:to-teal-700 transition-all"
                    >
                      Use this denial reason
                    </button>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              {!priorAuthDenialResult && (
                <div className="flex gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPriorAuthDenialModal(false);
                      setPriorAuthDenialResult(null);
                    }}
                    className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAnalyzePriorAuthDenial}
                    disabled={
                      !priorAuthDenialText.trim() || isAnalyzingPriorAuthDenial
                    }
                    className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-md shadow-teal-500/25 hover:from-teal-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                  >
                    {isAnalyzingPriorAuthDenial ? (
                      <>
                        <IconLoader className="w-4 h-4" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <IconSearch className="w-4 h-4" />
                        Analyze
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              Monthly limit reached
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              You've used your 2 free PA letters this month. Upgrade to
              Essential for unlimited letter generation.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpgrade("essential")}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-md shadow-teal-500/25 hover:from-teal-600 hover:to-teal-700 transition-all"
              >
                Upgrade — $29/mo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GUEST LETTER RESULT — shown when unauthenticated user generates a letter */}
      {guestLetter && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full flex flex-col"
            style={{ maxHeight: "calc(100vh - 2rem)" }}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-100 to-teal-50 flex items-center justify-center">
                  <IconSparkles className="w-4 h-4 text-teal-600" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Your Prior Auth Letter
                </h2>
              </div>
              <button
                onClick={() => {
                  setGuestLetter(null);
                  setGuestAssembledLetter("");
                  setGuestCopied(false);
                  setGuestGenerationSeconds(null);
                }}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <IconX className="w-5 h-5" />
              </button>
            </div>

            {/* Action bar */}
            <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-2">
              <button
                onClick={() => {
                  const textToCopy =
                    guestAssembledLetter || guestLetter.clinical_justification;
                  navigator.clipboard.writeText(textToCopy).then(() => {
                    setGuestCopied(true);
                    setTimeout(() => setGuestCopied(false), 2000);
                  });
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
              >
                {guestCopied ? (
                  <>
                    <IconCheck className="w-3.5 h-3.5 text-green-500" />
                    <span className="text-green-600">Copied!</span>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-3.5 h-3.5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
                      />
                    </svg>
                    Copy to Clipboard
                  </>
                )}
              </button>
              {guestGenerationSeconds !== null && (
                <span className="ml-auto px-2.5 py-1 rounded-full bg-teal-50 border border-teal-200/60 text-[11px] font-medium text-teal-700">
                  Generated in {guestGenerationSeconds}s
                </span>
              )}
            </div>

            {/* Letter Content */}
            <div className="px-6 py-5 flex-1 overflow-y-auto min-h-0">
              {guestAssembledLetter ? (
                <div
                  className="text-sm text-slate-700 leading-relaxed font-mono whitespace-pre-wrap"
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    fontSize: "13px",
                    lineHeight: "1.7",
                  }}
                >
                  {guestAssembledLetter}
                </div>
              ) : (
                /* Fallback: raw clinical argument if assembly failed */
                <div className="space-y-5">
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 mb-2 pb-1 border-b border-teal-500/40">
                      Medical Necessity
                    </h3>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {guestLetter.clinical_justification}
                    </p>
                  </div>

                  {guestLetter.criteria_citations?.length > 0 && (
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 mb-2 pb-1 border-b border-teal-500/40">
                        Criteria Met
                      </h3>
                      <ul className="space-y-1.5">
                        {guestLetter.criteria_citations.map(
                          (c: string, i: number) => (
                            <li
                              key={i}
                              className="text-sm text-slate-700 leading-relaxed"
                            >
                              {i + 1}. {c}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}

                  {guestLetter.literature_references?.length > 0 && (
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 mb-2 pb-1 border-b border-teal-500/40">
                        Literature References
                      </h3>
                      <ul className="space-y-1.5">
                        {guestLetter.literature_references.map(
                          (r: string, i: number) => (
                            <li
                              key={i}
                              className="text-sm text-slate-600 leading-relaxed"
                            >
                              {r}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}

                  {guestLetter.risks_of_denial?.length > 0 && (
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 mb-2 pb-1 border-b border-teal-500/40">
                        Risks of Denial
                      </h3>
                      <ul className="space-y-1.5">
                        {guestLetter.risks_of_denial.map(
                          (r: string, i: number) => (
                            <li
                              key={i}
                              className="text-sm text-slate-700 leading-relaxed"
                            >
                              {r}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}

                  {guestLetter.regulatory_leverage &&
                    guestLetter.regulatory_leverage.length > 0 && (
                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 mb-2 pb-1 border-b border-teal-500/40">
                          Regulatory Protections
                        </h3>
                        <ul className="space-y-1.5">
                          {guestLetter.regulatory_leverage.map(
                            (r: string, i: number) => (
                              <li
                                key={i}
                                className="text-sm text-slate-700 leading-relaxed"
                              >
                                {i + 1}. {r}
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                </div>
              )}
            </div>

            {/* Recommendations */}
            {guestLetter.recommended_additions?.length > 0 && (
              <div className="px-6 pb-4">
                <div className="px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-100">
                  <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wider mb-1.5">
                    Suggestions to Strengthen
                  </p>
                  <ul className="space-y-1">
                    {guestLetter.recommended_additions.map(
                      (rec: string, i: number) => (
                        <li
                          key={i}
                          className="text-xs text-amber-700 flex gap-1.5"
                        >
                          <span className="text-amber-400 flex-shrink-0 mt-0.5">
                            •
                          </span>
                          {rec}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              </div>
            )}

            {/* Footer — Soft sign-up prompt */}
            <div className="px-6 py-5 border-t border-slate-200 bg-slate-50/50 rounded-b-2xl flex-shrink-0">
              <p className="text-sm text-slate-600 mb-4">
                Create a free account to save your letters, download PDFs, and
                generate more.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    try {
                      const draft = {
                        formData,
                        selectedConditions,
                        selectedTreatments,
                        priorMedications,
                      };
                      sessionStorage.setItem(
                        "peerprep_draft",
                        JSON.stringify(draft)
                      );
                    } catch (_e) {}
                    setRedirectToSignIn(true);
                  }}
                  className="flex-1 px-6 py-3 rounded-xl text-sm font-semibold bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-md shadow-teal-500/25 hover:from-teal-600 hover:to-teal-700 transition-all"
                >
                  Create Free Account →
                </button>
                <button
                  onClick={() => {
                    setGuestLetter(null);
                    setGuestAssembledLetter("");
                    setGuestCopied(false);
                    setGuestGenerationSeconds(null);
                  }}
                  className="px-4 py-3 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
                >
                  Close
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-3">
                Free · No credit card · Takes 10 seconds
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SIGN-UP INTERSTITIAL — shown when unauthenticated user tries to generate */}
      {showSignUpModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            {/* Teal icon */}
            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-100 to-teal-50 flex items-center justify-center mb-5 ring-8 ring-teal-50">
              <svg
                className="w-7 h-7 text-teal-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                />
              </svg>
            </div>

            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              Your letter is ready to generate
            </h2>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              Create a free account to generate and view your prior auth letter.
              Your inputs have been saved — you'll pick up right where you left
              off.
            </p>

            <button
              onClick={() => setRedirectToSignIn(true)}
              className="w-full px-6 py-3 rounded-xl text-sm font-semibold bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-md shadow-teal-500/25 hover:from-teal-600 hover:to-teal-700 transition-all mb-3"
            >
              Create Free Account →
            </button>

            <p className="text-xs text-slate-400">
              Free · No credit card · Takes 10 seconds
            </p>

            <button
              onClick={() => setShowSignUpModal(false)}
              className="mt-4 text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              Go back to editing
            </button>
          </div>
        </div>
      )}

      {/* DEV: Scrub Audit Panel — Ctrl+Shift+S to toggle */}
      {import.meta.env.DEV && <ScrubAuditPanel fields={scrubAuditFields} />}
    </div>
  );
}
